// frontend/src/components/configuracion/ConfiguracionBase.jsx
// COMPONENTE BASE PARA TODAS LAS CONFIGURACIONES CON FUNCIONALIDAD CRUD COMPLETA
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { spin } from '../../styles/animations';
import { ActionButton as SharedActionButton, PrimaryButton, BaseButton, NavButton, DuplicarButton } from '../common/Buttons';
import SearchableSelectField from '../common/SearchableSelectField';
import useNavegacionTeclado, { useAutoFocoPrimerCampo } from '../../hooks/useNavegacionTeclado';
import apiClient from '../../services/apiClient';
import usuariosService from '../../services/usuariosService';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';
import HelpPanel, { HelpToggleBtn } from '../common/HelpPanel';
import ImportarModal from '../importacion/ImportarModal';
import { ENTIDADES_IMPORTABLES } from '../../services/importacionService';

// Cuántas filas se piden de una para un catálogo acotado. El backend admite
// hasta `max_page_size` (10000); con esto un catálogo entra en una sola
// respuesta en vez de una petición por cada 100 filas.
const PAGE_SIZE_CATALOGO = 2000;

// Tope de páginas al recorrer `next`. Es una red de seguridad contra un bucle
// infinito, no una forma de limitar datos: si se alcanza, la pantalla avisa que
// el listado quedó incompleto en vez de mostrarlo corto en silencio.
const MAX_PAGINAS_CATALOGO = 30;

const normalizeResults = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

// Campos booleanos que viajan como 'true'/'false' en el <select> de edición masiva
// (bulkChoiceFields) y deben convertirse a boolean real antes de mandarlos en el PATCH.
const BULK_CHOICE_BOOLEAN_FIELDS = new Set(['activo', 'uso_mantenimiento_diario', 'requiere_repuestos', 'no_usar']);

async function fetchAllOptions(endpoint, params = {}) {
  let url = endpoint.endsWith('/') ? endpoint : `${endpoint}/`;
  let firstParams = { page_size: 10000, ...params };
  let all = [];
  let loops = 0;

  while (url && loops < 40) {
    const resp = await apiClient.get(url, { params: firstParams });
    const payload = resp.data;

    if (Array.isArray(payload)) {
      all = payload;
      break;
    }

    all = all.concat(normalizeResults(payload));
    url = payload?.next || null;
    firstParams = undefined;
    loops += 1;
  }

  return all;
}

// Styled Components con tema oscuro profesional y colores mejorados
const ConfigContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: ${({ $embedded, $compact }) => {
    if ($embedded) return $compact ? '0.5rem 0.6rem 0.75rem' : '0.75rem 0.9rem 1rem';
    return $compact ? '0.85rem 1rem 1rem' : '1.25rem 1.25rem 1.25rem';
  }};
  background: ${({ theme, $embedded }) => ($embedded ? theme.colors.transparent : theme.colors.bgPrincipal)};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  display: flex;
  flex-direction: column;
  min-height: ${({ $embedded }) => ($embedded ? 'auto' : 'calc((100vh / var(--app-zoom, 1)) - 80px)')};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: ${({ $embedded, $compact }) => ($compact ? ($embedded ? '0.5rem' : '0.75rem') : ($embedded ? '0.75rem' : '1.4rem'))};
  padding-bottom: ${({ $embedded, $compact }) => ($compact ? ($embedded ? '0.35rem' : '0.45rem') : ($embedded ? '0.5rem' : '0.9rem'))};
  border-bottom: ${({ theme, $embedded, $compact }) => ($compact ? `1px solid ${theme.colors.info}` : ($embedded ? `1px solid ${theme.colors.info}` : `2px solid ${theme.colors.info}`))};
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.info};
  font-size: ${({ $embedded, $compact }) => {
    if ($compact) return $embedded ? '0.95rem' : '1.25rem';
    return $embedded ? '1.1rem' : '1.8rem';
  }};
  margin: 0;
  font-weight: ${({ $compact }) => ($compact ? 500 : 400)};
  display: flex;
  align-items: center;
  gap: 0.45rem;
  line-height: 1.1;
  i { color: ${({ theme }) => theme.colors.info}; }
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.txtSecundario};
  margin: ${({ $embedded, $compact }) => ($compact ? '0.15rem 0 0 0' : ($embedded ? '0.25rem 0 0 0' : '0.5rem 0 0 0'))};
  font-size: ${({ $embedded, $compact }) => {
    if ($compact) return $embedded ? '0.7rem' : '0.75rem';
    return $embedded ? '0.8rem' : '0.9rem';
  }};
  line-height: 1.2;
`;

const ControlsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ $embedded, $compact }) => ($compact ? ($embedded ? '0.55rem' : '0.75rem') : ($embedded ? '0.75rem' : '1.2rem'))};
  gap: ${({ $embedded, $compact }) => ($compact ? ($embedded ? '0.45rem' : '0.6rem') : ($embedded ? '0.5rem' : '1rem'))};
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme, $invalid }) => ($invalid ? theme.colors.danger : theme.colors.borderDefault)};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  transition: all 0.25s ease;
  padding: ${({ $embedded, $compact }) => {
    if ($compact) return $embedded ? '0.25rem 0.5rem' : '0.4rem 0.65rem';
    return $embedded ? '0.35rem 0.6rem' : '0.8rem 1rem';
  }};
  border-radius: ${({ $embedded, $compact }) => ($compact ? '4px' : ($embedded ? '4px' : '6px'))};
  font-size: ${({ $embedded, $compact }) => ($compact ? ($embedded ? '0.7rem' : '0.8rem') : ($embedded ? '0.85rem' : '0.9rem'))};
  width: ${({ $embedded, $compact }) => ($compact ? ($embedded ? '220px' : '240px') : ($embedded ? '260px' : '300px'))};

  &:focus {
    outline: none;
    /* ESQUEMA AZUL-PÚRPURA ELEGANTE */
    border-color: ${({ theme, $invalid }) => ($invalid ? theme.colors.danger : theme.colors.info)};
    box-shadow: 0 0 0 2px ${({ theme, $invalid }) => ($invalid ? `${theme.colors.danger}33` : `${theme.colors.info}33`)};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.txtSecundario};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.txtMuted};
  }
`;

// Toggle segmentado (Componente / Depósito) para el modal de tareas.
// Ocupa todo el ancho disponible, repartiendo las dos opciones por igual.
const SegmentedToggle = styled.div`
  display: flex;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;
  overflow: hidden;
`;

const SegmentedOption = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  flex: 1 1 0;
  text-align: center;
  padding: 0.55rem 0.9rem;
  font-size: 0.85rem;
  font-weight: 600;
  transition: background 0.2s ease, color 0.2s ease;
  background: ${({ theme, $active }) => ($active ? theme.colors.info : theme.colors.surface)};
  color: ${({ theme, $active }) => ($active ? theme.colors.onPrimary : theme.colors.txtSecundario)};

  & + & {
    border-left: 1px solid ${({ theme }) => theme.colors.borderDefault};
  }

  &:hover {
    background: ${({ theme, $active }) => ($active ? theme.colors.infoHover : theme.colors.surfaceHover)};
  }
`;

// Card clickeable para "¿Requiere repuestos?" — el checkbox decide si se revela
// el bloque de vínculo (Componente/Depósito) más abajo, así que necesita más
// presencia que un checkbox suelto en una línea.
const RequiereRepuestosCard = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: ${({ $embedded, $compact }) => ($compact ? ($embedded ? '0.5rem 0.65rem' : '0.65rem 0.8rem') : '0.8rem 1rem')};
  border: 1px solid ${({ theme, $checked }) => ($checked ? theme.colors.info : theme.colors.borderDefault)};
  border-radius: 6px;
  background: ${({ theme, $checked }) => ($checked ? `${theme.colors.info}0d` : theme.colors.transparent)};
  cursor: pointer;
  user-select: none;
  transition-property: background-color, border-color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;

  &:hover {
    border-color: ${({ theme }) => theme.colors.info};
  }
`;

const RequiereRepuestosCheckbox = styled.input.attrs({ type: 'checkbox' })`
  margin-top: 0.2rem;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.info};
`;

const RequiereRepuestosTexto = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const RequiereRepuestosTitulo = styled.span`
  font-size: ${({ $compact }) => ($compact ? '0.82rem' : '0.9rem')};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.txtPrincipal};
`;

const RequiereRepuestosHint = styled.span`
  font-size: ${({ $compact }) => ($compact ? '0.72rem' : '0.78rem')};
  color: ${({ theme }) => theme.colors.txtMuted};
`;

// Revelación progresiva del bloque Componente/Depósito: se anima altura +
// opacidad (nunca `transition: all`), coherente con el resto del proyecto que
// no usa librerías de animación.
const VinculoRevelado = styled.div`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition-property: grid-template-rows, opacity;
  transition-duration: 0.25s;
  transition-timing-function: ease-in-out;

  & > div {
    /* Solo recorta mientras la fila crece/decrece (grid-template-rows anima
       0fr→1fr). Una vez abierto, "visible" para que el DropdownList absoluto
       de Componente/Depósito pueda salir del contenedor sin quedar cortado. */
    overflow: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  }
`;

const AddButton = styled.button`
  background: ${({ theme }) => theme.colors.info};
  color: ${({ theme }) => theme.colors.onPrimary};
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: ${({ $embedded, $compact }) => {
    if ($compact) return $embedded ? '0.25rem 0.6rem' : '0.45rem 0.9rem';
    return $embedded ? '0.35rem 0.75rem' : '0.8rem 1.5rem';
  }};
  font-size: ${({ $embedded, $compact }) => ($compact ? ($embedded ? '0.7rem' : '0.8rem') : ($embedded ? '0.85rem' : '0.9rem'))};
  border-radius: ${({ $embedded, $compact }) => ($compact ? '4px' : ($embedded ? '4px' : '6px'))};

  &:hover {
      background: ${({ theme }) => theme.colors.infoHover};
    transform: translateY(-2px);
      box-shadow: 0 4px 12px ${({ theme }) => `${theme.colors.info}4D`};
  }

  &:active {
    transform: translateY(0);
  }
`;

// Botón volver variante outline ligera reutilizando dimensiones del modo compacto
const BackButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.transparent};
  color: ${({ theme }) => theme.colors.txtSecundario};
  border: 1px solid ${({ theme }) => theme.colors.border};
  &:hover {
    background: ${({ theme }) => theme.colors.overlayOnPrimarySoft};
    border-color: ${({ theme }) => theme.colors.info};
    color: ${({ theme }) => theme.colors.info};
    transform: none;
    box-shadow: none;
  }
`;

const BulkEditButton = styled(AddButton)`
  background: ${({ theme, $active }) => $active ? theme.colors.warning : theme.colors.transparent};
  color: ${({ theme, $active }) => $active ? theme.colors.onPrimary : theme.colors.warning};
  border: 1px solid ${({ theme }) => theme.colors.warning};
  &:hover {
    background: ${({ theme }) => theme.colors.warning};
    color: ${({ theme }) => theme.colors.onPrimary};
    transform: none;
    box-shadow: none;
  }
`;

const ContinuarButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.success};
  &:hover {
    background: ${({ theme }) => theme.colors.success};
    box-shadow: 0 4px 12px ${({ theme }) => `${theme.colors.success}4D`};
  }
`;

const BulkDeleteButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.transparent};
  color: ${({ theme }) => theme.colors.danger};
  border: 1px solid ${({ theme }) => theme.colors.danger};
  &:hover {
    background: ${({ theme }) => theme.colors.danger};
    color: ${({ theme }) => theme.colors.onPrimary};
    transform: none;
    box-shadow: none;
  }
`;

const BulkCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.info};
`;

const BulkModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.overlayBg};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ $zIndex }) => $zIndex || 1200};
`;

const BulkModalBox = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 10px;
  width: 100%;
  max-width: 460px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const BulkModalTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.info};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BulkModalSubtitle = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.txtSecundario};
`;

const BulkFieldLabel = styled.label`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.txtSecundario};
  margin-bottom: 0.3rem;
  display: block;
`;

const BulkFieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const BulkModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

// Small outlined icon button to match project's outline buttons
const SmallOutline = styled.button`
  background: ${({ theme }) => theme.colors.transparent};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0 10px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: ${({ $embedded }) => ($embedded ? '0.9rem' : '1rem')};
  height: ${({ $embedded }) => ($embedded ? '30px' : '36px')};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// Se monta vía portal en document.body con position:fixed. Dentro de un modal el
// cuerpo scrollea (overflow-y:auto) y el contenedor recorta (overflow:hidden), así
// que un dropdown `absolute` queda cortado contra el pie del modal. Al portalizarlo
// ningún ancestro puede recortarlo y se apoya sobre el pie en vez de meterse debajo.
const DropdownListBase = styled.ul`
  position: fixed;
  z-index: ${({ theme }) => theme.zIndex?.dropdownOverModal || 9500};
  max-height: 210px;
  overflow: auto;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;
  box-shadow: ${({ theme }) => theme.shadowModal};
`;

// Ancla el desplegable al wrapper `position: relative` que lo contiene en el JSX.
// Mide ese wrapper y se posiciona en coordenadas de viewport; si no hay lugar
// abajo (típico cuando el campo queda cerca del pie del modal) se abre hacia arriba.
const DropdownList = ({ children, ...props }) => {
  const anchorRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current?.parentElement;
    if (!anchor) return;

    const updatePosition = () => {
      const rect = anchor.getBoundingClientRect();
      // El root aplica `zoom` global (--app-zoom): getBoundingClientRect() ya viene
      // escalado, pero en el portal (document.body) el zoom se vuelve a aplicar.
      // Dividimos por el factor para trabajar en el espacio sin escalar.
      const zoomRaw = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--app-zoom')
      );
      const zoom = zoomRaw > 0 ? zoomRaw : 1;
      const viewportH = window.innerHeight / zoom;
      const top = rect.bottom / zoom + 2;
      const espacioAbajo = viewportH - top;
      const abrirArriba = espacioAbajo < 210 && rect.top / zoom > espacioAbajo;

      setPos({
        left: rect.left / zoom,
        width: rect.width / zoom,
        ...(abrirArriba
          ? { bottom: viewportH - rect.top / zoom + 2, maxHeight: Math.min(210, rect.top / zoom - 8) }
          : { top, maxHeight: Math.min(210, espacioAbajo - 8) }),
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, []);

  return (
    <>
      {/* Marcador invisible: sirve para ubicar el wrapper que ancla el desplegable. */}
      <span ref={anchorRef} style={{ display: 'none' }} aria-hidden="true" />
      {pos
        ? ReactDOM.createPortal(
            <DropdownListBase {...props} data-dropdown-portal="" style={pos}>
              {children}
            </DropdownListBase>,
            document.body
          )
        : null}
    </>
  );
};

// $activo = fila resaltada con las flechas; gana sobre el hover del mouse
// mientras se navega con el teclado.
const DropdownItem = styled.li`
  padding: 8px 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  background: ${({ $activo, theme }) => ($activo ? theme.colors.opcionActivaFondo : 'transparent')};
  box-shadow: ${({ $activo, theme }) => ($activo ? `inset 3px 0 0 ${theme.colors.opcionActivaBorde}` : 'none')};
  &:hover {
    background: ${({ $activo, theme }) => ($activo ? theme.colors.opcionActivaFondo : theme.colors.surfaceHover)};
  }
`;

// Fila de item con nombre (foco principal) a la izquierda y sistema
// (dato de ayuda/desambiguación) a la derecha, en menor énfasis.
const DropdownItemRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const DropdownItemNombre = styled.span`
  color: ${({ theme }) => theme.colors.txtPrincipal};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownItemSistema = styled.span`
  flex-shrink: 0;
  font-size: 0.78em;
  color: ${({ theme }) => theme.colors.txtMuted};
  white-space: nowrap;
`;

// Buscador que agrega a una lista (componentes de un repuesto, depósitos,
// equivalencias). Los tres tenían `tabIndex={0}` en CADA opción, lo que obligaba
// a tabular una vez por resultado para llegar al que se buscaba: peor que no
// tener teclado. Acá se navega con las flechas y se agrega con Enter, igual que
// en el resto de los desplegables del sistema.
const BuscadorAgregar = ({
  abierto, setAbierto, valor, onValorChange, opciones, onAgregar,
  placeholder, renderOpcion, vacioTexto = 'No hay resultados',
}) => {
  const { indiceActivo, setIndiceActivo, onKeyDown, registrarOpcion, propsAriaInput } =
    useNavegacionTeclado({
      items: opciones,
      abierto,
      setAbierto,
      onElegir: (item) => onAgregar(item.id),
      onCerrar: () => setAbierto(false),
      // Agrega a una lista y el campo queda listo para el siguiente: mover el
      // foco al campo de al lado cortaría la carga en seco.
      saltarAlSiguiente: false,
    });

  return (
    <>
      <Input
        {...propsAriaInput}
        type="text"
        value={valor}
        onChange={(e) => { onValorChange(e.target.value); setAbierto(true); }}
        onFocus={(e) => { setAbierto(true); e.target.select(); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
      />
      {abierto && (
        <DropdownList role="listbox">
          {opciones.length === 0 ? (
            <DropdownItem style={{ color: 'inherit' }}>{vacioTexto}</DropdownItem>
          ) : (
            opciones.map((opcion, indice) => (
              <DropdownItem
                key={opcion.id}
                id={`opcion-${indice}`}
                ref={registrarOpcion(indice)}
                role="option"
                aria-selected={false}
                $activo={indice === indiceActivo}
                onMouseEnter={() => setIndiceActivo(indice)}
                onClick={() => onAgregar(opcion.id)}
              >
                {renderOpcion(opcion)}
              </DropdownItem>
            ))
          )}
        </DropdownList>
      )}
    </>
  );
};


/* NUEVO: Contenedor con scroll para los datos */
const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  /* Usar flex-grow para ocupar el resto del espacio */
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  max-height: ${({ $embedded, theme }) => (
    $embedded
      ? 'calc((100vh / var(--app-zoom, 1)) - 220px)'
      : `calc((100vh / var(--app-zoom, 1)) - ${theme.heights.navbar} - ${theme.heights.cintaOpciones} - ${theme.heights.footer} - 165px)`
  )};
  overflow-y: auto;
  padding-bottom: ${({ $embedded }) => ($embedded ? '0' : '0px')};
  
  /* Estilizado del scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.bgPrincipal};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollThumb};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.scrollThumbHover};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  /* HEADER SÓLIDO Y NO TRANSPARENTE */
    background: ${({ theme }) => theme.colors.bgSecundario};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeaderCell = styled.th`
  padding: ${({ $embedded, $compact }) => {
    if ($compact) return $embedded ? '0.35rem 0.55rem' : '0.5rem 0.65rem';
    return $embedded ? '0.6rem' : '1rem';
  }};
  text-align: left;
  font-weight: 600;
  /* TEXTO BLANCO PARA CONTRASTE CON HEADER SÓLIDO */
    color: ${({ theme }) => theme.colors.txtPrincipal};
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: ${({ $embedded, $compact }) => ($compact ? ($embedded ? '0.7rem' : '0.8rem') : ($embedded ? '0.85rem' : '0.9rem'))};
  line-height: ${({ $compact }) => ($compact ? 1.1 : 1.2)};
  ${({ $sortable }) => $sortable && `
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    &:hover { opacity: 0.8; }
  `}
`;

const TableBody = styled.tbody`
  tr:nth-child(even) {
    background: ${({ theme }) => `${theme.colors.onPrimary}05`};
  }
  
  tr:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  tr:focus-within {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const TableCell = styled.td`
  padding: ${({ $embedded, $compact }) => {
    if ($compact) return $embedded ? '0.45rem 0.6rem' : '0.55rem 0.75rem';
    return $embedded ? '0.6rem 0.8rem' : '0.8rem 1rem';
  }};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: ${({ $embedded, $compact }) => ($compact ? ($embedded ? '0.7rem' : '0.8rem') : ($embedded ? '0.85rem' : '0.9rem'))};
  line-height: ${({ $compact }) => ($compact ? 1.15 : 1.25)};
`;

const ActionsCell = styled(TableCell)`
  text-align: right;
  white-space: nowrap;
  /* Mantener la celda visible para preservar bordes de fila;
     ocultamos solo los botones de acción hasta hover/focus. */
  & > * {
    display: inline-flex;
    vertical-align: middle;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease;
  }

  & > * + * {
    margin-left: 0.5rem;
  }

  tr:hover & > *,
  tr:focus-within & > * {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    & > * {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const ActionButton = SharedActionButton;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme, $soft }) => $soft ? theme.colors.overlayBackdropSoft : theme.colors.overlayBackdropStrong};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ $zIndex }) => ($zIndex != null ? $zIndex : 1000)};
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border-radius: 8px;
  width: 100%;
  max-width: ${({ $embedded }) => ($embedded ? '560px' : '680px')};
  min-height: ${({ $embedded, $compact }) => ($embedded || $compact ? 'auto' : '700px')};
  max-height: calc((100vh / var(--app-zoom, 1)) - 4rem);
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  display: flex;
  flex-direction: column;
`;

const SelectedChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const SelectedChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.8rem;
`;

const ChipRemoveButton = styled.button`
  border: none;
  background: ${({ theme }) => theme.colors.transparent};
  color: ${({ theme }) => theme.colors.txtSecundario};
  cursor: pointer;
  line-height: 1;
  padding: 0;
  font-size: 0.85rem;
`;

const EquivalenciasList = styled.div`
  margin-top: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 8px;
  overflow: hidden;
`;

const EquivalenciaRow = styled.div`
  display: grid;
  grid-template-columns: 120px 110px 1fr 55px 50px;
  gap: 8px;
  align-items: center;
  padding: 0.3rem 0.55rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: 0.8rem;

  &:last-child {
    border-bottom: none;
  }

  & > span:nth-last-child(2),
  & > span:last-child {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  & > span:last-child button {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
  }

  &:hover > span:last-child button,
  &:focus-within > span:last-child button {
    opacity: 1;
    pointer-events: auto;
  }
`;

const EquivalenciaHeader = styled(EquivalenciaRow)`
  background: ${({ theme }) => theme.colors.bgPrincipal};
  font-weight: 600;
`;

// Limita el alto de la tabla de equivalencias seleccionadas para que el modal
// no crezca sin límite cuando hay muchas — el modal completo ya tiene bastante
// contenido y esto evita que quede pegado contra el header de la app.
const EquivalenciasBody = styled.div`
  max-height: 160px;
  overflow-y: auto;
`;

const EquivalenciaDeleteButton = styled.button`
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.danger};
  background: ${({ theme }) => `${theme.colors.danger}12`};
  color: ${({ theme }) => theme.colors.danger};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  i {
    font-size: 0.72rem;
    line-height: 1;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ $embedded }) => ($embedded ? '0.75rem 1rem' : '0.9rem 1.5rem')};
  border-bottom: ${({ theme, $embedded }) => ($embedded ? `1px solid ${theme.colors.info}` : `2px solid ${theme.colors.info}`)};
  background: ${({ theme }) => theme.colors.bgSecundario};
  flex-shrink: 0;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ $embedded }) => ($embedded ? '1rem' : '0.85rem 1.25rem')};
  min-height: 0;
`;

const ModalTitle = styled.h2`
  color: ${({ theme }) => theme.colors.info};
  margin: 0;
  font-size: ${({ $embedded }) => ($embedded ? '1.1rem' : '1.4rem')}; /* Reducido de 1.5rem */
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: ${({ theme }) => theme.colors.transparent};
  border: none;
  color: ${({ theme }) => theme.colors.txtSecundario};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => `${theme.colors.danger}33`};
    color: ${({ theme }) => theme.colors.danger};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 0.85rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.9rem; /* Reducido de 1rem */
`;

const Input = styled.input`
  width: 100%;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.95rem;
  min-height: 34px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.info};
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.info}33`};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.txtSecundario};
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.95rem;
  min-height: 34px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.info};
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.info}33`};
  }

  option {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.txtPrincipal};
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.95rem;
  resize: vertical;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ $embedded }) => ($embedded ? '0.5rem' : '1rem')};
  justify-content: space-between;
  align-items: center;
  padding: ${({ $embedded }) => ($embedded ? '0.65rem 1rem' : '0.75rem 1.5rem')};
  background: ${({ theme }) => theme.colors.bgSecundario};
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  flex-shrink: 0;
`;

const ButtonGroupLeft = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: center;
`;

const ButtonGroupRight = styled.div`
  display: flex;
  gap: ${({ $embedded }) => ($embedded ? '0.5rem' : '0.75rem')};
  align-items: center;
`;

// Using shared buttons from common/Buttons.jsx (BaseButton, PrimaryButton, NavButton, DuplicarButton)

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 3px solid ${({ theme }) => theme.colors.borderDefault};
    border-top: 3px solid ${({ theme }) => theme.colors.info}; /* Usar color 'info' del tema */
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
  }
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => `${theme.colors.danger}1A`};
  border: 1px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: ${({ theme }) => theme.colors.txtSecundario};
  font-size: 1.1rem;
  
  i {
    font-size: 3rem;
    margin-bottom: 1rem;
    display: block;
    opacity: 0.5;
  }
`;

const ConfirmDialog = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border-radius: 8px;
  padding: 2rem;
  width: 100%;
  max-width: ${({ $wide }) => $wide ? '520px' : '400px'};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  text-align: center;
`;

const BloqueadosLista = styled.ul`
  list-style: none;
  margin: 0 0 2rem 0;
  padding: 0;
  max-height: 220px;
  overflow-y: auto;
  text-align: left;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;

  li {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.txtSecundario};

    &:last-child {
      border-bottom: none;
    }

    strong {
      color: ${({ theme }) => theme.colors.txtPrincipal};
    }
  }
`;

// Detalle de "quién usa" el registro que se quiere borrar: un bloque por cada
// tipo de registro que lo referencia, con los nombres concretos.
const UsosLista = styled.ul`
  list-style: none;
  margin: 0 0 1.5rem 0;
  padding: 0;
  max-height: 220px;
  overflow-y: auto;
  text-align: left;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;

  li {
    padding: 0.6rem 0.75rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.txtSecundario};

    &:last-child {
      border-bottom: none;
    }

    strong {
      color: ${({ theme }) => theme.colors.txtPrincipal};
    }
  }
`;

const UsosNombres = styled.div`
  margin-top: 0.35rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  line-height: 1.45;
`;

const ConfirmTitle = styled.h3`
  color: ${({ theme }) => theme.colors.danger};
  margin: 0 0 1rem 0;
  font-size: 1.3rem;
`;

const ConfirmMessage = styled.p`
  color: ${({ theme }) => theme.colors.txtSecundario};
  margin: 0 0 2rem 0;
  line-height: 1.5;
`;

const ConfiguracionBase = ({
  apiEndpoint,
  titulo,
  descripcion,
  icon,
  embedded = false,
  compact = false,
  backTo = '/configuracion',
  modalOnly = false,
  autoOpenAdd = false,
  autoOpenEditId = null,
  onRecordSaved = null,
  onRequestClose = null,
  modalZIndex = null,
  help = null,
}) => {
  const childModalZIndex = (modalZIndex || 1000) + 100;
  const theme = useTheme();

  // "Sin sistema" es un Sistema real del catálogo (placeholder que dejó la
  // importación en tareas sin sistema resuelto), pero no es un dato válido
  // para el usuario — se muestra igual que "sin dato" (guion) en vez de como
  // si fuera un sistema real, para poder detectar estas filas de un vistazo.
  const renderNombreSistema = (nombre) => (nombre && nombre !== 'Sin sistema' ? nombre : '-');
  const [datos, setDatos] = useState([]);
  const datosCargadosRef = useRef(0);
  useEffect(() => { datosCargadosRef.current = datos.length; }, [datos]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [totalRegistros, setTotalRegistros] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromSection = searchParams.get('from');
  const resolvedBackTo = (backTo === '/configuracion' && fromSection)
    ? `/configuracion?seccion=${fromSection}`
    : backTo;
  // Tablas que crecen sin techo con la operación: la búsqueda la resuelve el
  // servidor, nunca el navegador (ver CLAUDE.md, "para buscar en una tabla
  // grande, buscar en el servidor").
  //
  // Filtrar en memoria obliga a bajarse la tabla entera antes de poder buscar.
  // Medido en producción con 22.294 repuestos: 223 peticiones, 14 MB de JSON y
  // ~3,6 minutos para buscar una palabra, contra 1 petición y ~1 s pidiéndole
  // el filtro a Postgres. Y el costo crece con cada alta.
  //
  // Los catálogos acotados (marcas, tipos, estados, unidades…) NO van acá: son
  // decenas de filas, no crecen con el uso, y filtrar en memoria es correcto y
  // más ágil para ellos. El criterio es cómo crece la tabla, no su tamaño de hoy.
  const BUSQUEDA_EN_SERVIDOR = ['repuestos', 'tareas'];
  const buscaEnServidor = BUSQUEDA_EN_SERVIDOR.includes(apiEndpoint);

  const isTareas = apiEndpoint === 'tareas';
  const isModelos = apiEndpoint === 'modelos';
  const isRepuestos = apiEndpoint === 'repuestos';
  const isSubfamiliasRepuesto = apiEndpoint === 'subfamilias-repuesto';
  const isUnidadesMedida = apiEndpoint === 'unidades-medida';
  const isComponentes = apiEndpoint === 'componentes';
  const isDepositos = apiEndpoint === 'depositos';
  const isSistemas = apiEndpoint === 'sistemas';
  const isObras = apiEndpoint === 'obras';
  const isSectoresTrabajo = apiEndpoint === 'sectores-trabajo';
  const isEmpresas = apiEndpoint === 'empresas';
  const isCatalogoPuntoControl = apiEndpoint === 'catalogo-puntos-control';
  const isMecanicos = apiEndpoint === 'mecanicos';
  const isEstadosReparacion = apiEndpoint === 'estados-reparacion';
  const isCategoriasLogistica = apiEndpoint === 'categorias-logistica';

  const bulkFkFields = useMemo(() => {
    const map = {
      'modelos':                  [{ key: 'marca',      label: 'Marca',      optsKey: 'marcasOpts' }],
      'componentes':              [{ key: 'sistema',    label: 'Sistema',    optsKey: 'sistemasOpts' }],
      // Para tareas y repuestos, componente/depósito se maneja aparte con un toggle en el
      // modal masivo (son mutuamente excluyentes), no como FK fijo. Acá queda solo el resto.
      'tareas':                   [{ key: 'sistema',    label: 'Sistema',    optsKey: 'sistemasOpts' }],
      'subfamilias-repuesto':     [{ key: 'familia',    label: 'Familia',    optsKey: 'familiasOpts' }],
      'repuestos':                [{ key: 'familia',    label: 'Familia',    optsKey: 'familiasOpts' }, { key: 'marca', label: 'Marca', optsKey: 'marcasRepuestosOpts' }, { key: 'subfamilia', label: 'Subfamilia', optsKey: 'subfamiliasRepuestoOpts' }, { key: 'unidad_medida', label: 'Unidad de medida', optsKey: 'unidadesMedidaOpts' }],
      'sectores-trabajo':         [{ key: 'obra',       label: 'Obra',       optsKey: 'obrasOpts' }],
      'obras':                    [{ key: 'sucursal',   label: 'Sucursal',   optsKey: 'sucursalesOpts' }],
      'catalogo-puntos-control':  [{ key: 'sistema',    label: 'Sistema',    optsKey: 'sistemasOpts' }, { key: 'tarea', label: 'Tarea', optsKey: 'tareasOpts' }],
    };
    return map[apiEndpoint] || [];
  }, [apiEndpoint]);
  const bulkChoiceFields = useMemo(() => {
    const SI_NO = [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }];
    const map = {
      'catalogo-puntos-control': [
        { key: 'tipo_estado_default', label: 'Tipo', options: [
          { value: 'condicion', label: 'Condición – B / R / M' },
          { value: 'presencia', label: 'Presencia – P / NP' },
        ]},
      ],
      'repuestos': [
        { key: 'uso_mantenimiento_diario', label: 'Uso en mantenimiento diario', options: SI_NO },
        { key: 'no_usar', label: 'No usar (discontinuado)', options: SI_NO },
        { key: 'moneda', label: 'Moneda del precio', options: [
          { value: 'ARS', label: 'Pesos ($)' },
          { value: 'USD', label: 'Dólares (USD)' },
        ]},
      ],
      'tareas': [
        { key: 'requiere_repuestos', label: 'Requiere repuestos', options: SI_NO },
      ],
    };
    return map[apiEndpoint] || [];
  }, [apiEndpoint]);
  const bulkNumberFields = useMemo(() => {
    const map = {
      'repuestos': [
        { key: 'punto_de_pedido', label: 'Punto de pedido', step: '1', min: '0' },
        { key: 'precio_unitario', label: 'Precio unitario', step: '0.01', min: '0' },
      ],
      'tareas': [
        { key: 'tiempo_estimado_minutos', label: 'Tiempo estimado (min)', step: '1', min: '0' },
      ],
      'mecanicos': [
        { key: 'costo_hora', label: 'Costo por hora', step: '0.01', min: '0' },
      ],
    };
    return map[apiEndpoint] || [];
  }, [apiEndpoint]);
  const bulkTextFields = useMemo(() => {
    const map = {
      'repuestos': [
        { key: 'descripcion', label: 'Descripción' },
      ],
    };
    return map[apiEndpoint] || [];
  }, [apiEndpoint]);

  const supportsBulkEdit = bulkFkFields.length > 0 || bulkChoiceFields.length > 0 || bulkNumberFields.length > 0 || bulkTextFields.length > 0;

  // Estado de edición masiva
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFieldValues, setBulkFieldValues] = useState({});
  // Tareas: en la edición masiva se elige componente o depósito (excluyentes).
  const [bulkTipoAsignacion, setBulkTipoAsignacion] = useState('componente'); // 'componente' | 'deposito'
  // Distingue "el usuario tocó el toggle" de "nunca lo abrió": permite fijar el tipo de
  // vínculo (deposito/componente) para todas sin elegir todavía el valor específico de cada una.
  const [bulkAsignacionTocada, setBulkAsignacionTocada] = useState(false);
  // Repuestos: mismo patrón que Tareas pero en estado separado (pieza vs. fluido).
  const [bulkTipoAsignacionRepuesto, setBulkTipoAsignacionRepuesto] = useState('componente');
  const [bulkAsignacionRepuestoTocada, setBulkAsignacionRepuestoTocada] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  // Borrado masivo: reusa bulkMode/selectedIds, pero es independiente de supportsBulkEdit
  // (está disponible en todas las pantallas de Configuración, tengan o no edición masiva).
  const [showConfirmBulkDelete, setShowConfirmBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null); // { bloqueados: [...] } tras un borrado parcial
  // { nombre, desvinculable, grupos: [...] } cuando el borrado individual choca
  // con registros que lo usan. Convierte el diálogo de confirmación en el
  // detalle de "quién lo usa" + la opción de quitar los vínculos y borrar igual.
  const [bloqueoEliminar, setBloqueoEliminar] = useState(null);
  const lastCheckedIdRef = useRef(null);

  const toggleBulkMode = useCallback(() => {
    setBulkMode(prev => !prev);
    setSelectedIds(new Set());
    lastCheckedIdRef.current = null;
  }, []);

  const toggleSelectId = useCallback((id, shiftKey, listaIds) => {
    // La ref se lee y se fija ACÁ, fuera del updater funcional: en StrictMode (dev)
    // React invoca ese updater dos veces para detectar funciones impuras, y si
    // lastCheckedIdRef se mutara adentro, la segunda invocación vería el ref ya
    // actualizado por la primera y rompería el cálculo del rango.
    const anchorId = lastCheckedIdRef.current;
    lastCheckedIdRef.current = id;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (shiftKey && anchorId != null && listaIds) {
        const fromIdx = listaIds.indexOf(anchorId);
        const toIdx = listaIds.indexOf(id);
        if (fromIdx !== -1 && toIdx !== -1) {
          const [start, end] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
          const adding = !prev.has(id);
          for (let i = start; i <= end; i++) {
            if (adding) next.add(listaIds[i]);
            else next.delete(listaIds[i]);
          }
          return next;
        }
      }
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((ids) => {
    setSelectedIds(prev => prev.size === ids.length ? new Set() : new Set(ids));
  }, []);

  const abrirBulkModal = useCallback(() => {
    setBulkFieldValues({});
    setBulkTipoAsignacion('componente');
    setBulkAsignacionTocada(false);
    setBulkTipoAsignacionRepuesto('componente');
    setBulkAsignacionRepuestoTocada(false);
    setShowBulkModal(true);
  }, []);

  const cerrarBulkModal = useCallback(() => {
    setShowBulkModal(false);
    setBulkFieldValues({});
    setBulkTipoAsignacion('componente');
    setBulkAsignacionTocada(false);
    setBulkTipoAsignacionRepuesto('componente');
    setBulkAsignacionRepuestoTocada(false);
  }, []);

  const [helpOpen, setHelpOpen] = useState(false);

  // Estados del modal
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [itemEditar, setItemEditar] = useState(null);
  const [itemEliminar, setItemEliminar] = useState(null);
  const [nombre, setNombre] = useState('');
  const [abreviaturaUnidadMedida, setAbreviaturaUnidadMedida] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [cuit, setCuit] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [direccion, setDireccion] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  // Extra fields for tareas
  const [descripcionItem, setDescripcionItem] = useState('');
  const [tiempoEstimado, setTiempoEstimado] = useState('');
  const [requiereRepuestos, setRequiereRepuestos] = useState(false);
  const [esTanqueCombustible, setEsTanqueCombustible] = useState(false);
  const [componenteId, setComponenteId] = useState('');
  const [componentesOpts, setComponentesOpts] = useState([]);
  // Tareas: la tarea interviene sobre un componente O sobre un depósito de fluido.
  const [tipoAsignacion, setTipoAsignacion] = useState('componente'); // 'componente' | 'deposito'
  const [depositoId, setDepositoId] = useState('');
  const [depositosOpts, setDepositosOpts] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const puedeImportar = ENTIDADES_IMPORTABLES.includes(apiEndpoint);
  // Repuestos tiene además una planilla propia para actualizar precio/moneda/fecha
  // en masa (no da de alta nada, ver ENTIDADES_SOLO_ACTUALIZAN en ImportarModal).
  const [showImportPreciosModal, setShowImportPreciosModal] = useState(false);
  const [showAgregarComp, setShowAgregarComp] = useState(false);
  const [showAgregarDeposito, setShowAgregarDeposito] = useState(false);
  const [showAgregarSistema, setShowAgregarSistema] = useState(false);
  const [showAgregarTarea, setShowAgregarTarea] = useState(false);
  const [showAgregarFamiliaRepuesto, setShowAgregarFamiliaRepuesto] = useState(false);
  const [showAgregarSubfamiliaRepuesto, setShowAgregarSubfamiliaRepuesto] = useState(false);
  const [showAgregarMarcaRepuesto, setShowAgregarMarcaRepuesto] = useState(false);
  const [showAgregarUnidadMedida, setShowAgregarUnidadMedida] = useState(false);
  const [showAgregarMarcaModelo, setShowAgregarMarcaModelo] = useState(false);
  const [showAgregarObraSector, setShowAgregarObraSector] = useState(false);
  // Relación Marca para Modelos
  const [marcaId, setMarcaId] = useState('');
  const [marcasOpts, setMarcasOpts] = useState([]);
  const [filtroMarcaId, setFiltroMarcaId] = useState('');
  const [obraId, setObraId] = useState('');
  const [obrasOpts, setObrasOpts] = useState([]);
  const [filtroObraId, setFiltroObraId] = useState('');
  const [sistemaId, setSistemaId] = useState('');
  const [sistemasOpts, setSistemasOpts] = useState([]);
  const [filtroSistemaId, setFiltroSistemaId] = useState('');
  const [, setObraSearch] = useState('');
  const [, setObraOpen] = useState(false);
  const [centroCostoId, setCentroCostoId] = useState('');
  const [centrosCostoOpts, setCentrosCostoOpts] = useState([]);
  const [, setCentroCostoSearch] = useState('');
  const [, setCentroCostoOpen] = useState(false);
  const [sucursalId, setSucursalId] = useState('');
  const [sucursalesOpts, setSucursalesOpts] = useState([]);
  const [, setSucursalSearch] = useState('');
  const [showSubCentroCosto, setShowSubCentroCosto] = useState(false);
  // Extra fields for estados-reparacion
  const [colorValue, setColorValue] = useState('');
  // Extra fields for mecanicos
  const [mecanicoUsuarioId, setMecanicoUsuarioId] = useState('');
  const [mechanicoCostoHora, setMechanicoCostoHora] = useState('');
  const [usuariosMecanico, setUsuariosMecanico] = useState([]);
  const [moneda, setMoneda] = useState('ARS');
  const [tarifaCorta, setTarifaCorta] = useState('');
  const [tarifaLarga, setTarifaLarga] = useState('');
  const [distanciaCorte, setDistanciaCorte] = useState('100');
  const [tarifaUnificada, setTarifaUnificada] = useState(false);
  const [tarifaUnica, setTarifaUnica] = useState('');
  // Extra fields for repuestos
  const [codigo, setCodigo] = useState('');
  const [descripcionRepuesto, setDescripcionRepuesto] = useState('');
  const [stockRepuesto, setStockRepuesto] = useState('');
  // Repuesto: asignación exclusiva a Componente o Depósito, igual que Tarea.
  const [tipoAsignacionRepuesto, setTipoAsignacionRepuesto] = useState('componente');
  const [repuestoDepositoId, setRepuestoDepositoId] = useState('');
  const [repuestoDepositosIds, setRepuestoDepositosIds] = useState([]);
  const [repuestoDepQuery, setRepuestoDepQuery] = useState('');
  const [repuestoDepOpen, setRepuestoDepOpen] = useState(false);
  const [usoMantenimientoDiario, setUsoMantenimientoDiario] = useState(false);
  const [noUsarRepuesto, setNoUsarRepuesto] = useState(false);
  const [subfamiliaRepuestoId, setSubfamiliaRepuestoId] = useState('');
  const [, setSubfamiliaRepuestoSearch] = useState('');
  const [, setSubfamiliaRepuestoOpen] = useState(false);
  const [puntoPedidoRepuesto, setPuntoPedidoRepuesto] = useState('');
  const [precioUnitarioRepuesto, setPrecioUnitarioRepuesto] = useState('');
  const [monedaRepuesto, setMonedaRepuesto] = useState('ARS');
  const [equivalentesIds, setEquivalentesIds] = useState([]);
  const [equivalenteSearch, setEquivalenteSearch] = useState('');
  const [equivalenteOpen, setEquivalenteOpen] = useState(false);
  const [stockPorRepuestoId, setStockPorRepuestoId] = useState({});
  // Stock real de los equivalentes ya guardados, tal como lo calcula el backend
  // (mismo endpoint que usa el selector de equivalencias en Almacén) — evita el
  // desfasaje del cálculo local, que además solo lee las primeras 1000 filas de stock.
  const [stockEquivalentesGuardados, setStockEquivalentesGuardados] = useState({});
  const [familiaId, setFamiliaId] = useState('');
  const [subfamiliaFamiliaId, setSubfamiliaFamiliaId] = useState('');
  const [, setSubfamiliaFamiliaSearch] = useState('');
  const [, setSubfamiliaFamiliaOpen] = useState(false);
  const [repuestoMarcaId, setRepuestoMarcaId] = useState('');
  const [repuestoUnidadMedidaId, setRepuestoUnidadMedidaId] = useState('');
  const [repuestoComponenteId, setRepuestoComponenteId] = useState('');
  const [repuestoComponentesIds, setRepuestoComponentesIds] = useState([]);
  const [repuestoCompQuery, setRepuestoCompQuery] = useState('');
  const [repuestoCompOpen, setRepuestoCompOpen] = useState(false);
  const [, setFamiliaSearch] = useState('');
  const [, setFamiliaOpen] = useState(false);
  const [, setMarcaRepuestoSearch] = useState('');
  const [, setMarcaRepuestoOpen] = useState(false);
  const [, setUnidadMedidaSearch] = useState('');
  const [, setUnidadMedidaOpen] = useState(false);
  const [familiasOpts, setFamiliasOpts] = useState([]);
  const [subfamiliasRepuestoOpts, setSubfamiliasRepuestoOpts] = useState([]);
  const [marcasRepuestosOpts, setMarcasRepuestosOpts] = useState([]);
  const [unidadesMedidaOpts, setUnidadesMedidaOpts] = useState([]);
  const [componentesRepuestoOpts, setComponentesRepuestoOpts] = useState([]);
  const [repuestosOpts, setRepuestosOpts] = useState([]);
  // Extra fields for catalogo-puntos-control
  const [tareaId, setTareaId] = useState('');
  const [, setTareaLabel] = useState('');
  const [, setTareaOpen] = useState(false);
  const [tareasOpts, setTareasOpts] = useState([]);
  const [tipoEstadoDefault, setTipoEstadoDefault] = useState('condicion');
  const tareaWrapperRef = useRef(null);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Búsqueda server-side (ver BUSQUEDA_EN_SERVIDOR).
  const [buscandoServidor, setBuscandoServidor] = useState(false);
  // Término que produjo lo que hay en pantalla; null = listado sin filtrar.
  const resultadoBusquedaRef = useRef(null);
  // Cada búsqueda lleva número: sólo la última puede escribir el estado, para
  // que al tipear rápido la respuesta de un término viejo no pise a la actual.
  const busquedaCorridaRef = useRef(0);
  const autoOpenedRef = useRef(false);
  const autoOpenedEditRef = useRef(false);
  const loadRequestRef = useRef(0);
  const repuestosOptionsHydratedRef = useRef(false);
  const repuestosLookupsHydratedRef = useRef(false);
  const postGuardadoRef = useRef('cerrar');
  const duplicarItemFnRef = useRef(null);
  const abrirModalAgregarRef = useRef(null);
  const navItemTargetRef = useRef(null);
  // Fuerza un nuevo autofoco cuando el modal ya estaba abierto (Guardar y
  // nuevo/duplicar/anterior/siguiente no cierran el modal, así que `showModal`
  // no cambia y el efecto de autofoco no se re-dispara solo).
  const [autoFocoTrigger, setAutoFocoTrigger] = useState(0);

  const resolveRelacionId = useCallback((item, campo, campoNombre, opciones = []) => {
    const directo = item?.[campo];
    if (directo && typeof directo === 'object') {
      if (directo.id != null) return String(directo.id);
    } else if (directo != null && directo !== '') {
      return String(directo);
    }

    const alternativo = item?.[`${campo}_id`];
    if (alternativo != null && alternativo !== '') {
      return String(alternativo);
    }

    const nombre = item?.[campoNombre];
    if (nombre && Array.isArray(opciones) && opciones.length > 0) {
      const match = opciones.find(opt => String(opt?.nombre || '').trim().toLowerCase() === String(nombre).trim().toLowerCase());
      if (match?.id != null) return String(match.id);
    }

    return '';
  }, []);

  const handleComponenteCreadoDesdeFormulario = useCallback(async (savedRecord) => {
    try {
      const resp = await apiClient.get('componentes/');
      const list = resp.data.results || resp.data || [];
      setComponentesOpts(list);
      setComponentesRepuestoOpts(list);
      const createdId = savedRecord?.id != null ? String(savedRecord.id) : '';
      if (createdId) {
        if (isRepuestos) {
          setRepuestoComponenteId(createdId);
          setRepuestoComponentesIds(prev => {
            const base = (prev || []).map(v => String(v));
            if (base.includes(createdId)) return base;
            return [...base, createdId];
          });
        } else {
          setComponenteId(createdId);
        }
      }
      setShowAgregarComp(false);
    } catch (err) {
      console.error('Error refrescando componentes luego de alta', err);
    }
  }, [isRepuestos]);

  const handleDepositoCreadoDesdeFormulario = useCallback(async (savedRecord) => {
    try {
      const list = await fetchAllOptions('depositos');
      setDepositosOpts(Array.isArray(list) ? list : []);
      const createdId = savedRecord?.id != null ? String(savedRecord.id) : '';
      if (createdId) {
        if (isRepuestos) {
          setRepuestoDepositoId(createdId);
          setRepuestoDepositosIds(prev => {
            const base = (prev || []).map(v => String(v));
            if (base.includes(createdId)) return base;
            return [...base, createdId];
          });
        } else {
          setDepositoId(createdId);
        }
      }
      setShowAgregarDeposito(false);
    } catch (err) {
      console.error('Error refrescando depósitos luego de alta', err);
    }
  }, [isRepuestos]);

  const handleSistemaCreadoDesdeFormulario = useCallback(async (savedRecord) => {
    try {
      const list = await fetchAllOptions('sistemas');
      setSistemasOpts(Array.isArray(list) ? list : []);
      if (savedRecord?.id != null) {
        setSistemaId(String(savedRecord.id));
        setSistemaLabel(savedRecord.nombre || '');
      }
      setShowAgregarSistema(false);
    } catch (err) {
      console.error('Error refrescando sistemas luego de alta', err);
    }
  }, []);

  const handleTareaCreadaDesdeFormulario = useCallback(async (savedRecord) => {
    try {
      const list = await fetchAllOptions('tareas');
      setTareasOpts(Array.isArray(list) ? list : []);
      if (savedRecord?.id != null) {
        setTareaId(String(savedRecord.id));
        setTareaLabel(savedRecord.nombre || '');
      }
      setShowAgregarTarea(false);
    } catch (err) {
      console.error('Error refrescando tareas luego de alta', err);
    }
  }, []);

  const handleMarcaCreadaDesdeFormularioModelo = useCallback(async (savedRecord) => {
    try {
      const list = await fetchAllOptions('marcas');
      setMarcasOpts(Array.isArray(list) ? list : []);
      if (savedRecord?.id != null) {
        setMarcaId(String(savedRecord.id));
        setFieldErrors(prev => ({ ...prev, marca: undefined }));
      }
      setShowAgregarMarcaModelo(false);
    } catch (err) {
      console.error('Error refrescando marcas luego de alta', err);
    }
  }, []);

  const handleObraCreadaDesdeFormularioSector = useCallback(async (savedRecord) => {
    try {
      const list = await fetchAllOptions('obras');
      setObrasOpts(Array.isArray(list) ? list : []);
      if (savedRecord?.id != null) {
        setObraId(String(savedRecord.id));
        setObraSearch(savedRecord.nombre || '');
      }
      setShowAgregarObraSector(false);
    } catch (err) {
      console.error('Error refrescando obras luego de alta', err);
    }
  }, []);

  // Cargar datos
  const cargarDatos = useCallback(async () => {
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    setError('');
    try {
      if (isRepuestos) {
        // Renderizar rápido con primera página y permitir paginación explícita.
        // El ABM es donde se gestionan los discontinuados: acá sí se listan.
        const firstResp = await apiClient.get(`${apiEndpoint}/`, { params: { incluir_no_usar: true } });
        const firstPayload = firstResp.data;

        if (Array.isArray(firstPayload)) {
          if (loadRequestRef.current !== requestId) return;
          setDatos(firstPayload);
          setTotalRegistros(firstPayload.length);
          setNextPageUrl(null);
          return;
        }

        let acumuladoRepuestos = Array.isArray(firstPayload?.results) ? firstPayload.results : [];
        let siguienteUrl = firstPayload?.next || null;
        // Si ya había más páginas cargadas por scroll infinito (p.ej. tras editar/eliminar
        // un repuesto), volver a hidratarlas para no "perder" el scroll del usuario.
        //
        // Con una búsqueda activa NO se rehidrata: lo que hay en pantalla son
        // las coincidencias que devolvió el servidor, y volver a pedir páginas
        // del listado completo para "recuperar" el largo anterior es justo el
        // recorrido de 223 peticiones que esta pantalla dejó de hacer.
        const objetivoPrevio = resultadoBusquedaRef.current !== null
          ? 0
          : datosCargadosRef.current;
        let vueltas = 0;
        while (siguienteUrl && acumuladoRepuestos.length < objetivoPrevio && vueltas < 60) {
          const resp = await apiClient.get(siguienteUrl);
          const payload = resp.data;
          const chunk = Array.isArray(payload) ? payload : (Array.isArray(payload?.results) ? payload.results : []);
          acumuladoRepuestos = acumuladoRepuestos.concat(chunk);
          siguienteUrl = Array.isArray(payload) ? null : (payload?.next || null);
          vueltas += 1;
        }

        if (loadRequestRef.current !== requestId) return;
        setDatos(acumuladoRepuestos);
        setTotalRegistros(Number(firstPayload?.count || acumuladoRepuestos.length));
        setNextPageUrl(siguienteUrl);
        return;
      }

      // Catálogos acotados: se piden completos de una, con `page_size`
      // explícito. Recorrer `next` de a 100 costaba una petición por cada 100
      // filas — 29 para las tareas de un tenant mediano, cada una con su
      // preflight — cuando el listado entero entra en una sola respuesta.
      let url = `${apiEndpoint}/`;
      let params = { page_size: PAGE_SIZE_CATALOGO };
      let acumulado = [];
      let pages = 0;
      let ultimaUrl = null;
      let quedaronPaginas = false;

      while (url && pages < MAX_PAGINAS_CATALOGO) {
        const resp = await apiClient.get(url, params ? { params } : undefined);
        // `next` ya trae el page_size en la query: no hay que repetirlo.
        params = undefined;
        const data = resp.data;
        if (Array.isArray(data)) { // sin paginación estándar
          acumulado = data;
          url = null;
        } else if (data && Array.isArray(data.results)) {
          acumulado = acumulado.concat(data.results);
          url = data.next || null; // DRF next puede ser absoluta
        } else {
          acumulado = data.results ? data.results : data;
          url = null;
        }
        pages += 1;
        if (url && url === ultimaUrl) { // evitar loop accidental
          console.warn('Detenida paginación por URL repetida', url);
          url = null;
        }
        ultimaUrl = url;
        if (url && pages >= MAX_PAGINAS_CATALOGO) quedaronPaginas = true;
      }

      if (loadRequestRef.current !== requestId) return;
      setDatos(acumulado);
      setTotalRegistros(Array.isArray(acumulado) ? acumulado.length : 0);
      setNextPageUrl(null);

      // Truncar en silencio es el peor final posible: la pantalla muestra una
      // lista más corta y el usuario no tiene forma de saber que le faltan
      // filas. Si se llegó al tope, se dice — y la tabla que lo provoque tiene
      // que pasar a búsqueda server-side (ver BUSQUEDA_EN_SERVIDOR).
      if (quedaronPaginas) {
        console.warn(
          `[${apiEndpoint}] se alcanzó el tope de ${MAX_PAGINAS_CATALOGO} páginas: ` +
          'el listado está incompleto. Esta tabla debería buscar en el servidor.'
        );
        setError(
          'La lista es demasiado grande para mostrarla completa. ' +
          'Usá el buscador para encontrar lo que necesitás.'
        );
      }
    } catch (err) {
      setError('Error al cargar los datos');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, isRepuestos]);

  const cargarMasRepuestos = useCallback(async (cargarTodo = false) => {
    if (!isRepuestos || !nextPageUrl || loadingMore) return;

    setLoadingMore(true);
    let url = nextPageUrl;
    let loops = 0;

    try {
      while (url && loops < 60) {
        const resp = await apiClient.get(url);
        const payload = resp.data;
        const chunk = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.results) ? payload.results : []);

        if (chunk.length) {
          setDatos((prev) => {
            const byId = new Map((prev || []).map((item) => [String(item.id), item]));
            chunk.forEach((item) => {
              if (item?.id != null) byId.set(String(item.id), item);
            });
            return Array.from(byId.values());
          });
        }

        const next = Array.isArray(payload) ? null : (payload?.next || null);
        setNextPageUrl(next);
        url = cargarTodo ? next : null;
        loops += 1;
      }
    } catch (err) {
      console.warn('Error cargando más repuestos', err);
    } finally {
      setLoadingMore(false);
    }
  }, [isRepuestos, nextPageUrl, loadingMore]);

  const handleRepuestosScroll = useCallback((event) => {
    if (!isRepuestos || !nextPageUrl || loadingMore) return;
    const el = event.currentTarget;
    const threshold = 140;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (!nearBottom) return;
    void cargarMasRepuestos(false);
  }, [isRepuestos, nextPageUrl, loadingMore, cargarMasRepuestos]);

  // Búsqueda en el servidor para las tablas que crecen sin techo.
  //
  // Antes, al escribir en el buscador se descargaban TODAS las páginas
  // pendientes para después filtrar en memoria. Con 22.294 repuestos eso eran
  // 223 peticiones encadenadas y ~3,6 minutos de espera — y empeoraba con cada
  // alta. Ahora el filtro lo resuelve Postgres sobre los `search_fields` del
  // ViewSet: alcanza TODAS las filas de la tabla sin tener ninguna en memoria.
  useEffect(() => {
    if (!buscaEnServidor) return;

    const termino = busqueda.trim();
    // Sin término vuelve el listado normal (primera página + scroll infinito).
    // El `cargarDatos` de siempre lo repone; acá sólo se limpia la búsqueda.
    if (!termino) {
      if (resultadoBusquedaRef.current !== null) {
        resultadoBusquedaRef.current = null;
        void cargarDatos();
      }
      return;
    }

    const corrida = ++busquedaCorridaRef.current;
    const timer = setTimeout(async () => {
      setBuscandoServidor(true);
      setError('');
      try {
        const resp = await apiClient.get(`${apiEndpoint}/`, {
          params: { search: termino, page_size: 200, ...(isRepuestos ? { incluir_no_usar: true } : {}) },
        });
        // Una respuesta vieja no puede pisar a la del término actual.
        if (busquedaCorridaRef.current !== corrida) return;
        const payload = resp.data;
        const filas = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.results) ? payload.results : []);
        resultadoBusquedaRef.current = termino;
        setDatos(filas);
        setTotalRegistros(Number(payload?.count ?? filas.length));
        setNextPageUrl(Array.isArray(payload) ? null : (payload?.next || null));
      } catch (err) {
        if (busquedaCorridaRef.current !== corrida) return;
        // Un fallo de red NO se traga: si la búsqueda falla en silencio, el
        // usuario concluye que el registro no existe y lo carga duplicado.
        console.error('Error buscando en el servidor', err);
        setError('No se pudo completar la búsqueda. Revisá la conexión y probá de nuevo.');
      } finally {
        if (busquedaCorridaRef.current === corrida) setBuscandoServidor(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [buscaEnServidor, busqueda, apiEndpoint, isRepuestos, cargarDatos]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Cargar usuarios con rol mecánico si el endpoint es 'mecanicos'
  useEffect(() => {
    if (!isMecanicos) return;
    let cancelled = false;
    usuariosService.listarPorRol('mecanico').then((data) => {
      if (!cancelled) {
        const list = data?.results || (Array.isArray(data) ? data : []);
        setUsuariosMecanico(Array.isArray(list) ? list : []);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isMecanicos]);

  // Cargar marcas si endpoint es 'modelos'
  useEffect(() => {
    if (!isModelos) return;
    let cancelled = false;
    const loadMarcas = async () => {
      try {
        const list = await fetchAllOptions('marcas');
        if (!cancelled) setMarcasOpts(list);
      } catch (err) {
        console.warn('Error cargando marcas', err);
      }
    };
    loadMarcas();
    return () => { cancelled = true; };
  }, [isModelos]);

  useEffect(() => {
    if (!isSectoresTrabajo) return;
    let cancelled = false;
    const loadObras = async () => {
      try {
        const list = await fetchAllOptions('obras');
        if (!cancelled) setObrasOpts(list);
      } catch (err) {
        console.warn('Error cargando obras', err);
      }
    };
    loadObras();
    return () => { cancelled = true; };
  }, [isSectoresTrabajo]);

  useEffect(() => {
    if (!isComponentes && !isDepositos) return;
    let cancelled = false;
    const loadSistemas = async () => {
      try {
        const list = await fetchAllOptions('sistemas');
        if (!cancelled) setSistemasOpts(Array.isArray(list) ? list : []);
      } catch (err) {
        console.warn('Error cargando sistemas', err);
      }
    };
    loadSistemas();
    return () => { cancelled = true; };
  }, [isComponentes, isDepositos]);

  useEffect(() => {
    if (!isObras) return;
    let cancelled = false;
    const loadCentrosCosto = async () => {
      try {
        const list = await fetchAllOptions('centroscosto');
        if (!cancelled) setCentrosCostoOpts(list);
      } catch (err) {
        console.warn('Error cargando centros de costo', err);
      }
    };
    const loadSucursales = async () => {
      try {
        const list = await fetchAllOptions('sucursales');
        if (!cancelled) setSucursalesOpts(list);
      } catch (err) {
        console.warn('Error cargando sucursales', err);
      }
    };
    loadCentrosCosto();
    loadSucursales();
    return () => { cancelled = true; };
  }, [isObras]);

  useEffect(() => {
    if (!isRepuestos || (!showModal && !showBulkModal)) return;
    let cancelled = false;

    const fetchAll = async (endpoint) => {
      let url = `${endpoint}/`;
      let all = [];
      let loops = 0;
      while (url && loops < 40) {
        const resp = await apiClient.get(url);
        const payload = resp.data;
        if (Array.isArray(payload)) {
          all = payload;
          url = null;
        } else if (Array.isArray(payload?.results)) {
          all = all.concat(payload.results);
          url = payload.next || null;
        } else {
          all = payload?.results || [];
          url = null;
        }
        loops += 1;
      }
      return all;
    };

    const fetchLimited = async (endpoint, maxResults = 200, maxPages = 4) => {
      let url = `${endpoint}/`;
      let all = [];
      let loops = 0;
      while (url && loops < maxPages && all.length < maxResults) {
        const resp = await apiClient.get(url);
        const payload = resp.data;
        const chunk = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.results) ? payload.results : []);
        all = all.concat(chunk);
        if (all.length >= maxResults) break;
        url = Array.isArray(payload) ? null : (payload?.next || null);
        loops += 1;
      }
      return all.slice(0, maxResults);
    };

    const loadRepuestosOptions = async () => {
      try {
        if (!repuestosLookupsHydratedRef.current) {
          const [familiasList, subfamiliasList, marcasList, unidadesList, compList, depList] = await Promise.all([
            fetchAll('familias-repuesto'),
            fetchAll('subfamilias-repuesto'),
            fetchAll('marcas-repuesto'),
            fetchAll('unidades-medida'),
            fetchAll('componentes'),
            fetchAll('depositos'),
          ]);
          if (cancelled) return;
          setFamiliasOpts(Array.isArray(familiasList) ? familiasList : []);
          setSubfamiliasRepuestoOpts(Array.isArray(subfamiliasList) ? subfamiliasList : []);
          setMarcasRepuestosOpts(Array.isArray(marcasList) ? marcasList : []);
          setUnidadesMedidaOpts(Array.isArray(unidadesList) ? unidadesList : []);
          setComponentesRepuestoOpts(Array.isArray(compList) ? compList : []);
          setDepositosOpts(Array.isArray(depList) ? depList : []);
          repuestosLookupsHydratedRef.current = true;
        }

        // Cargar catálogo de equivalencias solo cuando el modal está abierto.
        if (repuestosOptionsHydratedRef.current) return;

        const [repuestosList, stockRows] = await Promise.all([
          fetchLimited('repuestos', 250, 5),
          fetchLimited('stock-repuestos', 1000, 10),
        ]);
        if (cancelled) return;

        const stockMap = {};
        (stockRows || []).forEach((s) => {
          const repuestoId = String(s.repuesto);
          stockMap[repuestoId] = (stockMap[repuestoId] || 0) + Number(s.cantidad || 0);
        });

        setRepuestosOpts(Array.isArray(repuestosList) ? repuestosList : []);
        setStockPorRepuestoId(stockMap);
        repuestosOptionsHydratedRef.current = true;
      } catch (err) {
        console.warn('Error cargando opciones de repuestos', err);
      }
    };

    loadRepuestosOptions();
    return () => { cancelled = true; };
  }, [isRepuestos, showModal, showBulkModal]);

  // Stock real de los equivalentes ya guardados: usa el mismo endpoint que el
  // selector de equivalencias en Almacén (repuestos/{id}/equivalencias/), que
  // suma TODO el stock del repuesto en el backend sin el recorte de 1000 filas
  // del catálogo cargado para el buscador.
  useEffect(() => {
    if (!isRepuestos || !showModal || !itemEditar?.id) {
      setStockEquivalentesGuardados({});
      return;
    }
    let cancelled = false;
    apiClient.get(`repuestos/${itemEditar.id}/equivalencias/`)
      .then((resp) => {
        if (cancelled) return;
        const map = {};
        (resp.data?.equivalentes || []).forEach((e) => {
          map[String(e.id)] = Number(e.stock_total || 0);
        });
        setStockEquivalentesGuardados(map);
      })
      .catch((err) => console.warn('Error cargando stock de equivalencias', err));
    return () => { cancelled = true; };
  }, [isRepuestos, showModal, itemEditar]);

  useEffect(() => {
    if (!isSubfamiliasRepuesto) return;
    let cancelled = false;
    const loadFamilias = async () => {
      try {
        const list = await fetchAllOptions('familias-repuesto');
        if (!cancelled) setFamiliasOpts(Array.isArray(list) ? list : []);
      } catch (err) {
        console.warn('Error cargando familias de repuesto', err);
      }
    };
    loadFamilias();
    return () => { cancelled = true; };
  }, [isSubfamiliasRepuesto]);

  // Searchable select state for componentes (used in tareas modal). El label ya
  // no se usa para filtrar (ver comentario más abajo, junto a sistemasFiltrados) —
  // se dejan los setters porque los sigue llamando el resto del formulario de
  // tareas (crear/limpiar vínculo); no leerlos más no cambia ese flujo.
  const [, setComponenteLabel] = useState('');
  const [, setComponenteOpen] = useState(false);
  // Searchable select state for depósitos (tareas modal)
  const [, setDepositoLabel] = useState('');
  const [, setDepositoOpen] = useState(false);
  const [, setSistemaLabel] = useState('');
  const [, setSistemaOpen] = useState(false);
  const repCompWrapperRef = useRef(null);
  const repDepWrapperRef = useRef(null);
  const equivalenciaWrapperRef = useRef(null);
  const {
    onBackdropMouseDown: handleModalBackdropMouseDown,
    buildOnBackdropClick: buildHandleModalBackdropClick,
  } = useBackdropCloseGuard();

  // Sistema, Componente y Depósito del modal de tareas: el propio
  // SearchableSelectField/SearchSuggestField ya resuelve la búsqueda por
  // texto tipeado y el "mostrar todo al enfocar" (showAllOnFocus) puertas
  // adentro. Filtrar acá también por el label seleccionado era redundante y
  // rompía ese comportamiento: al enfocar, SearchSuggestField mostraba TODO
  // lo que recibía en `options`, pero lo que recibía ya venía recortado a
  // un solo ítem (el que coincidía con el texto actual) — por eso el
  // desplegable parecía tener una sola fila hasta borrar el texto a mano.
  const sistemasFiltrados = sistemasOpts || [];

  // Tareas: mientras haya un componente o depósito vinculado, el sistema queda
  // fijado al de ese vínculo — evita guardar una tarea con sistema distinto al
  // de su componente/depósito. Para cambiar el sistema, primero hay que quitar el vínculo.
  const sistemaBloqueadoPorVinculo = isTareas && Boolean(componenteId || depositoId);

  // Componente y Depósito del modal de tareas: el catálogo se muestra completo,
  // sin recortar por el sistema actual de la tarea. La relación es "el
  // componente/depósito elegido define el sistema" (ver el onChange de cada
  // uno, que hace setSistemaId), no al revés — filtrar acá por sistemaId
  // dejaba sin opciones a cualquier tarea con un sistema sin componentes/
  // depósitos propios (p.ej. el placeholder "Sin sistema"), sin forma de
  // salir de ese estado desde el propio selector.
  const componentesFiltrados = componentesOpts || [];

  const depositosFiltrados = depositosOpts || [];

  const componentesRepuestoDisponibles = useMemo(() => {
    if (!Array.isArray(componentesRepuestoOpts)) return [];
    const seleccionados = new Set((repuestoComponentesIds || []).map(id => String(id)));
    const q = repuestoCompQuery.trim().toLowerCase();
    return componentesRepuestoOpts.filter(c => {
      const id = String(c.id);
      if (seleccionados.has(id)) return false;
      if (!q) return true;
      return String(c.nombre || '').toLowerCase().includes(q) || id === q;
    });
  }, [componentesRepuestoOpts, repuestoComponentesIds, repuestoCompQuery]);

  const depositosRepuestoDisponibles = useMemo(() => {
    if (!Array.isArray(depositosOpts)) return [];
    const seleccionados = new Set((repuestoDepositosIds || []).map(id => String(id)));
    const q = repuestoDepQuery.trim().toLowerCase();
    return depositosOpts.filter(d => {
      const id = String(d.id);
      if (seleccionados.has(id)) return false;
      if (!q) return true;
      return String(d.nombre || '').toLowerCase().includes(q) || id === q;
    });
  }, [depositosOpts, repuestoDepositosIds, repuestoDepQuery]);

  const subfamiliasFiltradas = useMemo(() => {
    if (!familiaId) return subfamiliasRepuestoOpts || [];
    return (subfamiliasRepuestoOpts || []).filter((s) => {
      const sfFamiliaId = s?.familia?.id ?? s?.familia ?? s?.familia_id;
      return String(sfFamiliaId || '') === String(familiaId);
    });
  }, [subfamiliasRepuestoOpts, familiaId]);

  const componentesRepuestoSeleccionados = useMemo(() => {
    const index = new Map((componentesRepuestoOpts || []).map(c => [String(c.id), c]));
    return (repuestoComponentesIds || []).map(id => index.get(String(id)) || { id, nombre: `ID ${id}` });
  }, [componentesRepuestoOpts, repuestoComponentesIds]);

  const depositosRepuestoSeleccionados = useMemo(() => {
    const index = new Map((depositosOpts || []).map(d => [String(d.id), d]));
    return (repuestoDepositosIds || []).map(id => index.get(String(id)) || { id, nombre: `ID ${id}` });
  }, [depositosOpts, repuestoDepositosIds]);

  const equivalentesSeleccionados = useMemo(() => {
    const index = new Map((repuestosOpts || []).map(r => [String(r.id), r]));
    return (equivalentesIds || []).map((id) => {
      const idStr = String(id);
      const rep = index.get(idStr);
      if (!rep) return null;
      const stockReal = stockEquivalentesGuardados[idStr];
      return {
        ...rep,
        stock_total: stockReal != null ? stockReal : (stockPorRepuestoId[idStr] ?? 0),
      };
    }).filter(Boolean);
  }, [repuestosOpts, equivalentesIds, stockPorRepuestoId, stockEquivalentesGuardados]);

  const equivalentesDisponibles = useMemo(() => {
    const q = equivalenteSearch.trim().toLowerCase();
    const excluidos = new Set((equivalentesIds || []).map(v => String(v)));
    if (itemEditar?.id != null) excluidos.add(String(itemEditar.id));
    return (repuestosOpts || []).filter((r) => {
      if (excluidos.has(String(r.id))) return false;
      if (!q) return true;
      return String(r.codigo || '').toLowerCase().includes(q)
        || String(r.descripcion || '').toLowerCase().includes(q)
        || String(r.marca_nombre || '').toLowerCase().includes(q);
    });
  }, [repuestosOpts, equivalentesIds, equivalenteSearch, itemEditar]);

  // Sync label when componenteId or componentesOpts change
  useEffect(() => {
    if (!componenteId) {
      setComponenteLabel('');
      return;
    }
    const sel = componentesOpts.find(c => String(c.id) === String(componenteId));
    if (sel) {
      setComponenteLabel(sel.nombre || sel.name || String(sel.id));
    }
  }, [componenteId, componentesOpts]);

  // Sync label when depositoId or depositosOpts change
  useEffect(() => {
    if (!depositoId) {
      setDepositoLabel('');
      return;
    }
    const sel = depositosOpts.find(d => String(d.id) === String(depositoId));
    if (sel) {
      setDepositoLabel(sel.nombre || String(sel.id));
    }
  }, [depositoId, depositosOpts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      // Los desplegables se montan vía portal en document.body, así que un clic en
      // una opción cae fuera de los wrappers y cerraría la lista antes de que corra
      // su onClick. Si el clic nació dentro de un desplegable, no cerramos nada:
      // la propia opción se encarga de cerrarlo al seleccionarse.
      if (e.target.closest?.('[data-dropdown-portal]')) return;
      if (repCompWrapperRef.current && !repCompWrapperRef.current.contains(e.target)) {
        setRepuestoCompOpen(false);
      }
      if (repDepWrapperRef.current && !repDepWrapperRef.current.contains(e.target)) {
        setRepuestoDepOpen(false);
      }
      if (equivalenciaWrapperRef.current && !equivalenciaWrapperRef.current.contains(e.target)) {
        setEquivalenteOpen(false);
      }
      if (tareaWrapperRef.current && !tareaWrapperRef.current.contains(e.target)) {
        setTareaOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showModal || !isRepuestos) return;
    if (!familiaId) {
      setFamiliaSearch('');
      return;
    }
    const selected = (familiasOpts || []).find(f => String(f.id) === String(familiaId));
    setFamiliaSearch(selected?.nombre || '');
  }, [showModal, isRepuestos, familiaId, familiasOpts]);

  useEffect(() => {
    if (!showModal || !isRepuestos) return;
    if (!repuestoMarcaId) {
      setMarcaRepuestoSearch('');
      return;
    }
    const selected = (marcasRepuestosOpts || []).find(m => String(m.id) === String(repuestoMarcaId));
    setMarcaRepuestoSearch(selected?.nombre || '');
  }, [showModal, isRepuestos, repuestoMarcaId, marcasRepuestosOpts]);

  useEffect(() => {
    if (!showModal || !isRepuestos) return;
    if (!repuestoUnidadMedidaId) {
      setUnidadMedidaSearch('');
      return;
    }
    const selected = (unidadesMedidaOpts || []).find(u => String(u.id) === String(repuestoUnidadMedidaId));
    setUnidadMedidaSearch(selected?.nombre || '');
  }, [showModal, isRepuestos, repuestoUnidadMedidaId, unidadesMedidaOpts]);

  useEffect(() => {
    if (!showModal || !isRepuestos) return;
    if (!subfamiliaRepuestoId) {
      setSubfamiliaRepuestoSearch('');
      return;
    }
    const selected = (subfamiliasRepuestoOpts || []).find(s => String(s.id) === String(subfamiliaRepuestoId));
    setSubfamiliaRepuestoSearch(selected?.nombre || '');
  }, [showModal, isRepuestos, subfamiliaRepuestoId, subfamiliasRepuestoOpts]);

  useEffect(() => {
    if (!showModal || !isSubfamiliasRepuesto) return;
    if (!subfamiliaFamiliaId) {
      setSubfamiliaFamiliaSearch('');
      return;
    }
    const selected = (familiasOpts || []).find(f => String(f.id) === String(subfamiliaFamiliaId));
    setSubfamiliaFamiliaSearch(selected?.nombre || '');
  }, [showModal, isSubfamiliasRepuesto, subfamiliaFamiliaId, familiasOpts]);

  useEffect(() => {
    if (!isRepuestos || !familiaId || !subfamiliaRepuestoId) return;
    const selected = (subfamiliasRepuestoOpts || []).find(s => String(s.id) === String(subfamiliaRepuestoId));
    const sfFamiliaId = selected?.familia?.id ?? selected?.familia ?? selected?.familia_id;
    if (selected && String(sfFamiliaId || '') !== String(familiaId)) {
      setSubfamiliaRepuestoId('');
      setSubfamiliaRepuestoSearch('');
    }
  }, [isRepuestos, familiaId, subfamiliaRepuestoId, subfamiliasRepuestoOpts]);

  const agregarComponenteRepuesto = useCallback((id) => {
    const idStr = String(id);
    setRepuestoComponentesIds(prev => {
      const base = (prev || []).map(v => String(v));
      if (base.includes(idStr)) return base;
      return [...base, idStr];
    });
    setRepuestoCompQuery('');
    setRepuestoCompOpen(false);
  }, []);

  const quitarComponenteRepuesto = useCallback((id) => {
    const idStr = String(id);
    setRepuestoComponentesIds(prev => (prev || []).map(v => String(v)).filter(v => v !== idStr));
  }, []);

  const agregarDepositoRepuesto = useCallback((id) => {
    const idStr = String(id);
    setRepuestoDepositosIds(prev => {
      const base = (prev || []).map(v => String(v));
      if (base.includes(idStr)) return base;
      return [...base, idStr];
    });
    setRepuestoDepQuery('');
    setRepuestoDepOpen(false);
  }, []);

  const quitarDepositoRepuesto = useCallback((id) => {
    const idStr = String(id);
    setRepuestoDepositosIds(prev => (prev || []).map(v => String(v)).filter(v => v !== idStr));
  }, []);

  const agregarEquivalente = useCallback((id) => {
    const idStr = String(id);
    setEquivalentesIds(prev => {
      const base = (prev || []).map(v => String(v));
      if (base.includes(idStr)) return base;
      return [...base, idStr];
    });
    setEquivalenteSearch('');
    setEquivalenteOpen(false);
  }, []);

  const quitarEquivalente = useCallback((id) => {
    const idStr = String(id);
    setEquivalentesIds(prev => (prev || []).map(v => String(v)).filter(v => v !== idStr));
  }, []);

  useEffect(() => {
    const seleccionados = (repuestoComponentesIds || []).map(v => String(v));
    if (!seleccionados.length) {
      if (repuestoComponenteId) setRepuestoComponenteId('');
      return;
    }
    if (!repuestoComponenteId || !seleccionados.includes(String(repuestoComponenteId))) {
      setRepuestoComponenteId(seleccionados[0]);
    }
  }, [repuestoComponentesIds, repuestoComponenteId]);

  useEffect(() => {
    const seleccionados = (repuestoDepositosIds || []).map(v => String(v));
    if (!seleccionados.length) {
      if (repuestoDepositoId) setRepuestoDepositoId('');
      return;
    }
    if (!repuestoDepositoId || !seleccionados.includes(String(repuestoDepositoId))) {
      setRepuestoDepositoId(seleccionados[0]);
    }
  }, [repuestoDepositosIds, repuestoDepositoId]);

  // If endpoint is 'tareas', load componentes, depósitos y sistemas for selects
  useEffect(() => {
    if (!isTareas) return;
    let cancelled = false;
    const loadData = async () => {
      try {
        const [compResp, sistList, depList] = await Promise.all([
          apiClient.get('componentes/'),
          fetchAllOptions('sistemas'),
          fetchAllOptions('depositos'),
        ]);
        if (!cancelled) {
          setComponentesOpts(compResp.data.results || compResp.data || []);
          setSistemasOpts(Array.isArray(sistList) ? sistList : []);
          setDepositosOpts(Array.isArray(depList) ? depList : []);
        }
      } catch (err) {
        console.warn('Error cargando datos para tareas', err);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [isTareas]);

  // Cargar sistemas y tareas para el endpoint catalogo-puntos-control
  useEffect(() => {
    if (!isCatalogoPuntoControl) return;
    let cancelled = false;
    const loadData = async () => {
      try {
        const [sistList, tareasList] = await Promise.all([
          fetchAllOptions('sistemas'),
          fetchAllOptions('tareas'),
        ]);
        if (!cancelled) {
          setSistemasOpts(Array.isArray(sistList) ? sistList : []);
          setTareasOpts(Array.isArray(tareasList) ? tareasList : []);
        }
      } catch (err) {
        console.warn('Error cargando datos para puntos de control', err);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [isCatalogoPuntoControl]);

  const handleFamiliaRepuestoCreadaDesdeFormulario = useCallback(async (savedRecord) => {
    try {
      const list = await fetchAllOptions('familias-repuesto');
      setFamiliasOpts(list);
      const nextId = savedRecord?.id != null ? String(savedRecord.id) : '';
      if (nextId) {
        if (isSubfamiliasRepuesto) {
          setSubfamiliaFamiliaId(nextId);
          setSubfamiliaFamiliaSearch(savedRecord?.nombre || '');
        } else {
          setFamiliaId(nextId);
          setFamiliaSearch(savedRecord?.nombre || '');
        }
        setFieldErrors(prev => ({ ...prev, familia: undefined }));
      }
    } catch (err) {
      console.error('Error refrescando familias de repuesto', err);
    }
  }, [isSubfamiliasRepuesto]);

  const handleSubfamiliaRepuestoCreadaDesdeFormulario = useCallback(async (savedRecord) => {
    try {
      let url = 'subfamilias-repuesto/';
      let list = [];
      let guard = 0;
      while (url && guard < 40) {
        const refresh = await apiClient.get(url);
        const payload = refresh.data;
        if (Array.isArray(payload)) {
          list = payload;
          url = null;
        } else if (Array.isArray(payload?.results)) {
          list = list.concat(payload.results);
          url = payload.next || null;
        } else {
          list = payload?.results || [];
          url = null;
        }
        guard += 1;
      }
      setSubfamiliasRepuestoOpts(Array.isArray(list) ? list : []);
      const nextId = savedRecord?.id != null ? String(savedRecord.id) : '';
      if (nextId) {
        setSubfamiliaRepuestoId(nextId);
        setSubfamiliaRepuestoSearch(savedRecord?.nombre || '');
        if (savedRecord?.familia != null) {
          setFamiliaId(String(savedRecord.familia));
        }
      }
    } catch (err) {
      console.error('Error refrescando subfamilias de repuesto', err);
    }
  }, []);

  const handleMarcaRepuestoCreadaDesdeFormulario = useCallback(async (savedRecord) => {
    try {
      const refresh = await apiClient.get('marcas-repuesto/');
      const list = refresh.data.results || refresh.data || [];
      setMarcasRepuestosOpts(list);
      const nextId = savedRecord?.id != null ? String(savedRecord.id) : '';
      if (nextId) {
        setRepuestoMarcaId(nextId);
        setMarcaRepuestoSearch(savedRecord?.nombre || '');
        setFieldErrors(prev => ({ ...prev, marca: undefined }));
      }
    } catch (err) {
      console.error('Error refrescando marcas de repuesto', err);
    }
  }, []);

  const handleUnidadMedidaCreadaDesdeFormulario = useCallback(async (savedRecord) => {
    try {
      const refresh = await apiClient.get('unidades-medida/');
      const list = refresh.data.results || refresh.data || [];
      setUnidadesMedidaOpts(list);
      const nextId = savedRecord?.id != null ? String(savedRecord.id) : '';
      if (nextId) {
        setRepuestoUnidadMedidaId(nextId);
        setUnidadMedidaSearch(savedRecord?.nombre || '');
        setFieldErrors(prev => ({ ...prev, unidad_medida: undefined }));
      }
    } catch (err) {
      console.error('Error refrescando unidades de medida', err);
    }
  }, []);

  // Filtrar datos
  const handleSort = useCallback((col) => {
    setSortColumn(prev => {
      if (prev === col) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return col;
      }
      setSortDirection('asc');
      return col;
    });
  }, []);

  const sortIcon = (col) => {
    if (sortColumn !== col) return ' ⇅';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const datosFiltrados = useMemo(() => {
    let base = datos;
    if (isModelos && filtroMarcaId) {
      base = base.filter(item => String(item.marca) === String(filtroMarcaId));
    }
    if (isSectoresTrabajo && filtroObraId) {
      base = base.filter(item => String(item.obra) === String(filtroObraId));
    }
    if ((isComponentes || isDepositos || isTareas || isCatalogoPuntoControl) && filtroSistemaId) {
      base = base.filter(item => String(item.sistema || '') === String(filtroSistemaId));
    }
    let filtered = base;
    // Con búsqueda server-side el filtro ya lo aplicó Postgres: volver a
    // filtrar acá descartaría filas que matchean por un campo que esta función
    // no mira (`search_fields` del ViewSet incluye, p.ej., marca__nombre).
    if (busqueda.trim() && !buscaEnServidor) {
      const q = busqueda.toLowerCase();
      filtered = base.filter(item =>
        item.nombre?.toLowerCase().includes(q) ||
        (isEmpresas && (
          item.razon_social?.toLowerCase().includes(q) ||
          item.cuit?.toLowerCase().includes(q) ||
          item.email?.toLowerCase().includes(q) ||
          item.telefono?.toLowerCase().includes(q)
        )) ||
        (isTareas && (
          item.sistema_nombre?.toLowerCase().includes(q) ||
          item.componente_nombre?.toLowerCase().includes(q) ||
          item.deposito_nombre?.toLowerCase().includes(q)
        )) ||
        ((isComponentes || isDepositos) && item.sistema_nombre?.toLowerCase().includes(q)) ||
        (isCatalogoPuntoControl && (
          item.sistema_nombre?.toLowerCase().includes(q) ||
          item.tarea_nombre?.toLowerCase().includes(q)
        )) ||
        (isObras && item.centro_costo_nombre?.toLowerCase().includes(q)) ||
        (isSubfamiliasRepuesto && item.familia_nombre?.toLowerCase().includes(q)) ||
        (isModelos && item.marca_nombre?.toLowerCase().includes(q)) ||
        (isRepuestos && (
          item.codigo?.toLowerCase().includes(q) ||
          item.descripcion?.toLowerCase().includes(q) ||
          item.componente_nombre?.toLowerCase().includes(q) ||
          (Array.isArray(item.componentes_nombres) && item.componentes_nombres.join(' ').toLowerCase().includes(q)) ||
          item.deposito_nombre?.toLowerCase().includes(q) ||
          (Array.isArray(item.depositos_nombres) && item.depositos_nombres.join(' ').toLowerCase().includes(q)) ||
          item.familia_nombre?.toLowerCase().includes(q) ||
          item.marca_nombre?.toLowerCase().includes(q) ||
          item.unidad_medida_nombre?.toLowerCase().includes(q)
        ))
      );
    }

    if (!sortColumn) return filtered;
    // La columna "Componente / Depósito" es excluyente (una tarea/repuesto tiene uno u
    // otro, nunca ambos): el valor mostrado en la celda sale de deposito_nombre O
    // componente_nombre según cuál esté cargado. Ordenar por a['componente_nombre']
    // directo dejaba afuera el valor real de las filas asignadas a un depósito
    // (quedaban todas con '' y se agrupaban al principio/final en vez de ordenarse
    // alfabéticamente junto con las de componente), así que replicamos acá el mismo
    // fallback que usa el render de la celda.
    const getSortValue = (item, col) => {
      if (col === 'componente_nombre') {
        if (isTareas) {
          return item.deposito_nombre
            || depositosOpts.find(d => String(d.id) === String(item.deposito))?.nombre
            || item.componente_nombre
            || componentesOpts.find(c => String(c.id) === String(item.componente))?.nombre
            || '';
        }
        if (isRepuestos) {
          if (item.tipo_asignacion === 'deposito') {
            return (Array.isArray(item.depositos_nombres) && item.depositos_nombres.length > 0)
              ? item.depositos_nombres.join(', ')
              : (item.deposito_nombre || depositosOpts.find(d => String(d.id) === String(item.deposito))?.nombre || '');
          }
          return (Array.isArray(item.componentes_nombres) && item.componentes_nombres.length > 0)
            ? item.componentes_nombres.join(', ')
            : (item.componente_nombre || componentesRepuestoOpts.find(c => String(c.id) === String(item.componente))?.nombre || '');
        }
      }
      return item[col];
    };
    return [...filtered].sort((a, b) => {
      const va = (getSortValue(a, sortColumn) ?? '').toString().toLowerCase();
      const vb = (getSortValue(b, sortColumn) ?? '').toString().toLowerCase();
      const num_a = parseFloat(getSortValue(a, sortColumn));
      const num_b = parseFloat(getSortValue(b, sortColumn));
      const numerico = !isNaN(num_a) && !isNaN(num_b);
      const cmp = numerico ? num_a - num_b : va.localeCompare(vb, 'es', { sensitivity: 'base' });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [datos, busqueda, buscaEnServidor, filtroMarcaId, filtroObraId, filtroSistemaId, isEmpresas, isComponentes, isDepositos, isObras, isSubfamiliasRepuesto, isModelos, isRepuestos, isSectoresTrabajo, isTareas, isCatalogoPuntoControl, sortColumn, sortDirection, depositosOpts, componentesOpts, componentesRepuestoOpts]);

  // Abrir modal para agregar
  const abrirModalAgregar = useCallback(() => {
    setFieldErrors({});
    setItemEditar(null);
    setNombre('');
    setAbreviaturaUnidadMedida('');
    setRazonSocial('');
    setCuit('');
    setTelefono('');
    setEmail('');
    setDireccion('');
    setLogoFile(null);
    setDescripcionItem('');
    setTiempoEstimado('');
    setRequiereRepuestos(false);
    setEsTanqueCombustible(false);
    setComponenteId('');
    setComponenteLabel('');
    setComponenteOpen(false);
    setTipoAsignacion('componente');
    setDepositoId('');
    setDepositoLabel('');
    setDepositoOpen(false);
    setMarcaId('');
    setCodigo('');
    setDescripcionRepuesto('');
    setStockRepuesto('');
    setSubfamiliaRepuestoId('');
    setSubfamiliaRepuestoSearch('');
    setSubfamiliaRepuestoOpen(false);
    setPuntoPedidoRepuesto('');
    setEquivalentesIds([]);
    setEquivalenteSearch('');
    setEquivalenteOpen(false);
    setFamiliaId('');
    setSubfamiliaFamiliaId('');
    setSubfamiliaFamiliaSearch('');
    setSubfamiliaFamiliaOpen(false);
    setRepuestoMarcaId('');
    setRepuestoUnidadMedidaId('');
    setRepuestoComponenteId('');
    setRepuestoComponentesIds([]);
    setRepuestoCompQuery('');
    setRepuestoCompOpen(false);
    setTipoAsignacionRepuesto('componente');
    setRepuestoDepositoId('');
    setRepuestoDepositosIds([]);
    setRepuestoDepQuery('');
    setRepuestoDepOpen(false);
    setUsoMantenimientoDiario(false);
    setNoUsarRepuesto(false);
    setFamiliaSearch('');
    setFamiliaOpen(false);
    setMarcaRepuestoSearch('');
    setMarcaRepuestoOpen(false);
    setUnidadMedidaSearch('');
    setUnidadMedidaOpen(false);
    setObraId('');
    setSistemaId('');
    setSistemaLabel('');
    setSistemaOpen(false);
    setObraSearch('');
    setObraOpen(false);
    setCentroCostoId('');
    setCentroCostoSearch('');
    setCentroCostoOpen(false);
    setSucursalId('');
    setSucursalSearch('');
    setTareaId('');
    setTareaLabel('');
    setTareaOpen(false);
    setTipoEstadoDefault('condicion');
    setMecanicoUsuarioId('');
    setMechanicoCostoHora('');
    setPrecioUnitarioRepuesto('');
    setMonedaRepuesto('ARS');
    setColorValue('');
    setMoneda('ARS');
    setTarifaCorta('');
    setTarifaLarga('');
    setDistanciaCorte('100');
    setTarifaUnificada(false);
    setTarifaUnica('');
    setShowModal(true);
    setAutoFocoTrigger((n) => n + 1);
  }, []);

  // Mantener el ref sincronizado con la función más reciente
  abrirModalAgregarRef.current = abrirModalAgregar;

  useEffect(() => {
    if (!(modalOnly || autoOpenAdd)) return;
    if (autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    abrirModalAgregar();
  }, [modalOnly, autoOpenAdd, abrirModalAgregar]);

  // Abrir modal para editar
  const abrirModalEditar = useCallback((item) => {
    setFieldErrors({});
    setItemEditar(item);
    setNombre(item.nombre || '');
    if (isEstadosReparacion) {
      setColorValue(item.color || '');
    }
    if (isCategoriasLogistica) {
      setMoneda(item.moneda || 'ARS');
      setTarifaCorta(item.tarifa_corta != null ? String(item.tarifa_corta) : '');
      setTarifaLarga(item.tarifa_larga != null ? String(item.tarifa_larga) : '');
      setDistanciaCorte(item.distancia_corte_km != null ? String(item.distancia_corte_km) : '100');
      setTarifaUnificada(!!item.tarifa_unificada);
      setTarifaUnica(item.tarifa_unica != null ? String(item.tarifa_unica) : '');
    }
    if (isMecanicos) {
      setMecanicoUsuarioId(item.usuario ? String(item.usuario) : '');
      setMechanicoCostoHora(item.costo_hora != null ? String(item.costo_hora) : '');
    }
    if (isEmpresas) {
      setRazonSocial(item.razon_social || '');
      setCuit(item.cuit || '');
      setTelefono(item.telefono || '');
      setEmail(item.email || '');
      setDireccion(item.direccion || '');
      setLogoFile(null);
    }
    if (isUnidadesMedida) {
      setAbreviaturaUnidadMedida(item.abreviatura || '');
    }
    // populate extra fields if tareas
    if (isTareas) {
      setDescripcionItem(item.descripcion || '');
      setTiempoEstimado(item.tiempo_estimado_minutos || '');
      const compIdEdit = item.componente?.id || item.componente || '';
      const depIdEdit = item.deposito?.id || item.deposito || '';
      // Datos previos a este flag pueden traer componente/depósito sin tener
      // requiere_repuestos en true — igual hay que mostrar el vínculo existente.
      setRequiereRepuestos(Boolean(item.requiere_repuestos) || Boolean(compIdEdit) || Boolean(depIdEdit));
      const resolvedSistemaId = resolveRelacionId(item, 'sistema', 'sistema_nombre', sistemasOpts);
      setSistemaId(resolvedSistemaId);
      const sistemaSeleccionado = sistemasOpts.find(s => String(s.id) === String(resolvedSistemaId));
      setSistemaLabel(sistemaSeleccionado?.nombre || item.sistema_nombre || '');
      setSistemaOpen(false);
      setComponenteId(compIdEdit);
      setDepositoId(depIdEdit);
      // Usar el tipo persistido en el backend, no inferirlo por el FK: una tarea puede
      // estar marcada como 'deposito' sin tener todavía un depósito puntual elegido
      // (p.ej. tras una edición masiva que fija el tipo para asignar el valor después).
      setTipoAsignacion(item.tipo_asignacion || (depIdEdit ? 'deposito' : 'componente'));
    }
    if (isComponentes || isDepositos) {
      setSistemaId(resolveRelacionId(item, 'sistema', 'sistema_nombre', sistemasOpts));
    }
    if (isDepositos) {
      setEsTanqueCombustible(Boolean(item.es_tanque_combustible));
    }
    if (isModelos) {
      setMarcaId(item.marca || '');
    }
    if (isSubfamiliasRepuesto) {
      const familiaSubfam = resolveRelacionId(item, 'familia', 'familia_nombre', familiasOpts);
      setSubfamiliaFamiliaId(familiaSubfam);
      const familiaSeleccionada = familiasOpts.find(f => String(f.id) === String(familiaSubfam));
      setSubfamiliaFamiliaSearch(familiaSeleccionada?.nombre || '');
      setSubfamiliaFamiliaOpen(false);
    }
    if (isSectoresTrabajo) {
      const resolvedObraId = resolveRelacionId(item, 'obra', 'obra_nombre', obrasOpts);
      setObraId(resolvedObraId);
      const obraSeleccionada = obrasOpts.find(o => String(o.id) === String(resolvedObraId));
      setObraSearch(obraSeleccionada?.nombre || '');
      setObraOpen(false);
    }
    if (isCatalogoPuntoControl) {
      const resolvedSistemaId = resolveRelacionId(item, 'sistema', 'sistema_nombre', sistemasOpts);
      setSistemaId(resolvedSistemaId);
      const sistSel = sistemasOpts.find(s => String(s.id) === String(resolvedSistemaId));
      setSistemaLabel(sistSel?.nombre || item.sistema_nombre || '');
      setSistemaOpen(false);
      setTipoEstadoDefault(item.tipo_estado_default || 'condicion');
      const resolvedTareaId = resolveRelacionId(item, 'tarea', 'tarea_nombre', tareasOpts);
      setTareaId(resolvedTareaId);
      const tareaSel = tareasOpts.find(t => String(t.id) === String(resolvedTareaId));
      setTareaLabel(tareaSel?.nombre || item.tarea_nombre || '');
      setTareaOpen(false);
    }
    if (isObras) {
      const resolvedCentroCostoId = resolveRelacionId(item, 'centro_costo', 'centro_costo_nombre', centrosCostoOpts);
      setCentroCostoId(resolvedCentroCostoId);
      const centroCostoSeleccionado = centrosCostoOpts.find(c => String(c.id) === String(resolvedCentroCostoId));
      setCentroCostoSearch(centroCostoSeleccionado?.nombre || '');
      setCentroCostoOpen(false);
      const resolvedSucursalId = resolveRelacionId(item, 'sucursal', 'sucursal_nombre', sucursalesOpts);
      setSucursalId(resolvedSucursalId);
      const sucursalSeleccionada = sucursalesOpts.find(s => String(s.id) === String(resolvedSucursalId));
      setSucursalSearch(sucursalSeleccionada?.nombre || '');
    }
    if (isRepuestos) {
      setCodigo(item.codigo || '');
      setDescripcionRepuesto(item.descripcion || '');
      setStockRepuesto(item.stock != null ? String(item.stock) : '');
      setSubfamiliaRepuestoId(resolveRelacionId(item, 'subfamilia', 'subfamilia_nombre', subfamiliasRepuestoOpts));
      setPuntoPedidoRepuesto(item.punto_de_pedido != null ? String(item.punto_de_pedido) : '');
      setPrecioUnitarioRepuesto(item.precio_unitario != null ? String(item.precio_unitario) : '');
    setMonedaRepuesto(item.moneda || 'ARS');
      setEquivalentesIds(Array.isArray(item.equivalentes) ? item.equivalentes.map(v => String(v)) : []);
      setEquivalenteSearch('');
      setEquivalenteOpen(false);
      setFamiliaId(resolveRelacionId(item, 'familia', 'familia_nombre', familiasOpts));
      setRepuestoMarcaId(resolveRelacionId(item, 'marca', 'marca_nombre', marcasRepuestosOpts));
      setRepuestoUnidadMedidaId(resolveRelacionId(item, 'unidad_medida', 'unidad_medida_nombre', unidadesMedidaOpts));
      const componentePrincipal = resolveRelacionId(item, 'componente', 'componente_nombre', componentesRepuestoOpts);
      const componentesMultiples = Array.isArray(item.componentes)
        ? item.componentes.map(c => String(c?.id ?? c)).filter(Boolean)
        : [];
      const componentesIniciales = componentesMultiples.length > 0
        ? componentesMultiples
        : (componentePrincipal ? [String(componentePrincipal)] : []);
      setRepuestoComponenteId(componentePrincipal || componentesIniciales[0] || '');
      setRepuestoComponentesIds(componentesIniciales);
      setRepuestoCompQuery('');
      setRepuestoCompOpen(false);
      const depositoPrincipal = resolveRelacionId(item, 'deposito', 'deposito_nombre', depositosOpts);
      const depositosMultiples = Array.isArray(item.depositos)
        ? item.depositos.map(d => String(d?.id ?? d)).filter(Boolean)
        : [];
      const depositosIniciales = depositosMultiples.length > 0
        ? depositosMultiples
        : (depositoPrincipal ? [String(depositoPrincipal)] : []);
      setRepuestoDepositoId(depositoPrincipal || depositosIniciales[0] || '');
      setRepuestoDepositosIds(depositosIniciales);
      setRepuestoDepQuery('');
      setRepuestoDepOpen(false);
      setTipoAsignacionRepuesto(item.tipo_asignacion || (depositosIniciales.length ? 'deposito' : 'componente'));
      setUsoMantenimientoDiario(Boolean(item.uso_mantenimiento_diario));
      setNoUsarRepuesto(Boolean(item.no_usar));
    }
    setShowModal(true);
    setAutoFocoTrigger((n) => n + 1);
  }, [
    isTareas,
    isEmpresas,
    isComponentes,
    isDepositos,
    isModelos,
    isSubfamiliasRepuesto,
    isUnidadesMedida,
    isObras,
    isSectoresTrabajo,
    isRepuestos,
    resolveRelacionId,
    obrasOpts,
    sistemasOpts,
    centrosCostoOpts,
    sucursalesOpts,
    familiasOpts,
    subfamiliasRepuestoOpts,
    marcasRepuestosOpts,
    unidadesMedidaOpts,
    componentesRepuestoOpts,
    depositosOpts,
    isCatalogoPuntoControl,
    isMecanicos,
    isEstadosReparacion,
    isCategoriasLogistica,
    tareasOpts,
  ]);

  const clonarRepuesto = useCallback((item) => {
    setFieldErrors({});
    setItemEditar(null);
    // Código y descripción se copian igual que el resto de los campos — código +
    // marca es lo único que no puede repetirse, y si el usuario guarda tal cual
    // el backend lo avisa (ver el catch de "guardar", isUniqueRepuestoError). No
    // tiene sentido borrarlos de entrada: casi siempre lo que se busca al
    // duplicar es cambiar un solo dígito del código (ver comentario en
    // duplicarItemFn).
    setCodigo(item.codigo || '');
    setDescripcionRepuesto(item.descripcion || '');
    setStockRepuesto('');
    setSubfamiliaRepuestoId(resolveRelacionId(item, 'subfamilia', 'subfamilia_nombre', subfamiliasRepuestoOpts));
    setSubfamiliaRepuestoSearch('');
    setSubfamiliaRepuestoOpen(false);
    setPuntoPedidoRepuesto(item.punto_de_pedido != null ? String(item.punto_de_pedido) : '');
    setPrecioUnitarioRepuesto(item.precio_unitario != null ? String(item.precio_unitario) : '');
    setMonedaRepuesto(item.moneda || 'ARS');
    setEquivalentesIds([]);
    setEquivalenteSearch('');
    setEquivalenteOpen(false);
    setFamiliaId(resolveRelacionId(item, 'familia', 'familia_nombre', familiasOpts));
    setFamiliaSearch('');
    setFamiliaOpen(false);
    setRepuestoMarcaId(resolveRelacionId(item, 'marca', 'marca_nombre', marcasRepuestosOpts));
    setMarcaRepuestoSearch('');
    setMarcaRepuestoOpen(false);
    setRepuestoUnidadMedidaId(resolveRelacionId(item, 'unidad_medida', 'unidad_medida_nombre', unidadesMedidaOpts));
    setUnidadMedidaSearch('');
    setUnidadMedidaOpen(false);
    const componentePrincipal = resolveRelacionId(item, 'componente', 'componente_nombre', componentesRepuestoOpts);
    const componentesMultiples = Array.isArray(item.componentes)
      ? item.componentes.map(c => String(c?.id ?? c)).filter(Boolean)
      : [];
    const componentesIniciales = componentesMultiples.length > 0
      ? componentesMultiples
      : (componentePrincipal ? [String(componentePrincipal)] : []);
    setRepuestoComponenteId(componentePrincipal || componentesIniciales[0] || '');
    setRepuestoComponentesIds(componentesIniciales);
    setRepuestoCompQuery('');
    setRepuestoCompOpen(false);
    const depositoPrincipal = resolveRelacionId(item, 'deposito', 'deposito_nombre', depositosOpts);
    const depositosMultiples = Array.isArray(item.depositos)
      ? item.depositos.map(d => String(d?.id ?? d)).filter(Boolean)
      : [];
    const depositosIniciales = depositosMultiples.length > 0
      ? depositosMultiples
      : (depositoPrincipal ? [String(depositoPrincipal)] : []);
    setRepuestoDepositoId(depositoPrincipal || depositosIniciales[0] || '');
    setRepuestoDepositosIds(depositosIniciales);
    setRepuestoDepQuery('');
    setRepuestoDepOpen(false);
    setTipoAsignacionRepuesto(item.tipo_asignacion || (depositosIniciales.length ? 'deposito' : 'componente'));
    setUsoMantenimientoDiario(Boolean(item.uso_mantenimiento_diario));
    setNoUsarRepuesto(false); // un clon es un repuesto nuevo: no hereda el "No usar"
    setShowModal(true);
  }, [
    resolveRelacionId,
    familiasOpts,
    subfamiliasRepuestoOpts,
    marcasRepuestosOpts,
    unidadesMedidaOpts,
    componentesRepuestoOpts,
    depositosOpts,
  ]);

  // Duplicar: copia todos los campos del item, incluido el nombre/código —
  // vaciarlo de entrada obligaba a retipear todo cuando en realidad el usuario
  // suele querer partir del valor existente y cambiarle sólo una letra o un
  // número (p. ej. "XR-200" → "XR-250"). Si guarda sin tocarlo, la validación
  // de duplicado (por nombre, o nombre+marca en Modelos) avisa igual.
  const duplicarItemFn = useCallback((item) => {
    if (isRepuestos) {
      clonarRepuesto(item);
      return;
    }
    setFieldErrors({});
    setItemEditar(null);
    setNombre(item.nombre || '');
    if (isEstadosReparacion) setColorValue(item.color || '');
    if (isCategoriasLogistica) {
      setMoneda(item.moneda || 'ARS');
      setTarifaCorta(item.tarifa_corta != null ? String(item.tarifa_corta) : '');
      setTarifaLarga(item.tarifa_larga != null ? String(item.tarifa_larga) : '');
      setDistanciaCorte(item.distancia_corte_km != null ? String(item.distancia_corte_km) : '100');
      setTarifaUnificada(!!item.tarifa_unificada);
      setTarifaUnica(item.tarifa_unica != null ? String(item.tarifa_unica) : '');
    }
    if (isMecanicos) {
      setMecanicoUsuarioId('');
      setMechanicoCostoHora(item.costo_hora != null ? String(item.costo_hora) : '');
    }
    if (isEmpresas) {
      setRazonSocial('');
      setCuit('');
      setTelefono(item.telefono || '');
      setEmail(item.email || '');
      setDireccion(item.direccion || '');
      setLogoFile(null);
    }
    if (isTareas) {
      setDescripcionItem(item.descripcion || '');
      setTiempoEstimado(item.tiempo_estimado_minutos || '');
      const compIdDup = item.componente?.id || item.componente || '';
      const depIdDup = item.deposito?.id || item.deposito || '';
      setRequiereRepuestos(Boolean(item.requiere_repuestos) || Boolean(compIdDup) || Boolean(depIdDup));
      const resolvedSistemaId = resolveRelacionId(item, 'sistema', 'sistema_nombre', sistemasOpts);
      setSistemaId(resolvedSistemaId);
      const sistemaSeleccionado = sistemasOpts.find(s => String(s.id) === String(resolvedSistemaId));
      setSistemaLabel(sistemaSeleccionado?.nombre || item.sistema_nombre || '');
      setSistemaOpen(false);
      setComponenteId(compIdDup);
      setDepositoId(depIdDup);
    }
    if (isComponentes || isDepositos) setSistemaId(resolveRelacionId(item, 'sistema', 'sistema_nombre', sistemasOpts));
    if (isModelos) setMarcaId(item.marca || '');
    if (isSubfamiliasRepuesto) {
      const familiaSubfam = resolveRelacionId(item, 'familia', 'familia_nombre', familiasOpts);
      setSubfamiliaFamiliaId(familiaSubfam);
      const familiaSeleccionada = familiasOpts.find(f => String(f.id) === String(familiaSubfam));
      setSubfamiliaFamiliaSearch(familiaSeleccionada?.nombre || '');
      setSubfamiliaFamiliaOpen(false);
    }
    if (isSectoresTrabajo) {
      const resolvedObraId = resolveRelacionId(item, 'obra', 'obra_nombre', obrasOpts);
      setObraId(resolvedObraId);
      const obraSeleccionada = obrasOpts.find(o => String(o.id) === String(resolvedObraId));
      setObraSearch(obraSeleccionada?.nombre || '');
      setObraOpen(false);
    }
    if (isCatalogoPuntoControl) {
      const resolvedSistemaId = resolveRelacionId(item, 'sistema', 'sistema_nombre', sistemasOpts);
      setSistemaId(resolvedSistemaId);
      const sistSel = sistemasOpts.find(s => String(s.id) === String(resolvedSistemaId));
      setSistemaLabel(sistSel?.nombre || item.sistema_nombre || '');
      setSistemaOpen(false);
      setTipoEstadoDefault(item.tipo_estado_default || 'condicion');
      const resolvedTareaId = resolveRelacionId(item, 'tarea', 'tarea_nombre', tareasOpts);
      setTareaId(resolvedTareaId);
      const tareaSel = tareasOpts.find(t => String(t.id) === String(resolvedTareaId));
      setTareaLabel(tareaSel?.nombre || item.tarea_nombre || '');
      setTareaOpen(false);
    }
    if (isObras) {
      const resolvedCentroCostoId = resolveRelacionId(item, 'centro_costo', 'centro_costo_nombre', centrosCostoOpts);
      setCentroCostoId(resolvedCentroCostoId);
      const centroCostoSeleccionado = centrosCostoOpts.find(c => String(c.id) === String(resolvedCentroCostoId));
      setCentroCostoSearch(centroCostoSeleccionado?.nombre || '');
      setCentroCostoOpen(false);
      const resolvedSucursalId = resolveRelacionId(item, 'sucursal', 'sucursal_nombre', sucursalesOpts);
      setSucursalId(resolvedSucursalId);
      const sucursalSeleccionada = sucursalesOpts.find(s => String(s.id) === String(resolvedSucursalId));
      setSucursalSearch(sucursalSeleccionada?.nombre || '');
    }
    setShowModal(true);
    setAutoFocoTrigger((n) => n + 1);
  }, [
    isRepuestos, clonarRepuesto, isEstadosReparacion, isCategoriasLogistica, isMecanicos,
    isEmpresas, isTareas, isComponentes, isDepositos, isModelos, isSubfamiliasRepuesto, isSectoresTrabajo,
    isCatalogoPuntoControl, isObras,
    resolveRelacionId, sistemasOpts, obrasOpts, centrosCostoOpts, sucursalesOpts, familiasOpts,
    tareasOpts,
  ]);

  // Mantener el ref sincronizado con la función más reciente
  duplicarItemFnRef.current = duplicarItemFn;

  useEffect(() => {
    if (!modalOnly) return;
    if (!autoOpenEditId) return;
    if (autoOpenedEditRef.current) return;
    autoOpenedEditRef.current = true;

    const openEdit = async () => {
      try {
        const response = await apiClient.get(`${apiEndpoint}/${autoOpenEditId}/`);
        const item = response?.data;
        if (item) abrirModalEditar(item);
      } catch (err) {
        setError('No se pudo abrir el formulario de edición solicitado.');
      }
    };

    openEdit();
  }, [modalOnly, autoOpenEditId, apiEndpoint, abrirModalEditar]);

  useEffect(() => {
    if (!showModal || !isRepuestos || !itemEditar) return;

    if (!familiaId) {
      const resolvedFamiliaId = resolveRelacionId(itemEditar, 'familia', 'familia_nombre', familiasOpts);
      if (resolvedFamiliaId) setFamiliaId(resolvedFamiliaId);
    }

    if (!repuestoMarcaId) {
      const resolvedMarcaId = resolveRelacionId(itemEditar, 'marca', 'marca_nombre', marcasRepuestosOpts);
      if (resolvedMarcaId) setRepuestoMarcaId(resolvedMarcaId);
    }

    if (!repuestoUnidadMedidaId) {
      const resolvedUnidadMedidaId = resolveRelacionId(itemEditar, 'unidad_medida', 'unidad_medida_nombre', unidadesMedidaOpts);
      if (resolvedUnidadMedidaId) setRepuestoUnidadMedidaId(resolvedUnidadMedidaId);
    }

    if (!repuestoComponenteId) {
      const resolvedComponenteId = resolveRelacionId(itemEditar, 'componente', 'componente_nombre', componentesRepuestoOpts);
      if (resolvedComponenteId) setRepuestoComponenteId(resolvedComponenteId);
    }

    if (!subfamiliaRepuestoId) {
      const resolvedSubfamiliaId = resolveRelacionId(itemEditar, 'subfamilia', 'subfamilia_nombre', subfamiliasRepuestoOpts);
      if (resolvedSubfamiliaId) setSubfamiliaRepuestoId(resolvedSubfamiliaId);
    }

    if (!repuestoComponentesIds.length) {
      const componentesMultiples = Array.isArray(itemEditar.componentes)
        ? itemEditar.componentes.map(c => String(c?.id ?? c)).filter(Boolean)
        : [];
      if (componentesMultiples.length) {
        setRepuestoComponentesIds(componentesMultiples);
      } else if (repuestoComponenteId) {
        setRepuestoComponentesIds([String(repuestoComponenteId)]);
      }
    }

    if (!repuestoDepositoId) {
      const resolvedDepositoId = resolveRelacionId(itemEditar, 'deposito', 'deposito_nombre', depositosOpts);
      if (resolvedDepositoId) setRepuestoDepositoId(resolvedDepositoId);
    }

    if (!repuestoDepositosIds.length) {
      const depositosMultiples = Array.isArray(itemEditar.depositos)
        ? itemEditar.depositos.map(d => String(d?.id ?? d)).filter(Boolean)
        : [];
      if (depositosMultiples.length) {
        setRepuestoDepositosIds(depositosMultiples);
      } else if (repuestoDepositoId) {
        setRepuestoDepositosIds([String(repuestoDepositoId)]);
      }
    }
  }, [
    showModal,
    isRepuestos,
    itemEditar,
    familiaId,
    repuestoMarcaId,
    repuestoUnidadMedidaId,
    repuestoComponenteId,
    subfamiliaRepuestoId,
    repuestoComponentesIds,
    repuestoDepositoId,
    repuestoDepositosIds,
    familiasOpts,
    subfamiliasRepuestoOpts,
    marcasRepuestosOpts,
    unidadesMedidaOpts,
    componentesRepuestoOpts,
    depositosOpts,
    resolveRelacionId,
  ]);

  // Guardar (agregar o editar)
  const guardar = useCallback(async () => {
    setFieldErrors({});
    if (!isRepuestos && !nombre.trim()) {
      setError('El nombre es requerido');
      setFieldErrors({ nombre: 'El nombre es requerido' });
      return;
    }

    if (isComponentes) {
      const existeComp = datos.some(c => (
        String(c.nombre || '').trim().toLowerCase() === nombre.trim().toLowerCase()
        && (!itemEditar || String(c.id) !== String(itemEditar.id))
      ));
      if (existeComp) {
        const msg = 'Ya existe un componente con ese nombre';
        setError(msg);
        setFieldErrors({ nombre: msg });
        return;
      }
    }

    if ((isComponentes || isDepositos || isTareas) && !sistemaId) {
      const msg = 'El sistema es requerido';
      setError(msg);
      setFieldErrors({ sistema: msg });
      return;
    }

    if (isSistemas) {
      const existeSistema = datos.some(s => (
        String(s.nombre || '').trim().toLowerCase() === nombre.trim().toLowerCase()
        && (!itemEditar || String(s.id) !== String(itemEditar.id))
      ));
      if (existeSistema) {
        const msg = 'Ya existe un sistema con ese nombre';
        setError(msg);
        setFieldErrors({ nombre: msg });
        return;
      }
    }

    // Validación genérica de duplicado por nombre para todos los endpoints simples
    if (!isComponentes && !isSistemas && !isModelos && !isRepuestos && !isTareas && !isObras && !isSectoresTrabajo && !isSubfamiliasRepuesto && !isEmpresas && !isCatalogoPuntoControl && nombre.trim()) {
      const existeGenerico = datos.some(item => (
        String(item.nombre || '').trim().toLowerCase() === nombre.trim().toLowerCase()
        && (!itemEditar || String(item.id) !== String(itemEditar.id))
      ));
      if (existeGenerico) {
        const msg = `Ya existe un registro con ese nombre`;
        setError(msg);
        setFieldErrors({ nombre: msg });
        return;
      }
    }

    if (isCategoriasLogistica) {
      const nextErrors = {};
      if (tarifaUnificada) {
        if (!tarifaUnica || isNaN(tarifaUnica)) nextErrors.tarifa_unica = 'Ingrese un valor numérico';
      } else {
        if (!tarifaCorta || isNaN(tarifaCorta)) nextErrors.tarifa_corta = 'Ingrese un valor numérico';
        if (!tarifaLarga || isNaN(tarifaLarga)) nextErrors.tarifa_larga = 'Ingrese un valor numérico';
        if (!distanciaCorte || isNaN(distanciaCorte) || Number(distanciaCorte) <= 0) nextErrors.distancia_corte_km = 'Ingrese una distancia válida';
      }
      if (Object.keys(nextErrors).length > 0) {
        setFieldErrors(nextErrors);
        setError('Complete los campos obligatorios');
        return;
      }
    }

    if (isEmpresas) {
      const nextErrors = {};
      if (!cuit.trim()) nextErrors.cuit = 'El CUIT es requerido';
      if (!telefono.trim()) nextErrors.telefono = 'El teléfono es requerido';
      if (!direccion.trim()) nextErrors.direccion = 'La dirección es requerida';
      if (Object.keys(nextErrors).length > 0) {
        setFieldErrors(nextErrors);
        setError('Complete los campos obligatorios');
        return;
      }
    }

    if (isRepuestos) {
      const nextErrors = {};
      if (!codigo.trim()) nextErrors.codigo = 'El código es requerido';
      if (!descripcionRepuesto.trim()) nextErrors.descripcion = 'La descripción es requerida';
      if (!familiaId) nextErrors.familia = 'La familia es requerida';
      if (!repuestoMarcaId) nextErrors.marca = 'La marca es requerida';
      if (!repuestoUnidadMedidaId) nextErrors.unidad_medida = 'La unidad de medida es requerida';
      if (Object.keys(nextErrors).length > 0) {
        setFieldErrors(nextErrors);
        setError('Complete los campos obligatorios');
        return;
      }
    }

    if (isSubfamiliasRepuesto && !subfamiliaFamiliaId) {
      setFieldErrors({ familia: 'La familia es requerida' });
      setError('Seleccione la familia de la subfamilia');
      return;
    }

    if (isObras && !sucursalId) {
      setFieldErrors({ sucursal: 'La sucursal es requerida' });
      setError('Seleccione la sucursal de la obra');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build payload depending on endpoint
      let data = { nombre: nombre.trim() };
      if (isEstadosReparacion) {
        data = { nombre: nombre.trim(), color: colorValue || null };
      }
      if (isCategoriasLogistica) {
        data = {
          nombre: nombre.trim(),
          moneda: moneda || 'ARS',
          tarifa_unificada: tarifaUnificada,
          tarifa_corta: tarifaCorta !== '' && !isNaN(tarifaCorta) ? parseFloat(tarifaCorta) : null,
          tarifa_larga: tarifaLarga !== '' && !isNaN(tarifaLarga) ? parseFloat(tarifaLarga) : null,
          distancia_corte_km: distanciaCorte !== '' && !isNaN(distanciaCorte) ? parseFloat(distanciaCorte) : 100,
          tarifa_unica: tarifaUnica !== '' && !isNaN(tarifaUnica) ? parseFloat(tarifaUnica) : null,
        };
      }
      if (isMecanicos) {
        data = {
          nombre: nombre.trim(),
          costo_hora: mechanicoCostoHora !== '' && !isNaN(mechanicoCostoHora) ? parseFloat(mechanicoCostoHora) : null,
          usuario: mecanicoUsuarioId ? Number(mecanicoUsuarioId) : null,
        };
      }
      if (isTareas) {
        // La tarea apunta a un componente O a un depósito según el tipo elegido; el otro va null.
        const esDeposito = tipoAsignacion === 'deposito';
        data = {
          nombre: nombre.trim(),
          sistema: sistemaId || null,
          tipo_asignacion: tipoAsignacion,
          componente: esDeposito ? null : (componenteId || null),
          deposito: esDeposito ? (depositoId || null) : null,
          descripcion: descripcionItem || '',
          tiempo_estimado_minutos: tiempoEstimado || null,
          requiere_repuestos: !!requiereRepuestos,
        };
      }
      if (isModelos) {
        if (!marcaId) {
          setError('La marca es requerida');
          setLoading(false);
          return;
        }
        // Validar duplicado (marca + nombre) antes de enviar
        const existe = datos.some(m => String(m.marca) === String(marcaId) && m.nombre?.trim().toLowerCase() === nombre.trim().toLowerCase() && (!itemEditar || m.id !== itemEditar.id));
        if (existe) {
          setError('Ya existe un modelo con ese nombre para esta marca');
          setLoading(false);
          return;
        }
        data = { nombre: nombre.trim(), marca: marcaId };
      }
      if (isComponentes || isDepositos) {
        data = { nombre: nombre.trim(), sistema: sistemaId || null };
        if (isDepositos) {
          data.es_tanque_combustible = !!esTanqueCombustible;
        }
      }
      if (isObras) {
        data = { nombre: nombre.trim(), sucursal: sucursalId || null, centro_costo: centroCostoId || null };
      }
      if (isRepuestos) {
        // Un repuesto es, por naturaleza, una pieza (componente) o un fluido
        // (depósito), nunca ambos — mismo criterio que Tareas.
        const esDepositoRepuesto = tipoAsignacionRepuesto === 'deposito';
        data = {
          codigo: codigo.trim(),
          descripcion: descripcionRepuesto || null,
          ubicacion: null,
          stock: stockRepuesto === '' ? null : Number(stockRepuesto),
          subfamilia: subfamiliaRepuestoId || null,
          punto_de_pedido: puntoPedidoRepuesto === '' ? 0 : Number(puntoPedidoRepuesto),
          precio_unitario: precioUnitarioRepuesto !== '' && !isNaN(precioUnitarioRepuesto) ? parseFloat(precioUnitarioRepuesto) : null,
          moneda: monedaRepuesto || 'ARS',
          familia: familiaId || null,
          marca: repuestoMarcaId || null,
          unidad_medida: repuestoUnidadMedidaId || null,
          tipo_asignacion: tipoAsignacionRepuesto,
          componente: esDepositoRepuesto ? null : ((repuestoComponentesIds[0] || repuestoComponenteId) || null),
          componentes: esDepositoRepuesto ? [] : (repuestoComponentesIds.length ? repuestoComponentesIds : (repuestoComponenteId ? [repuestoComponenteId] : [])).map(v => Number(v)),
          deposito: esDepositoRepuesto ? ((repuestoDepositosIds[0] || repuestoDepositoId) || null) : null,
          depositos: esDepositoRepuesto ? (repuestoDepositosIds.length ? repuestoDepositosIds : (repuestoDepositoId ? [repuestoDepositoId] : [])).map(v => Number(v)) : [],
          uso_mantenimiento_diario: !!usoMantenimientoDiario,
          no_usar: !!noUsarRepuesto,
          equivalentes: (equivalentesIds || []).map(v => Number(v)),
        };
      }
      if (isSubfamiliasRepuesto) {
        data = { nombre: nombre.trim(), familia: Number(subfamiliaFamiliaId) };
      }
      if (isSectoresTrabajo) {
        if (!obraId) {
          setError('La obra es requerida');
          setLoading(false);
          return;
        }
        data = { nombre: nombre.trim(), obra: obraId };
      }
      if (isCatalogoPuntoControl) {
        data = {
          nombre: nombre.trim(),
          sistema: sistemaId || null,
          tipo_estado_default: tipoEstadoDefault || 'condicion',
          tarea: tareaId || null,
        };
      }
      if (isUnidadesMedida) {
        data = { nombre: nombre.trim(), abreviatura: abreviaturaUnidadMedida.trim() || null };
      }
      if (isEmpresas) {
        const formData = new FormData();
        formData.append('nombre', nombre.trim());
        formData.append('razon_social', razonSocial || '');
        formData.append('cuit', cuit || '');
        formData.append('telefono', telefono || '');
        formData.append('email', email || '');
        formData.append('direccion', direccion || '');
        if (logoFile) formData.append('logo', logoFile);
        data = formData;
      }

      let resp;
      if (itemEditar) {
        if (isEmpresas) {
          resp = await apiClient.patch(`${apiEndpoint}/${itemEditar.id}/`, data, {
            headers: { 'Content-Type': undefined },
          });
        } else {
          resp = await apiClient.put(`${apiEndpoint}/${itemEditar.id}/`, data);
        }
      } else {
        if (isEmpresas) {
          resp = await apiClient.post(`${apiEndpoint}/`, data, {
            headers: { 'Content-Type': undefined },
          });
        } else {
          resp = await apiClient.post(`${apiEndpoint}/`, data);
        }
      }
      if (typeof onRecordSaved === 'function') {
        try {
          await onRecordSaved(resp?.data, apiEndpoint);
        } catch (cbErr) {
          console.warn('onRecordSaved callback error', cbErr);
        }
      }
      // Emitir eventos globales para que vistas embebidas (p.ej., RutinaMantenimiento) refresquen selects
      try {
        const payload = { data: resp?.data, endpoint: apiEndpoint, isEditing: Boolean(itemEditar) };
        window.dispatchEvent(new CustomEvent('gestion-taller:record-saved', { detail: payload }));
        window.dispatchEvent(new CustomEvent('gestion-taller:record-saved:confirmed', { detail: payload }));
      } catch (evtErr) {
        /* noop */
      }

      const accionPost = postGuardadoRef.current;
      postGuardadoRef.current = 'cerrar';

      // Refrescar lista antes de navegar para tener datos actualizados
      await cargarDatos();

      if (accionPost === 'anterior' || accionPost === 'siguiente') {
        const targetItem = navItemTargetRef.current;
        navItemTargetRef.current = null;
        setItemEditar(null);
        if (targetItem) {
          // Buscamos la versión actualizada del target en los datos recién cargados
          setDatos(prevDatos => {
            const fresh = prevDatos.find(it => String(it.id) === String(targetItem.id));
            setTimeout(() => abrirModalEditar(fresh || targetItem), 0);
            return prevDatos;
          });
        }
        return;
      }

      if (accionPost === 'duplicar') {
        const savedItem = resp?.data;
        setItemEditar(null);
        if (savedItem && duplicarItemFnRef.current) {
          setTimeout(() => duplicarItemFnRef.current(savedItem), 0);
        }
        return;
      }

      if (accionPost === 'nuevo') {
        setItemEditar(null);
        if (abrirModalAgregarRef.current) {
          setTimeout(() => abrirModalAgregarRef.current(), 0);
        }
        return;
      }

      setShowModal(false);
      setNombre('');
      setAbreviaturaUnidadMedida('');
      setColorValue('');
      setRazonSocial('');
      setCuit('');
      setTelefono('');
      setEmail('');
      setDireccion('');
      setLogoFile(null);
      setCodigo('');
      setDescripcionRepuesto('');
      setStockRepuesto('');
      setSubfamiliaRepuestoId('');
      setSubfamiliaRepuestoSearch('');
      setSubfamiliaRepuestoOpen(false);
      setPuntoPedidoRepuesto('');
      setEquivalentesIds([]);
      setEquivalenteSearch('');
      setEquivalenteOpen(false);
      setFamiliaId('');
      setSubfamiliaFamiliaId('');
      setSubfamiliaFamiliaSearch('');
      setSubfamiliaFamiliaOpen(false);
      setRepuestoMarcaId('');
      setMarcaRepuestoSearch('');
      setRepuestoUnidadMedidaId('');
      setUnidadMedidaSearch('');
      setRepuestoComponenteId('');
      setRepuestoComponentesIds([]);
      setRepuestoCompQuery('');
      setRepuestoCompOpen(false);
      setObraId('');
      setSistemaId('');
      setObraSearch('');
      setCentroCostoId('');
      setCentroCostoSearch('');
      setCentroCostoOpen(false);
      setSucursalId('');
      setSucursalSearch('');
      setMoneda('ARS');
      setTarifaCorta('');
      setTarifaLarga('');
      setDistanciaCorte('100');
      setTarifaUnificada(false);
      setTarifaUnica('');
      setMecanicoUsuarioId('');
      setMechanicoCostoHora('');
      setPrecioUnitarioRepuesto('');
      setMonedaRepuesto('ARS');
      setItemEditar(null);
      if (modalOnly && typeof onRequestClose === 'function') {
        onRequestClose();
      }
    } catch (err) {
      const errData = err.response?.data;
      let msg = 'Error al guardar';
      if (errData) {
        if (typeof errData === 'string') {
          msg = errData;
        } else {
          const nonFieldErrors = Array.isArray(errData.non_field_errors) ? errData.non_field_errors : [];
          const isUniqueRepuestoError = isRepuestos && nonFieldErrors.some(
            e => typeof e === 'string' && e.includes('codigo') && e.includes('marca')
          );
          const isUsuarioDuplicadoError = isMecanicos && errData.usuario && (
            Array.isArray(errData.usuario)
              ? errData.usuario.some(e => typeof e === 'string' && (e.includes('already exists') || e.includes('unique') || e.includes('ya existe')))
              : typeof errData.usuario === 'string' && (errData.usuario.includes('already exists') || errData.usuario.includes('unique'))
          );
          const isUniquePuntoControlError = isCatalogoPuntoControl && nonFieldErrors.some(
            e => typeof e === 'string' && (e.includes('nombre') || e.includes('sistema') || e.includes('único') || e.includes('unique'))
          );
          if (isUniqueRepuestoError) {
            const marcaNombre = marcasRepuestosOpts.find(m => String(m.id) === String(repuestoMarcaId))?.nombre;
            msg = `Ya existe el repuesto ${codigo.trim()}${marcaNombre ? ` (${marcaNombre})` : ''}`;
          } else if (isUniquePuntoControlError) {
            const sistemaNombre = sistemasOpts.find(s => String(s.id) === String(sistemaId))?.nombre;
            msg = `Ya existe "${nombre.trim()}" en el sistema${sistemaNombre ? ` "${sistemaNombre}"` : ' seleccionado'}. Podés usar el mismo punto de control en distintos sistemas, pero no repetir la combinación.`;
          } else if (isUsuarioDuplicadoError) {
            msg = 'Este usuario ya está vinculado a otro mecánico. Primero quitá el vínculo desde ese mecánico.';
          } else {
            const firstValue = Object.values(errData).flat().find(v => typeof v === 'string');
            if (firstValue) msg = firstValue;
          }
        }
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [
    apiEndpoint,
    itemEditar,
    nombre,
    cargarDatos,
    mecanicoUsuarioId,
    mechanicoCostoHora,
    precioUnitarioRepuesto,
    monedaRepuesto,
    descripcionItem,
    tiempoEstimado,
    requiereRepuestos,
    esTanqueCombustible,
    componenteId,
    tipoAsignacion,
    depositoId,
    marcaId,
    datos,
    isTareas,
    isComponentes,
    isDepositos,
    isSistemas,
    isModelos,
    isObras,
    isSectoresTrabajo,
    isSubfamiliasRepuesto,
    isUnidadesMedida,
    isEmpresas,
    isRepuestos,
    codigo,
    razonSocial,
    cuit,
    telefono,
    email,
    direccion,
    logoFile,
    descripcionRepuesto,
    stockRepuesto,
    subfamiliaRepuestoId,
    puntoPedidoRepuesto,
    equivalentesIds,
    familiaId,
    subfamiliaFamiliaId,
    repuestoMarcaId,
    repuestoUnidadMedidaId,
    abreviaturaUnidadMedida,
    marcasRepuestosOpts,
    sistemasOpts,
    repuestoComponenteId,
    repuestoComponentesIds,
    tipoAsignacionRepuesto,
    repuestoDepositoId,
    repuestoDepositosIds,
    usoMantenimientoDiario,
    noUsarRepuesto,
    obraId,
    sistemaId,
    centroCostoId,
    sucursalId,
    modalOnly,
    onRecordSaved,
    onRequestClose,
    isCatalogoPuntoControl,
    isMecanicos,
    isEstadosReparacion,
    isCategoriasLogistica,
    colorValue,
    tareaId,
    tipoEstadoDefault,
    moneda,
    tarifaCorta,
    tarifaLarga,
    distanciaCorte,
    tarifaUnificada,
    tarifaUnica,
    abrirModalEditar,
  ]);

  const agregarCentroCostoRapido = useCallback(() => {
    setShowSubCentroCosto(true);
  }, []);

  const handleCentroCostoCreadoDesdeFormulario = useCallback(async (savedRecord) => {
    try {
      const list = await fetchAllOptions('centroscosto');
      setCentrosCostoOpts(Array.isArray(list) ? list : []);
      if (savedRecord?.id != null) {
        setCentroCostoId(String(savedRecord.id));
        setCentroCostoSearch(savedRecord.nombre || '');
        setCentroCostoOpen(false);
      }
    } catch (err) {
      console.error('Error refrescando centros de costo luego de alta', err);
    }
    setShowSubCentroCosto(false);
  }, []);

  // Edición masiva de campos FK, choice y numéricos
  const guardarMasivo = useCallback(async () => {
    const patch = {};
    bulkFkFields.forEach(({ key }) => {
      if (bulkFieldValues[key] !== undefined && bulkFieldValues[key] !== '') {
        patch[key] = bulkFieldValues[key];
      }
    });
    bulkChoiceFields.forEach(({ key }) => {
      if (bulkFieldValues[key] !== undefined && bulkFieldValues[key] !== '') {
        patch[key] = BULK_CHOICE_BOOLEAN_FIELDS.has(key)
          ? bulkFieldValues[key] === 'true'
          : bulkFieldValues[key];
      }
    });
    bulkNumberFields.forEach(({ key }) => {
      if (bulkFieldValues[key] !== undefined && bulkFieldValues[key] !== '') {
        patch[key] = Number(bulkFieldValues[key]);
      }
    });
    bulkTextFields.forEach(({ key }) => {
      if (bulkFieldValues[key] !== undefined && bulkFieldValues[key] !== '') {
        patch[key] = bulkFieldValues[key];
      }
    });
    // Tareas: asignación de componente/depósito (excluyentes) según el toggle.
    // El valor concreto vive en la clave '__asignacion'. Si el usuario tocó el toggle pero
    // todavía no eligió el valor puntual, igual se fija el tipo (limpiando el FK opuesto)
    // para poder elegir el componente/depósito de cada tarea individualmente después.
    if (isTareas && bulkAsignacionTocada) {
      const valor = bulkFieldValues.__asignacion;
      const esDeposito = bulkTipoAsignacion === 'deposito';
      const tieneValor = valor !== undefined && valor !== '';
      patch.tipo_asignacion = esDeposito ? 'deposito' : 'componente';
      if (esDeposito) {
        patch.deposito = tieneValor ? valor : null;
        patch.componente = null;
      } else {
        patch.componente = tieneValor ? valor : null;
        patch.deposito = null;
      }
    }
    // Repuestos: mismo patrón, pero el modelo usa M2M (componentes/depositos) — el
    // valor elegido reemplaza el set completo de cada repuesto seleccionado.
    if (isRepuestos && bulkAsignacionRepuestoTocada) {
      const valor = bulkFieldValues.__asignacionRepuesto;
      const esDeposito = bulkTipoAsignacionRepuesto === 'deposito';
      const tieneValor = valor !== undefined && valor !== '';
      patch.tipo_asignacion = esDeposito ? 'deposito' : 'componente';
      if (esDeposito) {
        patch.depositos = tieneValor ? [valor] : [];
        patch.componentes = [];
      } else {
        patch.componentes = tieneValor ? [valor] : [];
        patch.depositos = [];
      }
    }
    if (Object.keys(patch).length === 0) return;

    setBulkSaving(true);
    try {
      const ids = Array.from(selectedIds);
      const itemsSeleccionados = datos.filter(d => ids.includes(d.id));
      await Promise.all(
        itemsSeleccionados.map(item => apiClient.patch(`${apiEndpoint}/${item.id}/`, patch))
      );
      await cargarDatos();
      setBulkMode(false);
      setSelectedIds(new Set());
      setShowBulkModal(false);
      setBulkFieldValues({});
    } catch (e) {
      setError('Error al guardar los cambios masivos. Revisá tu conexión e intentá de nuevo.');
    } finally {
      setBulkSaving(false);
    }
  }, [bulkFkFields, bulkChoiceFields, bulkNumberFields, bulkTextFields, bulkFieldValues, isTareas, bulkTipoAsignacion, bulkAsignacionTocada, isRepuestos, bulkTipoAsignacionRepuesto, bulkAsignacionRepuestoTocada, selectedIds, datos, apiEndpoint, cargarDatos]);

  // Confirmar eliminación
  const confirmarEliminar = useCallback((item) => {
    setItemEliminar(item);
    setBloqueoEliminar(null);
    setShowConfirm(true);
  }, []);

  // Eliminar. `desvincular` reintenta quitando los vínculos que lo bloquean:
  // el backend solo lo permite si TODOS son quitables (clasificaciones dentro de
  // definiciones de configuración), nunca sobre registros históricos.
  const eliminar = useCallback(async (desvincular = false) => {
    if (!itemEliminar) return;

    setLoading(true);
    try {
      const url = `${apiEndpoint}/${itemEliminar.id}/${desvincular ? '?desvincular=1' : ''}`;
      await apiClient.delete(url);
      // Emitir evento global para que contenedores refresquen selects si corresponde
      try {
        const payload = { data: { id: itemEliminar.id }, endpoint: apiEndpoint };
        window.dispatchEvent(new CustomEvent('gestion-taller:record-deleted', { detail: payload }));
      } catch {}
      setShowConfirm(false);
      setItemEliminar(null);
      setBloqueoEliminar(null);
      cargarDatos();
    } catch (err) {
      const raw = err.response?.data;
      // El registro está en uso: en vez de un error seco, mostramos qué lo usa y
      // —si se puede— ofrecemos quitar esos vínculos y eliminar igual.
      if (raw?.bloqueo && !desvincular) {
        setBloqueoEliminar(raw.bloqueo);
        setLoading(false);
        return;
      }
      let msg = 'No se pudo eliminar. Revisá si hay registros vinculados.';
      if (raw?.detail) {
        msg = raw.detail;
      } else if (raw) {
        const first = typeof raw === 'string'
          ? raw
          : Object.values(raw).flat().find(v => typeof v === 'string');
        if (first) msg = first;
      }
      setShowConfirm(false);
      setBloqueoEliminar(null);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, itemEliminar, cargarDatos]);

  // Borrado masivo (selección múltiple)
  const confirmarEliminarMasivo = useCallback(() => {
    setBulkDeleteResult(null);
    setShowConfirmBulkDelete(true);
  }, []);

  const cerrarConfirmBulkDelete = useCallback(() => {
    setShowConfirmBulkDelete(false);
    setBulkDeleteResult(null);
  }, []);

  // `desvincular` reintenta los bloqueados quitándoles los vínculos que se pueden
  // quitar. El backend valida registro por registro: los que estén usados por
  // registros de operación siguen sin borrarse y vuelven en `bloqueados`.
  const ejecutarEliminacionMasiva = useCallback(async (desvincular = false) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setBulkDeleting(true);
    try {
      const resp = await apiClient.post(`${apiEndpoint}/bulk-delete/`, { ids, desvincular });
      const { eliminados = 0, bloqueados = [] } = resp.data || {};

      if (eliminados > 0) {
        const idsBloqueados = new Set(bloqueados.map(b => b.id));
        ids.filter(id => !idsBloqueados.has(id)).forEach(id => {
          try {
            const payload = { data: { id }, endpoint: apiEndpoint };
            window.dispatchEvent(new CustomEvent('gestion-taller:record-deleted', { detail: payload }));
          } catch {}
        });
        await cargarDatos();
      }

      if (bloqueados.length > 0) {
        setBulkDeleteResult({ bloqueados });
        setSelectedIds(new Set(bloqueados.map(b => b.id)));
      } else {
        setShowConfirmBulkDelete(false);
        setBulkDeleteResult(null);
        setBulkMode(false);
        setSelectedIds(new Set());
      }
    } catch (err) {
      let msg = 'No se pudo completar el borrado. Revisá tu conexión e intentá de nuevo.';
      const raw = err.response?.data;
      if (raw?.detail) {
        msg = raw.detail;
      } else if (raw) {
        const first = typeof raw === 'string'
          ? raw
          : Object.values(raw).flat().find(v => typeof v === 'string');
        if (first) msg = first;
      }
      setShowConfirmBulkDelete(false);
      setError(msg);
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedIds, apiEndpoint, cargarDatos]);

  // Cerrar modales
  const cerrarModal = useCallback(() => {
    setShowModal(false);
    setShowConfirm(false);
    setItemEditar(null);
    setItemEliminar(null);
    setBloqueoEliminar(null);
    setNombre('');
    setRazonSocial('');
    setCuit('');
    setTelefono('');
    setEmail('');
    setDireccion('');
    setLogoFile(null);
    setError('');
    setFieldErrors({});
    setSistemaId('');
    setCentroCostoId('');
    setCentroCostoSearch('');
    setCentroCostoOpen(false);
    setSucursalId('');
    setSucursalSearch('');
    setObraOpen(false);
    setSubfamiliaFamiliaId('');
    setSubfamiliaFamiliaSearch('');
    setSubfamiliaFamiliaOpen(false);
    setTareaId('');
    setTareaLabel('');
    setTareaOpen(false);
    setTipoEstadoDefault('condicion');
    setMoneda('ARS');
    setTarifaCorta('');
    setTarifaLarga('');
    setDistanciaCorte('100');
    setTarifaUnificada(false);
    setTarifaUnica('');
    setMecanicoUsuarioId('');
    setMechanicoCostoHora('');
    setPrecioUnitarioRepuesto('');
    setMonedaRepuesto('ARS');
    if (modalOnly && typeof onRequestClose === 'function') {
      onRequestClose();
    }
  }, [modalOnly, onRequestClose]);

  // Al abrir el modal, el cursor va al primer campo editable: se puede empezar a
  // escribir sin buscarlo primero. Varios campos ya traían autoFocus propio; el
  // hook no los pisa (si algo del modal ya tiene el foco, no hace nada).
  const modalRef = useRef(null);
  useAutoFocoPrimerCampo(modalRef, showModal, autoFocoTrigger);

  // Escape cierra ESTE modal y no deja que la tecla siga viaje.
  //
  // Este modal se dibuja con un styled propio, no con el componente Modal
  // compartido, así que no participa de su pila. Sin este handler, al abrir el
  // alta rápida de una FK desde otro formulario (Alt+N o el botón "+"), el
  // Escape burbujeaba hasta el Modal de abajo y cerraba LOS DOS: se perdía todo
  // lo cargado en el formulario original.
  //
  // Va en fase de captura (`true`) para llegar antes que el listener del Modal
  // de abajo, que está registrado en `document`.
  useEffect(() => {
    if (!showModal) return undefined;
    const alPresionarEscape = (evento) => {
      if (evento.key !== 'Escape') return;
      evento.stopPropagation();
      cerrarModal();
    };
    document.addEventListener('keydown', alPresionarEscape, true);
    return () => document.removeEventListener('keydown', alPresionarEscape, true);
  }, [showModal, cerrarModal]);

  const modalBaseTitle = useMemo(() => {
    const raw = String(titulo || '').trim();
    if (!raw) return '';
    const clean = raw.replace(/^(agregar|editar|nuevo)\s+/i, '').trim();
    if (clean.length > 1 && /s$/i.test(clean)) {
      return clean.slice(0, -1);
    }
    return clean;
  }, [titulo]);

  const Inner = (
    <div style={modalOnly ? {display:'contents'} : {display:'flex', flexDirection:'column', flex:1, minHeight:0}}>
      {!modalOnly && (
      <Header $embedded={embedded} $compact={compact}>
        <div>
          <Title $embedded={embedded} $compact={compact}>
            <i className={icon}></i>
            {titulo}
            {totalRegistros > 0 && (
              <span style={{
                fontSize: compact ? (embedded ? '0.55rem':'0.6rem') : (embedded ? '0.65rem':'0.7rem'),
                fontWeight: 400,
                color: 'inherit',
                marginLeft: '0.4rem'
              }}>
                ({totalRegistros} registros)
              </span>
            )}
          </Title>
          <Description $embedded={embedded} $compact={compact}>{descripcion}</Description>
        </div>
        {help && !embedded && (
          <HelpToggleBtn
            type="button"
            $active={helpOpen}
            onClick={() => setHelpOpen(v => !v)}
          >
            <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
            Ayuda
          </HelpToggleBtn>
        )}
      </Header>
      )}
      {help && !embedded && helpOpen && (
        <div style={{ marginBottom: 10 }}>
          <HelpPanel
            prereqs={help.prereqs}
            tips={help.tips}
            nextSteps={help.nextSteps}
            inlineOpen
          />
        </div>
      )}

      {!modalOnly && (
      <ControlsSection $embedded={embedded} $compact={compact}>
        <SearchInput
          type="text"
          placeholder="Buscar..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          $embedded={embedded}
          $compact={compact}
        />
        <div style={{display:'flex', gap: compact ? '.4rem':'.6rem'}}>
          {apiEndpoint === 'modelos' && (
            <div style={{ width: compact ? 140 : 200 }}>
              <SearchableSelectField
                options={marcasOpts}
                value={filtroMarcaId}
                onChange={(value) => setFiltroMarcaId(value)}
                placeholder="Todas las marcas"
                getOptionValue={(option) => String(option.id)}
                getOptionLabel={(option) => option.nombre || `Marca ${option.id}`}
                showAllOnFocus
                clearable
                size={compact ? 'sm' : 'md'}
              />
            </div>
          )}
          {apiEndpoint === 'sectores-trabajo' && (
            <div style={{ width: compact ? 160 : 200 }}>
              <SearchableSelectField
                options={obrasOpts}
                value={filtroObraId}
                onChange={(value) => setFiltroObraId(value)}
                placeholder="Todas las obras"
                getOptionValue={(option) => String(option.id)}
                getOptionLabel={(option) => option.nombre || `Obra ${option.id}`}
                showAllOnFocus
                clearable
                size={compact ? 'sm' : 'md'}
              />
            </div>
          )}
          {(apiEndpoint === 'componentes' || apiEndpoint === 'depositos' || apiEndpoint === 'tareas' || apiEndpoint === 'catalogo-puntos-control') && (
            <div style={{ width: compact ? 180 : 240 }}>
              <SearchableSelectField
                options={sistemasOpts}
                value={filtroSistemaId}
                onChange={(value) => setFiltroSistemaId(value)}
                placeholder="Todos los sistemas"
                getOptionValue={(option) => String(option.id)}
                getOptionLabel={(option) => option.nombre || `Sistema ${option.id}`}
                showAllOnFocus
                clearable
                size={compact ? 'sm' : 'md'}
              />
            </div>
          )}
          {!embedded && (
            <BackButton
              type="button"
              onClick={() => { if (resolvedBackTo) navigate(resolvedBackTo); else navigate(-1); }}
              $embedded={embedded}
              $compact={compact}
            >
              <i className="fas fa-arrow-left"></i>
              Volver
            </BackButton>
          )}
          {puedeImportar && !embedded && (
            <BackButton
              type="button"
              onClick={() => setShowImportModal(true)}
              $embedded={embedded}
              $compact={compact}
            >
              <i className="fas fa-file-import"></i>
              Importar
            </BackButton>
          )}
          {isRepuestos && !embedded && (
            <BackButton
              type="button"
              onClick={() => setShowImportPreciosModal(true)}
              $embedded={embedded}
              $compact={compact}
              title="Descargar la lista de precios actual, editarla y volver a subirla"
            >
              <i className="fas fa-file-invoice-dollar"></i>
              Actualizar precios
            </BackButton>
          )}
          {!embedded && (
            bulkMode ? (
              <>
                <BulkEditButton
                  type="button"
                  $embedded={embedded}
                  $compact={compact}
                  onClick={toggleBulkMode}
                  title="Cancelar selección"
                >
                  <i className="fas fa-times"></i>
                  Cancelar
                </BulkEditButton>
                {selectedIds.size >= 1 && (
                  <BulkDeleteButton
                    type="button"
                    $embedded={embedded}
                    $compact={compact}
                    onClick={confirmarEliminarMasivo}
                    title="Eliminar los elementos seleccionados"
                  >
                    <i className="fas fa-trash"></i>
                    Eliminar ({selectedIds.size})
                  </BulkDeleteButton>
                )}
                {supportsBulkEdit && selectedIds.size >= 2 && (
                  <ContinuarButton
                    type="button"
                    $embedded={embedded}
                    $compact={compact}
                    onClick={abrirBulkModal}
                  >
                    <i className="fas fa-check"></i>
                    Editar ({selectedIds.size})
                  </ContinuarButton>
                )}
              </>
            ) : (
              <BulkEditButton
                type="button"
                $embedded={embedded}
                $compact={compact}
                onClick={toggleBulkMode}
                title={supportsBulkEdit
                  ? 'Seleccionar varios elementos para editar campos en común o eliminarlos'
                  : 'Seleccionar varios elementos para eliminarlos'}
              >
                <i className="fas fa-layer-group"></i>
                Seleccionar
              </BulkEditButton>
            )
          )}
          <AddButton onClick={abrirModalAgregar} $embedded={embedded} $compact={compact}>
            <i className="fas fa-plus"></i>
            Agregar
          </AddButton>
        </div>
      </ControlsSection>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {!modalOnly && (
      <>
      <TableContainer
        $embedded={embedded}
        $compact={compact}
        onScroll={isRepuestos ? handleRepuestosScroll : undefined}
      >
        {loading && datos.length === 0 ? (
          <LoadingSpinner />
        ) : datosFiltrados.length === 0 ? (
          <NoDataMessage>
            <i className="fas fa-inbox"></i>
            No se encontraron datos
          </NoDataMessage>
        ) : (
          <Table>
            <TableHeader>
              <tr>
                {bulkMode && (
                  <TableHeaderCell $embedded={embedded} $compact={compact} style={{ width: 36, textAlign: 'center' }}>
                    <BulkCheckbox
                      checked={selectedIds.size === datosFiltrados.length && datosFiltrados.length > 0}
                      onChange={() => toggleSelectAll(datosFiltrados.map(d => d.id))}
                      title="Seleccionar todos"
                    />
                  </TableHeaderCell>
                )}
                {!isRepuestos && (
                  <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('nombre')}>Nombre{sortIcon('nombre')}</TableHeaderCell>
                )}
                {isEmpresas && (
                  <>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('razon_social')}>Razón social{sortIcon('razon_social')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('cuit')}>CUIT{sortIcon('cuit')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('email')}>Email{sortIcon('email')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('telefono')}>Teléfono{sortIcon('telefono')}</TableHeaderCell>
                  </>
                )}
                {isMecanicos && (
                  <>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('costo_hora')}>$/h{sortIcon('costo_hora')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact}>Portal</TableHeaderCell>
                  </>
                )}
                {isModelos && (
                  <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('marca_nombre')}>Marca{sortIcon('marca_nombre')}</TableHeaderCell>
                )}
                {isUnidadesMedida && (
                  <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('abreviatura')}>Abreviatura{sortIcon('abreviatura')}</TableHeaderCell>
                )}
                {isSectoresTrabajo && (
                  <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('obra_nombre')}>Obra{sortIcon('obra_nombre')}</TableHeaderCell>
                )}
                {(isComponentes || isDepositos) && (
                  <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('sistema_nombre')}>Sistema{sortIcon('sistema_nombre')}</TableHeaderCell>
                )}
                {isTareas && (
                  <>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('sistema_nombre')}>Sistema{sortIcon('sistema_nombre')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('componente_nombre')}>Componente / Depósito{sortIcon('componente_nombre')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('requiere_repuestos')}>Requiere repuestos{sortIcon('requiere_repuestos')}</TableHeaderCell>
                  </>
                )}
                {isCatalogoPuntoControl && (
                  <>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('sistema_nombre')}>Sistema{sortIcon('sistema_nombre')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('tipo_estado_default')}>Tipo{sortIcon('tipo_estado_default')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('tarea_nombre')}>Tarea asociada{sortIcon('tarea_nombre')}</TableHeaderCell>
                  </>
                )}
                {isSubfamiliasRepuesto && (
                  <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('familia_nombre')}>Familia{sortIcon('familia_nombre')}</TableHeaderCell>
                )}
                {isObras && (
                  <>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('sucursal_nombre')}>Sucursal{sortIcon('sucursal_nombre')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact}>Ubicaciones</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('centro_costo_nombre')}>Centro de Costo{sortIcon('centro_costo_nombre')}</TableHeaderCell>
                  </>
                )}
                {isCategoriasLogistica && (
                  <>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('moneda')}>Moneda{sortIcon('moneda')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('tarifa_corta')}>Tarifa corta distancia (/km){sortIcon('tarifa_corta')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('tarifa_larga')}>Tarifa larga distancia (/km){sortIcon('tarifa_larga')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('distancia_corte_km')}>Distancia de corte (km){sortIcon('distancia_corte_km')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('tarifa_unica')}>Tarifa única (/km){sortIcon('tarifa_unica')}</TableHeaderCell>
                  </>
                )}
                {isRepuestos && (
                  <>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('codigo')}>Código{sortIcon('codigo')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('marca_nombre')}>Marca{sortIcon('marca_nombre')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('descripcion')}>Descripción{sortIcon('descripcion')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('familia_nombre')}>Familia{sortIcon('familia_nombre')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('subfamilia_nombre')}>Subfamilia{sortIcon('subfamilia_nombre')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('componente_nombre')}>Componente / Depósito{sortIcon('componente_nombre')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('punto_pedido')}>Punto Pedido{sortIcon('punto_pedido')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('unidad_medida_nombre')}>Unidad{sortIcon('unidad_medida_nombre')}</TableHeaderCell>
                    <TableHeaderCell $embedded={embedded} $compact={compact} $sortable onClick={() => handleSort('precio_referencia')}>Precio Ref.{sortIcon('precio_referencia')}</TableHeaderCell>
                  </>
                )}
                <TableHeaderCell $embedded={embedded} $compact={compact} style={{ width: '120px' }}>Acciones</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {datosFiltrados.map((item) => (
                <tr
                  key={item.id}
                  onClick={bulkMode ? (e) => toggleSelectId(item.id, e.shiftKey, datosFiltrados.map(d => d.id)) : undefined}
                  style={{
                    ...(bulkMode && selectedIds.has(item.id) ? { background: `${theme.colors.info}18` } : {}),
                    ...(bulkMode ? { cursor: 'pointer', userSelect: 'none' } : {}),
                  }}
                >
                  {bulkMode && (
                    <TableCell $embedded={embedded} $compact={compact} style={{ width: 36, textAlign: 'center' }}>
                      {/* El toggle real lo maneja el onClick del <tr>; este checkbox es solo visual
                          para no disparar toggleSelectId dos veces en el mismo click (click + change). */}
                      <BulkCheckbox checked={selectedIds.has(item.id)} onChange={() => {}} />
                    </TableCell>
                  )}
                  {!isRepuestos && (
                    <TableCell $embedded={embedded} $compact={compact}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {isEstadosReparacion && item.color && (
                          <span style={{
                            display: 'inline-block', width: 12, height: 12,
                            borderRadius: '50%', background: item.color, flexShrink: 0,
                          }} title={item.color} />
                        )}
                        {item.nombre}
                        {isDepositos && item.es_tanque_combustible && (
                          <i className="bi bi-fuel-pump-fill" title="Tanque de combustible" style={{ color: theme.colors.txtSecondary }} />
                        )}
                      </span>
                    </TableCell>
                  )}
                  {isEmpresas && (
                    <>
                      <TableCell $embedded={embedded} $compact={compact}>{item.razon_social || '-'}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.cuit || '-'}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.email || '-'}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.telefono || '-'}</TableCell>
                    </>
                  )}
                  {isMecanicos && (
                    <>
                      <TableCell $embedded={embedded} $compact={compact}>
                        {item.costo_hora != null
                          ? `$${Number(item.costo_hora).toLocaleString('es-AR')}`
                          : '—'}
                      </TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>
                        {item.usuario_username ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px',
                            borderRadius: 999, background: theme.successAlpha, color: theme.colors.success,
                          }}>
                            <i className="bi bi-person-check" />
                            {item.usuario_username}
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: theme.txtMuted }}>Sin acceso</span>
                        )}
                      </TableCell>
                    </>
                  )}
                  {isModelos && (
                    <TableCell $embedded={embedded} $compact={compact}>{item.marca_nombre || marcasOpts.find(m=>m.id===item.marca)?.nombre || ''}</TableCell>
                  )}
                  {isUnidadesMedida && (
                    <TableCell $embedded={embedded} $compact={compact}>{item.abreviatura || '-'}</TableCell>
                  )}
                  {isSectoresTrabajo && (
                    <TableCell $embedded={embedded} $compact={compact}>{item.obra_nombre || obrasOpts.find(o => String(o.id) === String(item.obra))?.nombre || ''}</TableCell>
                  )}
                  {(isComponentes || isDepositos) && (
                    <TableCell $embedded={embedded} $compact={compact}>{renderNombreSistema(item.sistema_nombre || sistemasOpts.find(s => String(s.id) === String(item.sistema))?.nombre)}</TableCell>
                  )}
                  {isTareas && (
                    <>
                      <TableCell $embedded={embedded} $compact={compact}>{renderNombreSistema(item.sistema_nombre || sistemasOpts.find(s => String(s.id) === String(item.sistema))?.nombre)}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>
                        {item.deposito_nombre || depositosOpts.find(d => String(d.id) === String(item.deposito))?.nombre
                          || item.componente_nombre || componentesOpts.find(c => String(c.id) === String(item.componente))?.nombre
                          || '-'}
                      </TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.requiere_repuestos ? 'Sí' : 'No'}</TableCell>
                    </>
                  )}
                  {isCatalogoPuntoControl && (
                    <>
                      <TableCell $embedded={embedded} $compact={compact}>{renderNombreSistema(item.sistema_nombre || sistemasOpts.find(s => String(s.id) === String(item.sistema))?.nombre)}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.tipo_estado_default === 'presencia' ? 'P / NP' : 'B / R / M'}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.tarea_nombre || tareasOpts.find(t => String(t.id) === String(item.tarea))?.nombre || '-'}</TableCell>
                    </>
                  )}
                  {isSubfamiliasRepuesto && (
                    <TableCell $embedded={embedded} $compact={compact}>{item.familia_nombre || familiasOpts.find(f => String(f.id) === String(item.familia))?.nombre || '-'}</TableCell>
                  )}
                  {isObras && (
                    <>
                      <TableCell $embedded={embedded} $compact={compact}>
                        {item.sucursal_nombre || sucursalesOpts.find(s => String(s.id) === String(item.sucursal))?.nombre || (
                          <span style={{ color: theme.colors.danger, fontStyle: 'italic' }}>Sin asignar</span>
                        )}
                      </TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>
                        {Array.isArray(item.ubicaciones) && item.ubicaciones.length > 0
                          ? item.ubicaciones.map(u => u.nombre).join(', ')
                          : '—'}
                      </TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.centro_costo_nombre || centrosCostoOpts.find(c => String(c.id) === String(item.centro_costo))?.nombre || '-'}</TableCell>
                    </>
                  )}
                  {isCategoriasLogistica && (
                    <>
                      <TableCell $embedded={embedded} $compact={compact}>{item.moneda === 'USD' ? 'USD' : '$'}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.tarifa_unificada ? '—' : item.tarifa_corta}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.tarifa_unificada ? '—' : item.tarifa_larga}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.tarifa_unificada ? '—' : item.distancia_corte_km}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.tarifa_unificada ? item.tarifa_unica : '—'}</TableCell>
                    </>
                  )}
                  {isRepuestos && (
                    <>
                      <TableCell $embedded={embedded} $compact={compact}>
                        {item.codigo || '-'}
                        {item.no_usar && (
                          <span title="Repuesto discontinuado: no se ofrece en buscadores ni requerimientos nuevos" style={{ marginLeft: 6, fontSize: '0.75em', fontWeight: 600, color: theme.colors.danger }}>
                            NO USAR
                          </span>
                        )}
                      </TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.marca_nombre || marcasRepuestosOpts.find(m => String(m.id) === String(item.marca))?.nombre || '-'}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.descripcion || '-'}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.familia_nombre || familiasOpts.find(f => String(f.id) === String(item.familia))?.nombre || '-'}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.subfamilia_nombre || item.subfamilia?.nombre || '-'}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>
                        {item.tipo_asignacion === 'deposito'
                          ? (Array.isArray(item.depositos_nombres) && item.depositos_nombres.length > 0
                            ? item.depositos_nombres.join(', ')
                            : (item.deposito_nombre || depositosOpts.find(d => String(d.id) === String(item.deposito))?.nombre || '-'))
                          : (Array.isArray(item.componentes_nombres) && item.componentes_nombres.length > 0
                            ? item.componentes_nombres.join(', ')
                            : (item.componente_nombre || componentesRepuestoOpts.find(c => String(c.id) === String(item.componente))?.nombre || '-'))}
                      </TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>{item.punto_de_pedido ?? '-'}</TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>
                        {item.unidad_medida_abreviatura
                          || unidadesMedidaOpts.find(u => String(u.id) === String(item.unidad_medida))?.abreviatura
                          || item.unidad_medida_nombre
                          || unidadesMedidaOpts.find(u => String(u.id) === String(item.unidad_medida))?.nombre
                          || '-'}
                      </TableCell>
                      <TableCell $embedded={embedded} $compact={compact}>
                        {item.precio_unitario != null
                          ? `${item.moneda === 'USD' ? 'USD ' : '$'}${Number(item.precio_unitario).toLocaleString('es-AR')}`
                          : '—'}
                      </TableCell>
                    </>
                  )}
                  <ActionsCell $embedded={embedded} $compact={compact} onClick={(e) => e.stopPropagation()}>
                    <ActionButton
                      variant="edit"
                      onClick={() => abrirModalEditar(item)}
                      $size={embedded ? 'sm' : undefined}
                    >
                      <i className="fas fa-edit"></i>
                      Editar
                    </ActionButton>
                    {isRepuestos && (
                      <ActionButton
                        variant="info"
                        onClick={() => clonarRepuesto(item)}
                        $size={embedded ? 'sm' : undefined}
                        title="Clonar repuesto"
                      >
                        <i className="fas fa-copy"></i>
                        Clonar
                      </ActionButton>
                    )}
                    <ActionButton
                      variant="delete"
                      onClick={() => confirmarEliminar(item)}
                      $size={embedded ? 'sm' : undefined}
                    >
                      <i className="fas fa-trash"></i>
                      Eliminar
                    </ActionButton>
                  </ActionsCell>
                </tr>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {(isRepuestos || buscaEnServidor) && !loading && (
        <small style={{ marginTop: compact ? '0.35rem' : '0.6rem', color: 'inherit' }}>
          {busqueda.trim() && buscaEnServidor
            ? `${datos.length} de ${totalRegistros} coincidencias`
            : `Mostrando ${datos.length} de ${totalRegistros} ${(titulo || 'registros').toLowerCase()}`}
          {buscandoServidor ? ' · Buscando...' : ''}
          {loadingMore ? ' · Cargando más...' : ''}
        </small>
      )}
      </>
      )}

      {/* Modal Agregar/Editar */}
      {showModal && (
        <Modal $zIndex={modalZIndex} $soft={compact} onClick={buildHandleModalBackdropClick(cerrarModal)} onMouseDown={handleModalBackdropMouseDown}>
          <ModalContent ref={modalRef} onClick={(e) => e.stopPropagation()} $embedded={embedded} $compact={compact}>
                <ModalHeader $embedded={embedded}>
                  <ModalTitle $embedded={embedded}>
                    {itemEditar ? 'Editar' : 'Agregar'} {modalBaseTitle}
                  </ModalTitle>
                  {/* type="button" explícito: sin él un <button> dentro de un
                      formulario es de tipo submit, así que Enter en cualquier
                      campo podía cerrar el modal en vez de guardar. */}
                  <CloseButton type="button" tabIndex={-1} onClick={cerrarModal}>
                    <i className="fas fa-times"></i>
                  </CloseButton>
                </ModalHeader>

            <ModalBody $embedded={embedded}>
            {error && <ErrorMessage style={{ marginBottom: '1rem' }}>{error}</ErrorMessage>}

            {isTareas ? (
              <div>
                <div style={{ marginBottom: '1rem' }}>
                  <Label>Nombre *</Label>
                  <Input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <RequiereRepuestosCard $embedded={embedded} $compact={compact} $checked={requiereRepuestos}>
                    <RequiereRepuestosCheckbox
                      checked={requiereRepuestos}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setRequiereRepuestos(checked);
                        if (!checked) {
                          // Al destildar, se suelta el vínculo — el sistema vuelve a ser editable.
                          setComponenteId('');
                          setComponenteLabel('');
                          setDepositoId('');
                          setDepositoLabel('');
                        }
                      }}
                    />
                    <RequiereRepuestosTexto>
                      <RequiereRepuestosTitulo $compact={compact}>¿Requiere repuestos?</RequiereRepuestosTitulo>
                      <RequiereRepuestosHint $compact={compact}>
                        Activá esta opción para vincular la tarea a un componente o depósito puntual.
                      </RequiereRepuestosHint>
                    </RequiereRepuestosTexto>
                  </RequiereRepuestosCard>
                </div>

                <VinculoRevelado $open={requiereRepuestos}>
                  <div>
                <div style={{ marginBottom: '1rem', marginTop: '0.2rem' }}>
                  <Label>¿Sobre qué interviene?</Label>
                  <SegmentedToggle role="tablist">
                    <SegmentedOption
                      type="button"
                      role="tab"
                      aria-selected={tipoAsignacion === 'componente'}
                      $active={tipoAsignacion === 'componente'}
                      onClick={() => {
                        setTipoAsignacion('componente');
                        setDepositoId('');
                        setDepositoLabel('');
                        setDepositoOpen(false);
                      }}
                    >
                      Componente
                    </SegmentedOption>
                    <SegmentedOption
                      type="button"
                      role="tab"
                      aria-selected={tipoAsignacion === 'deposito'}
                      $active={tipoAsignacion === 'deposito'}
                      onClick={() => {
                        setTipoAsignacion('deposito');
                        setComponenteId('');
                        setComponenteLabel('');
                        setComponenteOpen(false);
                      }}
                    >
                      Depósito
                    </SegmentedOption>
                  </SegmentedToggle>
                </div>

                {tipoAsignacion === 'componente' ? (
                <div style={{ marginBottom: '1rem', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Label>Componente</Label>
                    <SearchableSelectField
                      options={componentesFiltrados}
                      value={componenteId}
                      onChange={(value) => {
                        const c = (componentesFiltrados || []).find((opt) => String(opt.id) === String(value));
                        setComponenteId(value || '');
                        setComponenteLabel(c ? (c.nombre || c.name || String(c.id)) : '');
                        // El sistema queda fijado al del componente elegido — no editable
                        // mientras el componente esté vinculado (ver sistemaBloqueadoPorVinculo).
                        const sistemaComp = c ? (c.sistema ?? c.sistema_id ?? '') : '';
                        if (sistemaComp) {
                          setSistemaId(String(sistemaComp));
                          setSistemaLabel(c.sistema_nombre || sistemasOpts.find(s => String(s.id) === String(sistemaComp))?.nombre || '');
                        }
                        setFieldErrors(prev => ({ ...prev, sistema: undefined }));
                      }}
                      placeholder="Buscar y seleccionar componente..."
                      getOptionValue={(c) => String(c.id)}
                      getOptionLabel={(c) => c.nombre || c.name || ''}
                      renderItem={(c) => (
                        <DropdownItemRow>
                          <DropdownItemNombre>{c.nombre || c.name}</DropdownItemNombre>
                          {c.sistema_nombre && <DropdownItemSistema>{c.sistema_nombre}</DropdownItemSistema>}
                        </DropdownItemRow>
                      )}
                      showAllOnFocus
                      size={compact ? 'sm' : 'md'}
                    />
                  </div>
                  {componenteId && (
                    <SmallOutline
                      tabIndex={-1}
                      type="button"
                      title="Quitar componente (para poder elegir otro sistema)"
                      aria-label="Quitar componente"
                      $embedded={embedded}
                      onClick={() => { setComponenteId(''); setComponenteLabel(''); }}
                    >
                      <i className="bi bi-x-lg" />
                    </SmallOutline>
                  )}
                  <SmallOutline
                    tabIndex={-1}
                    type="button"
                    title="Agregar componente"
                    aria-label="Agregar componente"
                    $embedded={embedded}
                    onClick={() => setShowAgregarComp(true)}
                  >
                    +
                  </SmallOutline>
                </div>
                ) : (
                <div style={{ marginBottom: '1rem', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Label>Depósito</Label>
                    <SearchableSelectField
                      options={depositosFiltrados}
                      value={depositoId}
                      onChange={(value) => {
                        const d = (depositosFiltrados || []).find((opt) => String(opt.id) === String(value));
                        setDepositoId(value || '');
                        setDepositoLabel(d ? (d.nombre || String(d.id)) : '');
                        // El sistema queda fijado al del depósito elegido — no editable
                        // mientras el depósito esté vinculado (ver sistemaBloqueadoPorVinculo).
                        const sistemaDep = d ? (d.sistema ?? d.sistema_id ?? '') : '';
                        if (sistemaDep) {
                          setSistemaId(String(sistemaDep));
                          setSistemaLabel(d.sistema_nombre || sistemasOpts.find(s => String(s.id) === String(sistemaDep))?.nombre || '');
                        }
                        setFieldErrors(prev => ({ ...prev, sistema: undefined }));
                      }}
                      placeholder="Buscar y seleccionar depósito..."
                      getOptionValue={(d) => String(d.id)}
                      getOptionLabel={(d) => d.nombre || ''}
                      renderItem={(d) => (
                        <DropdownItemRow>
                          <DropdownItemNombre>{d.nombre}</DropdownItemNombre>
                          {d.sistema_nombre && <DropdownItemSistema>{d.sistema_nombre}</DropdownItemSistema>}
                        </DropdownItemRow>
                      )}
                      showAllOnFocus
                      size={compact ? 'sm' : 'md'}
                    />
                  </div>
                  {depositoId && (
                    <SmallOutline
                      tabIndex={-1}
                      type="button"
                      title="Quitar depósito (para poder elegir otro sistema)"
                      aria-label="Quitar depósito"
                      $embedded={embedded}
                      onClick={() => { setDepositoId(''); setDepositoLabel(''); }}
                    >
                      <i className="bi bi-x-lg" />
                    </SmallOutline>
                  )}
                  <SmallOutline
                    tabIndex={-1}
                    type="button"
                    title="Agregar depósito"
                    aria-label="Agregar depósito"
                    $embedded={embedded}
                    onClick={() => setShowAgregarDeposito(true)}
                  >
                    +
                  </SmallOutline>
                </div>
                )}
                  </div>
                </VinculoRevelado>

                <div style={{ marginBottom: '1rem', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Label>
                      Sistema *
                      {sistemaBloqueadoPorVinculo
                        ? ` (definido por ${tipoAsignacion === 'deposito' ? 'el depósito' : 'el componente'} elegido)`
                        : ''}
                    </Label>
                    <SearchableSelectField
                      options={sistemasFiltrados}
                      value={sistemaId}
                      onChange={(value) => {
                        const s = (sistemasFiltrados || []).find((opt) => String(opt.id) === String(value));
                        setSistemaId(value || '');
                        setSistemaLabel(s ? (s.nombre || String(s.id)) : '');
                        setFieldErrors(prev => ({ ...prev, sistema: undefined }));
                      }}
                      placeholder="Buscar y seleccionar sistema..."
                      getOptionValue={(s) => String(s.id)}
                      getOptionLabel={(s) => s.nombre || ''}
                      showAllOnFocus
                      size={compact ? 'sm' : 'md'}
                      invalid={Boolean(fieldErrors.sistema)}
                      disabled={sistemaBloqueadoPorVinculo}
                    />
                    {fieldErrors.sistema && <small style={{ color: theme.colors.danger }}>{fieldErrors.sistema}</small>}
                  </div>
                  <SmallOutline
                    tabIndex={-1}
                    type="button"
                    title="Agregar sistema"
                    aria-label="Agregar sistema"
                    $embedded={embedded}
                    disabled={sistemaBloqueadoPorVinculo}
                    onClick={() => setShowAgregarSistema(true)}
                  >
                    +
                  </SmallOutline>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <Label>Descripción</Label>
                  <StyledTextarea value={descripcionItem} onChange={(e) => setDescripcionItem(e.target.value)} />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <Label>Tiempo estimado (min)</Label>
                  <Input type="number" value={tiempoEstimado} onChange={(e) => setTiempoEstimado(e.target.value)} />
                </div>
              </div>
            ) : isCatalogoPuntoControl ? (
              <div>
                <FormGroup>
                  <Label>Nombre *</Label>
                  <Input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus />
                  {fieldErrors.nombre && <small style={{ color: theme.colors.danger }}>{fieldErrors.nombre}</small>}
                </FormGroup>

                <FormGroup>
                  <Label>Sistema</Label>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <SearchableSelectField
                        options={sistemasOpts}
                        value={sistemaId}
                        onChange={(value) => {
                          const selected = sistemasOpts.find((s) => String(s.id) === String(value));
                          setSistemaId(value || '');
                          setSistemaLabel(selected?.nombre || '');
                        }}
                        placeholder="Buscar sistema..."
                        getOptionValue={(s) => String(s.id)}
                        getOptionLabel={(s) => s.nombre || ''}
                        showAllOnFocus
                        size={compact ? 'sm' : 'md'}
                      />
                    </div>
                    <SmallOutline type="button" tabIndex={-1} $embedded={embedded} onClick={() => setShowAgregarSistema(true)} title="Agregar nuevo sistema">+</SmallOutline>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Tipo de verificación</Label>
                  <StyledSelect value={tipoEstadoDefault} onChange={(e) => setTipoEstadoDefault(e.target.value)}>
                    <option value="condicion">Condición – B / R / M</option>
                    <option value="presencia">Presencia – P / NP</option>
                  </StyledSelect>
                </FormGroup>

                <FormGroup>
                  <Label>Tarea asociada (para OTC)</Label>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <SearchableSelectField
                        options={tareasOpts}
                        value={tareaId}
                        onChange={(value) => {
                          const selected = tareasOpts.find((t) => String(t.id) === String(value));
                          setTareaId(value || '');
                          setTareaLabel(selected?.nombre || '');
                        }}
                        placeholder="Buscar tarea..."
                        getOptionValue={(t) => String(t.id)}
                        getOptionLabel={(t) => t.nombre || ''}
                        showAllOnFocus
                        size={compact ? 'sm' : 'md'}
                      />
                    </div>
                    <SmallOutline type="button" tabIndex={-1} $embedded={embedded} onClick={() => setShowAgregarTarea(true)} title="Agregar nueva tarea">+</SmallOutline>
                  </div>
                  <small style={{ opacity: 0.6, marginTop: 4, display: 'block' }}>
                    Tarea que se generará automáticamente en la OTC cuando este punto falle.
                  </small>
                </FormGroup>
              </div>
            ) : isRepuestos ? (
              <>
                <FormGroup>
                  <Label>Código *</Label>
                  <Input
                    type="text"
                    value={codigo}
                    onChange={(e) => {
                      setCodigo(e.target.value);
                      setFieldErrors(prev => ({ ...prev, codigo: undefined }));
                    }}
                    placeholder="Ingrese el código"
                    autoFocus
                  />
                  {fieldErrors.codigo && <small style={{ color: theme.colors.danger }}>{fieldErrors.codigo}</small>}
                </FormGroup>
                <FormGroup>
                  <Label>Descripción *</Label>
                  <Input
                    type="text"
                    value={descripcionRepuesto}
                    onChange={(e) => {
                      setDescripcionRepuesto(e.target.value);
                      setFieldErrors(prev => ({ ...prev, descripcion: undefined }));
                    }}
                    placeholder="Ingrese la descripción"
                  />
                  {fieldErrors.descripcion && <small style={{ color: theme.colors.danger }}>{fieldErrors.descripcion}</small>}
                </FormGroup>
                <FormGroup>
                  <Label>Marca *</Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <SearchableSelectField
                        options={marcasRepuestosOpts}
                        value={repuestoMarcaId}
                        onChange={(value) => {
                          setRepuestoMarcaId(value || '');
                          setFieldErrors(prev => ({ ...prev, marca: undefined }));
                        }}
                        placeholder="Buscar marca..."
                        getOptionValue={(m) => String(m.id)}
                        getOptionLabel={(m) => m.nombre || ''}
                        showAllOnFocus
                        invalid={Boolean(fieldErrors.marca)}
                      />
                    </div>
                    <SmallOutline
                      tabIndex={-1}
                      type="button"
                      $embedded={embedded}
                      onClick={() => setShowAgregarMarcaRepuesto(true)}
                      title="Agregar marca"
                    >
                      +
                    </SmallOutline>
                  </div>
                  {fieldErrors.marca && <small style={{ color: theme.colors.danger }}>{fieldErrors.marca}</small>}
                </FormGroup>
                <FormGroup>
                  <Label>Unidad de medida *</Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <SearchableSelectField
                        options={unidadesMedidaOpts}
                        value={repuestoUnidadMedidaId}
                        onChange={(value) => {
                          setRepuestoUnidadMedidaId(value || '');
                          setFieldErrors(prev => ({ ...prev, unidad_medida: undefined }));
                        }}
                        placeholder="Buscar unidad de medida..."
                        getOptionValue={(u) => String(u.id)}
                        getOptionLabel={(u) => (u.abreviatura ? `${u.nombre} (${u.abreviatura})` : u.nombre) || ''}
                        showAllOnFocus
                        invalid={Boolean(fieldErrors.unidad_medida)}
                      />
                    </div>
                    <SmallOutline
                      tabIndex={-1}
                      type="button"
                      $embedded={embedded}
                      onClick={() => setShowAgregarUnidadMedida(true)}
                      title="Agregar unidad de medida"
                    >
                      +
                    </SmallOutline>
                  </div>
                  {fieldErrors.unidad_medida && <small style={{ color: theme.colors.danger }}>{fieldErrors.unidad_medida}</small>}
                </FormGroup>
                <FormGroup>
                  <Label>¿Es una pieza o un fluido?</Label>
                  <SegmentedToggle role="tablist">
                    <SegmentedOption
                      type="button"
                      role="tab"
                      aria-selected={tipoAsignacionRepuesto === 'componente'}
                      $active={tipoAsignacionRepuesto === 'componente'}
                      onClick={() => setTipoAsignacionRepuesto('componente')}
                    >
                      Pieza de un componente
                    </SegmentedOption>
                    <SegmentedOption
                      type="button"
                      role="tab"
                      aria-selected={tipoAsignacionRepuesto === 'deposito'}
                      $active={tipoAsignacionRepuesto === 'deposito'}
                      onClick={() => setTipoAsignacionRepuesto('deposito')}
                    >
                      Fluido de un depósito
                    </SegmentedOption>
                  </SegmentedToggle>
                </FormGroup>
                {tipoAsignacionRepuesto === 'componente' ? (
                <FormGroup>
                  <Label>Componentes</Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div ref={repCompWrapperRef} style={{ position: 'relative', flex: 1 }}>
                      <BuscadorAgregar
                        abierto={repuestoCompOpen}
                        setAbierto={setRepuestoCompOpen}
                        valor={repuestoCompQuery}
                        onValorChange={(v) => {
                          setRepuestoCompQuery(v);
                          setFieldErrors(prev => ({ ...prev, componentes: undefined }));
                        }}
                        opciones={componentesRepuestoDisponibles}
                        onAgregar={agregarComponenteRepuesto}
                        placeholder="Buscar componente y agregar..."
                        renderOpcion={(c) => (
                                <DropdownItemRow>
                                  <DropdownItemNombre>{c.nombre}</DropdownItemNombre>
                                  {c.sistema_nombre && <DropdownItemSistema>{c.sistema_nombre}</DropdownItemSistema>}
                                </DropdownItemRow>
                        )}
                      />
                    </div>
                    <SmallOutline
                      tabIndex={-1}
                      type="button"
                      $embedded={embedded}
                      onClick={() => { setShowAgregarComp(true); }}
                      title="Agregar componente"
                    >
                      +
                    </SmallOutline>
                  </div>
                  {fieldErrors.componentes && <small style={{ color: theme.colors.danger }}>{fieldErrors.componentes}</small>}
                  <SelectedChips>
                    {componentesRepuestoSeleccionados.map(c => (
                      <SelectedChip key={c.id}>
                        {c.nombre}{c.sistema_nombre ? ` — ${c.sistema_nombre}` : ''}
                        <ChipRemoveButton type="button" onClick={() => quitarComponenteRepuesto(c.id)}>
                          <i className="fas fa-times" />
                        </ChipRemoveButton>
                      </SelectedChip>
                    ))}
                  </SelectedChips>
                  <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: 'inherit' }}>
                    Escribí para buscar y tocá un ítem para agregarlo.
                  </div>
                </FormGroup>
                ) : (
                <>
                <FormGroup>
                  <Label>Depósitos</Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div ref={repDepWrapperRef} style={{ position: 'relative', flex: 1 }}>
                      <BuscadorAgregar
                        abierto={repuestoDepOpen}
                        setAbierto={setRepuestoDepOpen}
                        valor={repuestoDepQuery}
                        onValorChange={(v) => {
                          setRepuestoDepQuery(v);
                          setFieldErrors(prev => ({ ...prev, depositos: undefined }));
                        }}
                        opciones={depositosRepuestoDisponibles}
                        onAgregar={agregarDepositoRepuesto}
                        placeholder="Buscar depósito y agregar..."
                        renderOpcion={(d) => (
                          <DropdownItemRow>
                            <DropdownItemNombre>{d.nombre}</DropdownItemNombre>
                            {d.sistema_nombre && <DropdownItemSistema>{d.sistema_nombre}</DropdownItemSistema>}
                          </DropdownItemRow>
                        )}
                      />
                    </div>
                    <SmallOutline
                      tabIndex={-1}
                      type="button"
                      $embedded={embedded}
                      onClick={() => { setShowAgregarDeposito(true); }}
                      title="Agregar depósito"
                    >
                      +
                    </SmallOutline>
                  </div>
                  {fieldErrors.depositos && <small style={{ color: theme.colors.danger }}>{fieldErrors.depositos}</small>}
                  <SelectedChips>
                    {depositosRepuestoSeleccionados.map(d => (
                      <SelectedChip key={d.id}>
                        {d.nombre}{d.sistema_nombre ? ` — ${d.sistema_nombre}` : ''}
                        <ChipRemoveButton type="button" onClick={() => quitarDepositoRepuesto(d.id)}>
                          <i className="fas fa-times" />
                        </ChipRemoveButton>
                      </SelectedChip>
                    ))}
                  </SelectedChips>
                  <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: 'inherit' }}>
                    Escribí para buscar y tocá un ítem para agregarlo.
                  </div>
                </FormGroup>
                <FormGroup>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      id="usoMantenimientoDiario"
                      type="checkbox"
                      checked={usoMantenimientoDiario}
                      onChange={(e) => setUsoMantenimientoDiario(e.target.checked)}
                    />
                    <label htmlFor="usoMantenimientoDiario" style={{ margin: 0 }}>¿Se usa en el mantenimiento diario?</label>
                  </div>
                  <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: 'inherit' }}>
                    Habilita este repuesto en el formulario de recarga de lubricantes en campo.
                  </div>
                </FormGroup>
                </>
                )}
                <FormGroup>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      id="noUsarRepuesto"
                      type="checkbox"
                      checked={noUsarRepuesto}
                      onChange={(e) => setNoUsarRepuesto(e.target.checked)}
                    />
                    <label htmlFor="noUsarRepuesto" style={{ margin: 0 }}>No usar (repuesto discontinuado)</label>
                  </div>
                  <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: 'inherit' }}>
                    Deja de ofrecerse en los buscadores y no se puede agregar a requerimientos ni ingresos nuevos.
                    Conserva su historial, y sigue apareciendo en informes y auditorías.
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label>Familia *</Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <SearchableSelectField
                        options={familiasOpts}
                        value={familiaId}
                        onChange={(value) => {
                          setFamiliaId(value || '');
                          setFieldErrors(prev => ({ ...prev, familia: undefined }));
                        }}
                        placeholder="Buscar familia..."
                        getOptionValue={(f) => String(f.id)}
                        getOptionLabel={(f) => f.nombre || ''}
                        showAllOnFocus
                        invalid={Boolean(fieldErrors.familia)}
                      />
                    </div>
                    <SmallOutline
                      tabIndex={-1}
                      type="button"
                      $embedded={embedded}
                      onClick={() => setShowAgregarFamiliaRepuesto(true)}
                      title="Agregar familia"
                    >
                      +
                    </SmallOutline>
                  </div>
                  {fieldErrors.familia && <small style={{ color: theme.colors.danger }}>{fieldErrors.familia}</small>}
                </FormGroup>
                <FormGroup>
                  <Label>Subfamilia</Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <SearchableSelectField
                        options={subfamiliasFiltradas}
                        value={subfamiliaRepuestoId}
                        onChange={(value) => setSubfamiliaRepuestoId(value || '')}
                        placeholder="Buscar subfamilia..."
                        getOptionValue={(s) => String(s.id)}
                        getOptionLabel={(s) => (s.familia_nombre ? `${s.familia_nombre} - ${s.nombre}` : s.nombre) || ''}
                        showAllOnFocus
                      />
                    </div>
                    <SmallOutline
                      tabIndex={-1}
                      type="button"
                      $embedded={embedded}
                      onClick={() => setShowAgregarSubfamiliaRepuesto(true)}
                      title="Agregar subfamilia"
                    >
                      +
                    </SmallOutline>
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label>Punto de pedido</Label>
                  <Input
                    type="number"
                    min="0"
                    value={puntoPedidoRepuesto}
                    onChange={(e) => setPuntoPedidoRepuesto(e.target.value)}
                    placeholder="0"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Precio unitario de referencia</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={precioUnitarioRepuesto}
                    onChange={(e) => setPrecioUnitarioRepuesto(e.target.value)}
                    placeholder="Ej: 15000"
                  />
                  <small style={{ opacity: 0.6, marginTop: 4, display: 'block' }}>
                    Se pre-llena automáticamente en OTCs y planillas al usar este repuesto.
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label>Moneda del precio</Label>
                  <SegmentedToggle role="tablist">
                    <SegmentedOption
                      type="button"
                      role="tab"
                      aria-selected={monedaRepuesto === 'ARS'}
                      $active={monedaRepuesto === 'ARS'}
                      onClick={() => setMonedaRepuesto('ARS')}
                    >
                      Pesos ($)
                    </SegmentedOption>
                    <SegmentedOption
                      type="button"
                      role="tab"
                      aria-selected={monedaRepuesto === 'USD'}
                      $active={monedaRepuesto === 'USD'}
                      onClick={() => setMonedaRepuesto('USD')}
                    >
                      Dólares (USD)
                    </SegmentedOption>
                  </SegmentedToggle>
                  <small style={{ opacity: 0.6, marginTop: 4, display: 'block' }}>
                    Los informes pueden mostrarse en pesos o en dólares: la conversión
                    usa la cotización del día en que se cargó este precio.
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label>Equivalencias</Label>
                  <div ref={equivalenciaWrapperRef} style={{ position: 'relative' }}>
                    <BuscadorAgregar
                      valor={equivalenteSearch}
                      onValorChange={setEquivalenteSearch}
                      abierto={equivalenteOpen}
                      setAbierto={setEquivalenteOpen}
                      opciones={equivalentesDisponibles.slice(0, 40)}
                      onAgregar={agregarEquivalente}
                      placeholder="Buscar por código o descripción y agregar..."
                      renderOpcion={(r) => [r.codigo, r.marca_nombre, r.descripcion || 'Sin descripción'].filter(Boolean).join(' - ')}
                    />
                  </div>
                  <EquivalenciasList>
                    <EquivalenciaHeader>
                      <span>Código</span>
                      <span>Marca</span>
                      <span>Descripción</span>
                      <span title="Suma de stock en todos los depósitos/obras">Stock total</span>
                      <span>Acción</span>
                    </EquivalenciaHeader>
                    <EquivalenciasBody>
                      {equivalentesSeleccionados.length === 0 ? (
                        <EquivalenciaRow>
                          <span>-</span>
                          <span>-</span>
                          <span>Sin equivalencias seleccionadas</span>
                          <span>0</span>
                          <span>-</span>
                        </EquivalenciaRow>
                      ) : (
                        equivalentesSeleccionados.map((r) => (
                          <EquivalenciaRow key={r.id}>
                            <span>{r.codigo || '-'}</span>
                            <span>{r.marca_nombre || '-'}</span>
                            <span>{r.descripcion || 'Sin descripción'}</span>
                            <span>{Number(r.stock_total || 0)}</span>
                            <span>
                              <EquivalenciaDeleteButton type="button" onClick={() => quitarEquivalente(r.id)} title="Quitar equivalencia">
                                <i className="bi bi-trash" />
                              </EquivalenciaDeleteButton>
                            </span>
                          </EquivalenciaRow>
                        ))
                      )}
                    </EquivalenciasBody>
                  </EquivalenciasList>
                </FormGroup>

              </>
            ) : isSubfamiliasRepuesto ? (
              <>
                <FormGroup>
                  <Label>Familia *</Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <SearchableSelectField
                        options={familiasOpts}
                        value={subfamiliaFamiliaId}
                        onChange={(value) => {
                          const selected = familiasOpts.find((option) => String(option.id) === String(value));
                          setSubfamiliaFamiliaId(value);
                          setSubfamiliaFamiliaSearch(selected?.nombre || '');
                          setFieldErrors(prev => ({ ...prev, familia: undefined }));
                        }}
                        placeholder="Buscar familia..."
                        getOptionValue={(option) => String(option.id)}
                        getOptionLabel={(option) => option.nombre || `Familia ${option.id}`}
                        showAllOnFocus
                        size={embedded ? 'sm' : 'md'}
                        invalid={Boolean(fieldErrors.familia)}
                      />
                    </div>
                    <SmallOutline
                      tabIndex={-1}
                      type="button"
                      $embedded={embedded}
                      onClick={() => setShowAgregarFamiliaRepuesto(true)}
                      title="Agregar familia"
                    >
                      +
                    </SmallOutline>
                  </div>
                  {fieldErrors.familia && <small style={{ color: theme.colors.danger }}>{fieldErrors.familia}</small>}
                </FormGroup>

                <FormGroup>
                  <Label>Nombre *</Label>
                  <Input
                    type="text"
                    value={nombre}
                    onChange={(e) => {
                      setNombre(e.target.value);
                      setFieldErrors(prev => ({ ...prev, nombre: undefined }));
                    }}
                    placeholder="Ingrese la subfamilia"
                  />
                  {fieldErrors.nombre && <small style={{ color: theme.colors.danger }}>{fieldErrors.nombre}</small>}
                </FormGroup>
              </>
            ) : (
              <>
                {isModelos && (
                  <>
                    <FormGroup>
                      <Label>Marca *</Label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <SearchableSelectField
                            options={marcasOpts}
                            value={marcaId}
                            onChange={(value) => setMarcaId(value)}
                            placeholder="Buscar marca..."
                            getOptionValue={(option) => String(option.id)}
                            getOptionLabel={(option) => option.nombre || `Marca ${option.id}`}
                            showAllOnFocus
                            size={embedded ? 'sm' : 'md'}
                            invalid={Boolean(fieldErrors.marca)}
                          />
                        </div>
                        <SmallOutline tabIndex={-1} type="button" $embedded={embedded} onClick={() => setShowAgregarMarcaModelo(true)} title="Agregar marca">+</SmallOutline>
                      </div>
                    </FormGroup>
                    <FormGroup>
                      <Label>Modelo *</Label>
                      <Input
                        type="text"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        placeholder="Ingrese el modelo"
                      />
                    </FormGroup>
                  </>
                )}
                {(isComponentes || isDepositos) && (
                  <>
                    <FormGroup>
                      <Label>Nombre *</Label>
                      <Input
                        type="text"
                        value={nombre}
                        onChange={(e) => {
                          setNombre(e.target.value);
                          setFieldErrors(prev => ({ ...prev, nombre: undefined }));
                        }}
                        placeholder={isDepositos ? 'Ingrese el depósito' : 'Ingrese el componente'}
                        autoFocus
                      />
                      {fieldErrors.nombre && <small style={{ color: theme.colors.danger }}>{fieldErrors.nombre}</small>}
                    </FormGroup>
                    <FormGroup>
                      <Label>Sistema *</Label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <SearchableSelectField
                            options={sistemasOpts}
                            value={sistemaId}
                            onChange={(value) => {
                              setSistemaId(value);
                              setFieldErrors(prev => ({ ...prev, sistema: undefined }));
                            }}
                            placeholder="Buscar y seleccionar sistema..."
                            getOptionValue={(option) => String(option.id)}
                            getOptionLabel={(option) => option.nombre || `Sistema ${option.id}`}
                            showAllOnFocus
                            size={embedded ? 'sm' : 'md'}
                            invalid={Boolean(fieldErrors.sistema)}
                          />
                        </div>
                        <SmallOutline
                          tabIndex={-1}
                          type="button"
                          $embedded={embedded}
                          onClick={() => setShowAgregarSistema(true)}
                          title="Agregar sistema"
                        >
                          +
                        </SmallOutline>
                      </div>
                      {fieldErrors.sistema && <small style={{ color: theme.colors.danger }}>{fieldErrors.sistema}</small>}
                    </FormGroup>
                    {isDepositos && (
                      <FormGroup>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            id="esTanqueCombustible"
                            type="checkbox"
                            checked={esTanqueCombustible}
                            onChange={(e) => setEsTanqueCombustible(e.target.checked)}
                          />
                          <label htmlFor="esTanqueCombustible" style={{ margin: 0 }}>¿Es el tanque de combustible?</label>
                        </div>
                        <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: 'inherit' }}>
                          Marcalo solo en el depósito reservado para combustible del equipo. Habilita elegir tipos de combustible como fluido en Capacidades de Llenado.
                        </div>
                      </FormGroup>
                    )}
                  </>
                )}
                {isEmpresas && (
                  <>
                    <FormGroup>
                      <Label>Nombre *</Label>
                      <Input
                        type="text"
                        value={nombre}
                        onChange={(e) => {
                          setNombre(e.target.value);
                          setFieldErrors(prev => ({ ...prev, nombre: undefined }));
                        }}
                        placeholder="Ingrese el nombre"
                        autoFocus
                      />
                      {fieldErrors.nombre && <small style={{ color: theme.colors.danger }}>{fieldErrors.nombre}</small>}
                    </FormGroup>
                    <FormGroup>
                      <Label>Razón social</Label>
                      <Input
                        type="text"
                        value={razonSocial}
                        onChange={(e) => setRazonSocial(e.target.value)}
                        placeholder="Ingrese la razón social"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>CUIT *</Label>
                      <Input
                        type="text"
                        value={cuit}
                        onChange={(e) => { setCuit(e.target.value); setFieldErrors(prev => ({ ...prev, cuit: undefined })); }}
                        placeholder="Ingrese el CUIT"
                      />
                      {fieldErrors.cuit && <small style={{ color: theme.colors.danger }}>{fieldErrors.cuit}</small>}
                    </FormGroup>
                    <FormGroup>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Ingrese el email"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Teléfono *</Label>
                      <Input
                        type="text"
                        value={telefono}
                        onChange={(e) => { setTelefono(e.target.value); setFieldErrors(prev => ({ ...prev, telefono: undefined })); }}
                        placeholder="Ingrese el teléfono"
                      />
                      {fieldErrors.telefono && <small style={{ color: theme.colors.danger }}>{fieldErrors.telefono}</small>}
                    </FormGroup>
                    <FormGroup>
                      <Label>Dirección *</Label>
                      <Input
                        type="text"
                        value={direccion}
                        onChange={(e) => { setDireccion(e.target.value); setFieldErrors(prev => ({ ...prev, direccion: undefined })); }}
                        placeholder="Ingrese la dirección"
                      />
                      {fieldErrors.direccion && <small style={{ color: theme.colors.danger }}>{fieldErrors.direccion}</small>}
                    </FormGroup>
                    <FormGroup>
                      <Label>Logo</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                      />
                      {logoFile && <small>{logoFile.name}</small>}
                    </FormGroup>
                  </>
                )}
                {!isModelos && !isObras && !isSectoresTrabajo && !isComponentes && !isDepositos && !isEmpresas && (
                  <FormGroup>
                    <Label>Nombre *</Label>
                    <Input
                      type="text"
                      value={nombre}
                      onChange={(e) => {
                        setNombre(e.target.value);
                        setFieldErrors(prev => ({ ...prev, nombre: undefined }));
                      }}
                      placeholder="Ingrese el nombre"
                      autoFocus
                    />
                    {fieldErrors.nombre && <small style={{ color: theme.colors.danger }}>{fieldErrors.nombre}</small>}
                  </FormGroup>
                )}
                {isUnidadesMedida && (
                  <FormGroup>
                    <Label>Abreviatura</Label>
                    <Input
                      type="text"
                      value={abreviaturaUnidadMedida}
                      onChange={(e) => setAbreviaturaUnidadMedida(e.target.value)}
                      placeholder="Ej: L, kg, u."
                      maxLength={10}
                    />
                  </FormGroup>
                )}
                {isEstadosReparacion && (
                  <FormGroup>
                    <Label>Color del estado</Label>
                    {colorValue ? (
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <input
                          type="color"
                          value={colorValue}
                          onChange={(e) => setColorValue(e.target.value)}
                          style={{ width: 44, height: 36, border: 'none', borderRadius: 6, cursor: 'pointer', padding: 2, background: 'none' }}
                          title="Cambiar color"
                        />
                        <Input
                          type="text"
                          value={colorValue}
                          onChange={(e) => setColorValue(e.target.value)}
                          placeholder="#RRGGBB"
                          style={{ flex: 1, fontFamily: 'monospace' }}
                          maxLength={20}
                        />
                        <button
                          type="button"
                          onClick={() => setColorValue('')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', opacity: 0.6, padding: '0 4px' }}
                          title="Quitar color"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setColorValue(theme.colors.brandDefaultColor)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: 'none', border: `1px dashed ${theme.colors.borderDefault}`,
                          borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
                          fontSize: '0.875rem', color: theme.txtMuted,
                        }}
                      >
                        <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', background: theme.colors.bgSecundario, border: `1px solid ${theme.colors.borderDefault}` }} />
                        Asignar color (opcional)
                      </button>
                    )}
                    <small style={{ color: theme.txtMuted, marginTop: 4, display: 'block' }}>
                      El color se usará para identificar visualmente el estado en las órdenes.
                    </small>
                  </FormGroup>
                )}
                {isCategoriasLogistica && (
                  <>
                    <FormGroup>
                      <Label>Moneda</Label>
                      <StyledSelect value={moneda} onChange={(e) => setMoneda(e.target.value)}>
                        <option value="ARS">Pesos ($)</option>
                        <option value="USD">Dólares (USD)</option>
                      </StyledSelect>
                    </FormGroup>
                    <FormGroup>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          id="tarifaUnificada"
                          type="checkbox"
                          checked={tarifaUnificada}
                          onChange={(e) => { setTarifaUnificada(e.target.checked); setFieldErrors({}); }}
                        />
                        <label htmlFor="tarifaUnificada" style={{ margin: 0 }}>Usar tarifa única (no varía según distancia)</label>
                      </div>
                    </FormGroup>
                    {tarifaUnificada ? (
                      <FormGroup>
                        <Label>Tarifa única {moneda === 'USD' ? 'USD' : '$'}/km *</Label>
                        <Input
                          type="number" min="0" step="0.01"
                          value={tarifaUnica}
                          onChange={(e) => { setTarifaUnica(e.target.value); setFieldErrors(prev => ({ ...prev, tarifa_unica: undefined })); }}
                          placeholder="0.00"
                        />
                        {fieldErrors.tarifa_unica && <small style={{ color: theme.colors.danger }}>{fieldErrors.tarifa_unica}</small>}
                        <small style={{ display: 'block', marginTop: '0.35rem', color: 'inherit' }}>
                          Se cobra esta tarifa por km sin importar la distancia recorrida.
                        </small>
                      </FormGroup>
                    ) : (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                          <FormGroup>
                            <Label>Tarifa corta distancia {moneda === 'USD' ? 'USD' : '$'}/km *</Label>
                            <Input
                              type="number" min="0" step="0.01"
                              value={tarifaCorta}
                              onChange={(e) => { setTarifaCorta(e.target.value); setFieldErrors(prev => ({ ...prev, tarifa_corta: undefined })); }}
                              placeholder="0.00"
                            />
                            {fieldErrors.tarifa_corta && <small style={{ color: theme.colors.danger }}>{fieldErrors.tarifa_corta}</small>}
                          </FormGroup>
                          <FormGroup>
                            <Label>Tarifa larga distancia {moneda === 'USD' ? 'USD' : '$'}/km *</Label>
                            <Input
                              type="number" min="0" step="0.01"
                              value={tarifaLarga}
                              onChange={(e) => { setTarifaLarga(e.target.value); setFieldErrors(prev => ({ ...prev, tarifa_larga: undefined })); }}
                              placeholder="0.00"
                            />
                            {fieldErrors.tarifa_larga && <small style={{ color: theme.colors.danger }}>{fieldErrors.tarifa_larga}</small>}
                          </FormGroup>
                        </div>
                        <FormGroup>
                          <Label>Distancia de corte (km) *</Label>
                          <Input
                            type="number" min="1" step="1"
                            value={distanciaCorte}
                            onChange={(e) => { setDistanciaCorte(e.target.value); setFieldErrors(prev => ({ ...prev, distancia_corte_km: undefined })); }}
                            placeholder="100"
                          />
                          {fieldErrors.distancia_corte_km && <small style={{ color: theme.colors.danger }}>{fieldErrors.distancia_corte_km}</small>}
                        </FormGroup>
                        <small style={{ display: 'block', marginTop: '-0.5rem', marginBottom: '0.5rem', color: 'inherit' }}>
                          Tarifa corta distancia: menor a {distanciaCorte || 100} km · Tarifa larga distancia: desde {distanciaCorte || 100} km
                        </small>
                      </>
                    )}
                  </>
                )}
                {isMecanicos && (
                  <FormGroup>
                    <Label>Tarifa horaria base ($/h)</Label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={mechanicoCostoHora}
                      onChange={(e) => setMechanicoCostoHora(e.target.value)}
                      placeholder="Ej: 5000"
                    />
                    <small style={{ opacity: 0.6, marginTop: 4, display: 'block' }}>
                      Se pre-llena automáticamente en las OTCs al asignar este mecánico.
                    </small>
                  </FormGroup>
                )}
                {isMecanicos && (
                  <FormGroup>
                    <Label>Usuario del portal</Label>
                    <SearchableSelectField
                      options={usuariosMecanico}
                      value={mecanicoUsuarioId}
                      onChange={(v) => setMecanicoUsuarioId(v || '')}
                      placeholder="Toque para ver usuarios disponibles..."
                      getOptionValue={(o) => String(o.id)}
                      getOptionLabel={(o) => o.full_name ? `${o.full_name} (${o.username})` : o.username}
                      showAllOnFocus
                      size={embedded ? 'sm' : 'md'}
                    />
                    {mecanicoUsuarioId && (
                      <small
                        style={{ color: theme.txtMuted, marginTop: 4, display: 'block', cursor: 'pointer' }}
                        onClick={() => setMecanicoUsuarioId('')}
                      >
                        × Quitar vínculo
                      </small>
                    )}
                    {!mecanicoUsuarioId && (
                      <small style={{ color: theme.txtMuted, marginTop: 4, display: 'block' }}>
                        El usuario vinculado podrá acceder al portal de mecánicos.
                      </small>
                    )}
                  </FormGroup>
                )}
                {isObras && (
                  <>
                    <FormGroup>
                      <Label>Nombre *</Label>
                      <Input
                        type="text"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        placeholder="Ingrese el nombre de la obra"
                        autoFocus
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Sucursal *</Label>
                      <SearchableSelectField
                        options={sucursalesOpts}
                        value={sucursalId}
                        onChange={(value) => {
                          const selected = sucursalesOpts.find((option) => String(option.id) === String(value));
                          setSucursalId(value);
                          setSucursalSearch(selected?.nombre || '');
                        }}
                        placeholder="Buscar sucursal..."
                        getOptionValue={(option) => String(option.id)}
                        getOptionLabel={(option) => option.nombre || `Sucursal ${option.id}`}
                        showAllOnFocus
                        size={embedded ? 'sm' : 'md'}
                        invalid={Boolean(fieldErrors.sucursal)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Centro de Costo</Label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <SearchableSelectField
                            options={centrosCostoOpts}
                            value={centroCostoId}
                            onChange={(value) => {
                              const selected = centrosCostoOpts.find((option) => String(option.id) === String(value));
                              setCentroCostoId(value);
                              setCentroCostoSearch(selected?.nombre || '');
                            }}
                            placeholder="Sin centro de costo"
                            getOptionValue={(option) => String(option.id)}
                            getOptionLabel={(option) => option.nombre || `Centro ${option.id}`}
                            showAllOnFocus
                            size={embedded ? 'sm' : 'md'}
                          />
                        </div>
                        <SmallOutline tabIndex={-1} type="button" $embedded={embedded} onClick={agregarCentroCostoRapido} title="Agregar centro de costo">+</SmallOutline>
                      </div>
                    </FormGroup>
                    {itemEditar && (
                      <FormGroup>
                        <Label>Ubicaciones vinculadas</Label>
                        <div style={{ fontSize: '0.85rem', color: 'inherit', lineHeight: 1.6 }}>
                          {Array.isArray(itemEditar.ubicaciones) && itemEditar.ubicaciones.length > 0 ? (
                            itemEditar.ubicaciones.map(u => (
                              <div key={u.id} style={{ padding: '2px 0' }}>
                                <strong>{u.nombre}</strong>
                                {(u.localidad || u.provincia) && (
                                  <span style={{ opacity: 0.65, marginLeft: 6 }}>
                                    {[u.localidad, u.provincia].filter(Boolean).join(', ')}
                                  </span>
                                )}
                              </div>
                            ))
                          ) : (
                            <span style={{ opacity: 0.5, fontStyle: 'italic' }}>
                              Sin ubicaciones asignadas. Vincúlalas desde Configuración → Ubicaciones.
                            </span>
                          )}
                        </div>
                      </FormGroup>
                    )}
                  </>
                )}
                {isSectoresTrabajo && (
                  <>
                    <FormGroup>
                      <Label>Obra *</Label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <SearchableSelectField
                            options={obrasOpts}
                            value={obraId}
                            onChange={(value) => {
                              const selected = obrasOpts.find((option) => String(option.id) === String(value));
                              setObraId(value);
                              setObraSearch(selected?.nombre || '');
                            }}
                            placeholder="Buscar obra..."
                            getOptionValue={(option) => String(option.id)}
                            getOptionLabel={(option) => option.nombre || `Obra ${option.id}`}
                            showAllOnFocus
                            size={embedded ? 'sm' : 'md'}
                            invalid={Boolean(fieldErrors.obra)}
                          />
                        </div>
                        <SmallOutline tabIndex={-1} type="button" $embedded={embedded} onClick={() => setShowAgregarObraSector(true)} title="Agregar obra">+</SmallOutline>
                      </div>
                    </FormGroup>
                    <FormGroup>
                      <Label>Sector *</Label>
                      <Input
                        type="text"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        placeholder="Ingrese el sector"
                      />
                    </FormGroup>
                  </>
                )}

              </>
            )}
            </ModalBody>
            {(() => {
              const idxEditar = itemEditar ? datosFiltrados.findIndex(it => String(it.id) === String(itemEditar.id)) : -1;
              const hayAnterior = idxEditar > 0;
              const haySiguiente = idxEditar >= 0 && idxEditar < datosFiltrados.length - 1;
              return (
                <ButtonGroup $embedded={embedded}>
                  <ButtonGroupLeft>
                    <DuplicarButton
                      type="button"
                      title={itemEditar
                        ? 'Duplicar este elemento (guardar y crear uno nuevo con los mismos datos)'
                        : 'Guardar y crear otro con los mismos datos (los campos únicos quedan vacíos)'}
                      onClick={() => { postGuardadoRef.current = 'duplicar'; guardar(); }}
                      disabled={loading}
                    >
                      <i className="bi bi-copy" style={{ fontSize: '0.75rem' }} />
                      {itemEditar ? 'Duplicar' : 'Guardar y duplicar'}
                    </DuplicarButton>
                    {!itemEditar && (
                      <NavButton
                        type="button"
                        title="Guardar y abrir un formulario en blanco para cargar otro"
                        onClick={() => { postGuardadoRef.current = 'nuevo'; guardar(); }}
                        disabled={loading}
                      >
                        <i className="bi bi-file-earmark-plus" style={{ fontSize: '0.75rem' }} />
                        Guardar y nuevo
                      </NavButton>
                    )}
                    {itemEditar && (
                      <>
                        <NavButton
                          type="button"
                          title="Guardar y ir al elemento anterior"
                          disabled={loading || !hayAnterior}
                          onClick={() => {
                            navItemTargetRef.current = datosFiltrados[idxEditar - 1] || null;
                            postGuardadoRef.current = 'anterior';
                            guardar();
                          }}
                        >
                          <i className="bi bi-chevron-up" style={{ fontSize: '0.7rem' }} />
                          Anterior
                        </NavButton>
                        <NavButton
                          type="button"
                          title="Guardar y ir al elemento siguiente"
                          disabled={loading || !haySiguiente}
                          onClick={() => {
                            navItemTargetRef.current = datosFiltrados[idxEditar + 1] || null;
                            postGuardadoRef.current = 'siguiente';
                            guardar();
                          }}
                        >
                          Siguiente
                          <i className="bi bi-chevron-down" style={{ fontSize: '0.7rem' }} />
                        </NavButton>
                      </>
                    )}
                  </ButtonGroupLeft>
                  <ButtonGroupRight $embedded={embedded}>
                    <BaseButton type="button" onClick={cerrarModal} $size={embedded ? 'sm' : undefined}>
                      Cancelar
                    </BaseButton>
                    <PrimaryButton onClick={guardar} disabled={loading} $size={embedded ? 'sm' : undefined}>
                      {loading ? 'Guardando...' : 'Guardar'}
                    </PrimaryButton>
                  </ButtonGroupRight>
                </ButtonGroup>
              );
            })()}
          </ModalContent>
        </Modal>
      )}

      {/* Modal Edición Masiva de Campos FK */}
      {showBulkModal && (() => {
        const optsMap = {
          marcasOpts, sistemasOpts, componentesOpts, depositosOpts, familiasOpts,
          marcasRepuestosOpts, subfamiliasRepuestoOpts, obrasOpts, sucursalesOpts,
          tareasOpts, unidadesMedidaOpts,
        };
        const itemsSeleccionados = datos.filter(d => selectedIds.has(d.id));
        return (
          <BulkModalOverlay $zIndex={(modalZIndex || 1000) + 50}>
            <BulkModalBox onClick={(e) => e.stopPropagation()}>
              <div>
                <BulkModalTitle>
                  <i className="fas fa-layer-group" />
                  Editar campos en común
                </BulkModalTitle>
                <BulkModalSubtitle>
                  {selectedIds.size} elemento{selectedIds.size !== 1 ? 's' : ''} seleccionado{selectedIds.size !== 1 ? 's' : ''}.
                  Los campos vacíos no se modificarán. El nombre de cada elemento se mantiene intacto.
                </BulkModalSubtitle>
              </div>
              {bulkFkFields.map(({ key, label, optsKey }) => {
                const opts = optsMap[optsKey] || [];
                const valoresActuales = [...new Set(itemsSeleccionados.map(it => String(it[key] ?? '')).filter(Boolean))];
                const placeholder = valoresActuales.length === 1
                  ? (opts.find(o => String(o.id) === valoresActuales[0])?.nombre || '— valor actual —')
                  : '— valores distintos —';
                return (
                  <BulkFieldRow key={key}>
                    <BulkFieldLabel>{label}</BulkFieldLabel>
                    <SearchableSelectField
                      options={opts}
                      value={bulkFieldValues[key] ?? ''}
                      onChange={(val) => setBulkFieldValues(prev => ({ ...prev, [key]: val }))}
                      placeholder={placeholder}
                      getOptionValue={(o) => String(o.id)}
                      getOptionLabel={(o) => o.nombre || `${label} ${o.id}`}
                      showAllOnFocus
                      size="md"
                      clearable
                    />
                  </BulkFieldRow>
                );
              })}
              {isTareas && (() => {
                const esDeposito = bulkTipoAsignacion === 'deposito';
                const opts = esDeposito ? (depositosOpts || []) : (componentesOpts || []);
                const valorActual = bulkFieldValues.__asignacion ?? '';
                return (
                  <BulkFieldRow>
                    <BulkFieldLabel>Componente / Depósito (podés dejar sin elegir el valor y asignarlo tarea por tarea después)</BulkFieldLabel>
                    <SegmentedToggle role="tablist" style={{ marginBottom: 8 }}>
                      <SegmentedOption
                        type="button"
                        role="tab"
                        aria-selected={!esDeposito}
                        $active={!esDeposito}
                        onClick={() => {
                          setBulkTipoAsignacion('componente');
                          setBulkAsignacionTocada(true);
                          setBulkFieldValues(prev => ({ ...prev, __asignacion: '' }));
                        }}
                      >
                        Componente
                      </SegmentedOption>
                      <SegmentedOption
                        type="button"
                        role="tab"
                        aria-selected={esDeposito}
                        $active={esDeposito}
                        onClick={() => {
                          setBulkTipoAsignacion('deposito');
                          setBulkAsignacionTocada(true);
                          setBulkFieldValues(prev => ({ ...prev, __asignacion: '' }));
                        }}
                      >
                        Depósito
                      </SegmentedOption>
                    </SegmentedToggle>
                    <SearchableSelectField
                      options={opts}
                      value={valorActual}
                      onChange={(val) => {
                        setBulkAsignacionTocada(true);
                        setBulkFieldValues(prev => ({ ...prev, __asignacion: val }));
                      }}
                      placeholder={esDeposito ? 'Elegí un depósito para aplicar a todas...' : 'Elegí un componente para aplicar a todas...'}
                      getOptionValue={(o) => String(o.id)}
                      getOptionLabel={(o) => o.nombre || `${esDeposito ? 'Depósito' : 'Componente'} ${o.id}`}
                      showAllOnFocus
                      size="md"
                      clearable
                    />
                  </BulkFieldRow>
                );
              })()}
              {isRepuestos && (() => {
                const esDeposito = bulkTipoAsignacionRepuesto === 'deposito';
                const opts = esDeposito ? (depositosOpts || []) : (componentesRepuestoOpts || []);
                const valorActual = bulkFieldValues.__asignacionRepuesto ?? '';
                return (
                  <BulkFieldRow>
                    <BulkFieldLabel>¿Es una pieza o un fluido? (podés dejar sin elegir el valor y asignarlo repuesto por repuesto después)</BulkFieldLabel>
                    <SegmentedToggle role="tablist" style={{ marginBottom: 8 }}>
                      <SegmentedOption
                        type="button"
                        role="tab"
                        aria-selected={!esDeposito}
                        $active={!esDeposito}
                        onClick={() => {
                          setBulkTipoAsignacionRepuesto('componente');
                          setBulkAsignacionRepuestoTocada(true);
                          setBulkFieldValues(prev => ({ ...prev, __asignacionRepuesto: '' }));
                        }}
                      >
                        Pieza de un componente
                      </SegmentedOption>
                      <SegmentedOption
                        type="button"
                        role="tab"
                        aria-selected={esDeposito}
                        $active={esDeposito}
                        onClick={() => {
                          setBulkTipoAsignacionRepuesto('deposito');
                          setBulkAsignacionRepuestoTocada(true);
                          setBulkFieldValues(prev => ({ ...prev, __asignacionRepuesto: '' }));
                        }}
                      >
                        Fluido de un depósito
                      </SegmentedOption>
                    </SegmentedToggle>
                    <SearchableSelectField
                      options={opts}
                      value={valorActual}
                      onChange={(val) => {
                        setBulkAsignacionRepuestoTocada(true);
                        setBulkFieldValues(prev => ({ ...prev, __asignacionRepuesto: val }));
                      }}
                      placeholder={esDeposito ? 'Elegí un depósito para aplicar a todos...' : 'Elegí un componente para aplicar a todos...'}
                      getOptionValue={(o) => String(o.id)}
                      getOptionLabel={(o) => o.nombre || `${esDeposito ? 'Depósito' : 'Componente'} ${o.id}`}
                      showAllOnFocus
                      size="md"
                      clearable
                    />
                  </BulkFieldRow>
                );
              })()}
              {bulkChoiceFields.map(({ key, label, options }) => {
                const valoresActuales = [...new Set(itemsSeleccionados.map(it => it[key] ?? '').filter(Boolean))];
                const valorUnico = valoresActuales.length === 1 ? valoresActuales[0] : null;
                const placeholder = valorUnico
                  ? (options.find(o => o.value === valorUnico)?.label || '— valor actual —')
                  : '— valores distintos —';
                // Si hay un único valor común, esa opción ya queda representada por el
                // placeholder de arriba: se oculta de la lista para no verla duplicada.
                const opcionesVisibles = valorUnico
                  ? options.filter(o => o.value !== valorUnico)
                  : options;
                return (
                  <BulkFieldRow key={key}>
                    <BulkFieldLabel>{label}</BulkFieldLabel>
                    <select
                      value={bulkFieldValues[key] ?? ''}
                      onChange={(e) => setBulkFieldValues(prev => ({ ...prev, [key]: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '0.45rem 0.65rem',
                        background: theme.colors.bgSecundario,
                        color: theme.colors.txtPrincipal,
                        border: `1px solid ${theme.colors.borderDefault}`,
                        borderRadius: 6,
                        fontSize: '0.875rem',
                      }}
                    >
                      <option value="">{placeholder}</option>
                      {opcionesVisibles.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </BulkFieldRow>
                );
              })}
              {bulkNumberFields.map(({ key, label, step, min }) => {
                const valoresActuales = [...new Set(itemsSeleccionados.map(it => it[key] ?? '').filter(v => v !== '' && v != null))];
                const placeholder = valoresActuales.length === 1 ? `— valor actual: ${valoresActuales[0]} —` : '— valores distintos —';
                return (
                  <BulkFieldRow key={key}>
                    <BulkFieldLabel>{label}</BulkFieldLabel>
                    <input
                      type="number"
                      step={step}
                      min={min}
                      value={bulkFieldValues[key] ?? ''}
                      onChange={(e) => setBulkFieldValues(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder={placeholder}
                      style={{
                        width: '100%',
                        padding: '0.45rem 0.65rem',
                        background: theme.colors.bgSecundario,
                        color: theme.colors.txtPrincipal,
                        border: `1px solid ${theme.colors.borderDefault}`,
                        borderRadius: 6,
                        fontSize: '0.875rem',
                      }}
                    />
                  </BulkFieldRow>
                );
              })}
              {bulkTextFields.map(({ key, label }) => {
                const valoresActuales = [...new Set(itemsSeleccionados.map(it => it[key] ?? '').filter(Boolean))];
                const placeholder = valoresActuales.length === 1 ? `— valor actual: ${valoresActuales[0]} —` : '— valores distintos —';
                return (
                  <BulkFieldRow key={key}>
                    <BulkFieldLabel>{label}</BulkFieldLabel>
                    <input
                      type="text"
                      value={bulkFieldValues[key] ?? ''}
                      onChange={(e) => setBulkFieldValues(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder={placeholder}
                      style={{
                        width: '100%',
                        padding: '0.45rem 0.65rem',
                        background: theme.colors.bgSecundario,
                        color: theme.colors.txtPrincipal,
                        border: `1px solid ${theme.colors.borderDefault}`,
                        borderRadius: 6,
                        fontSize: '0.875rem',
                      }}
                    />
                  </BulkFieldRow>
                );
              })}
              <BulkModalActions>
                <BaseButton type="button" onClick={cerrarBulkModal} disabled={bulkSaving}>
                  Cancelar
                </BaseButton>
                <PrimaryButton
                  onClick={guardarMasivo}
                  disabled={bulkSaving || (
                    !bulkAsignacionTocada
                    && !bulkAsignacionRepuestoTocada
                    && Object.keys(bulkFieldValues).every(k => !bulkFieldValues[k])
                  )}
                >
                  {bulkSaving ? 'Guardando...' : `Guardar en ${selectedIds.size} elemento${selectedIds.size !== 1 ? 's' : ''}`}
                </PrimaryButton>
              </BulkModalActions>
            </BulkModalBox>
          </BulkModalOverlay>
        );
      })()}

      {/* Modal Confirmar Eliminación */}
      {showConfirm && (
        <Modal $zIndex={modalZIndex} onClick={buildHandleModalBackdropClick(cerrarModal)} onMouseDown={handleModalBackdropMouseDown}>
          <ConfirmDialog $wide={!!bloqueoEliminar} onClick={(e) => e.stopPropagation()}>
            {bloqueoEliminar ? (
              <>
                <ConfirmTitle>
                  <i className="fas fa-exclamation-triangle"></i>
                  Este registro está en uso
                </ConfirmTitle>
                <ConfirmMessage>
                  "<strong>{bloqueoEliminar.nombre || itemEliminar?.nombre || itemEliminar?.codigo || ''}</strong>" está siendo utilizado en:
                </ConfirmMessage>
                <UsosLista>
                  {(bloqueoEliminar.grupos || []).map((g) => {
                    const ejemplos = g.ejemplos || [];
                    const restantes = (g.cantidad || 0) - ejemplos.length;
                    return (
                      <li key={`${g.modelo}-${g.campo}`}>
                        <strong>{g.cantidad} {g.label}</strong>
                        {ejemplos.length > 0 && (
                          <UsosNombres>
                            {ejemplos.join(' · ')}
                            {restantes > 0 && ` · y ${restantes} más`}
                          </UsosNombres>
                        )}
                      </li>
                    );
                  })}
                </UsosLista>
                <ConfirmMessage>
                  {bloqueoEliminar.desvinculable
                    ? '¿Desea quitarlo de esos registros y eliminarlo igual? Los registros no se borran: solo dejan de tener este valor asignado.'
                    : 'No se puede quitar automáticamente porque son registros de operación que perderían información. Hay que modificarlos o eliminarlos antes.'}
                </ConfirmMessage>
                <ButtonGroup $embedded={embedded}>
                  <ButtonGroupLeft />
                  <ButtonGroupRight $embedded={embedded}>
                    <BaseButton type="button" onClick={cerrarModal} $size={embedded ? 'sm' : undefined}>
                      {bloqueoEliminar.desvinculable ? 'No, cancelar' : 'Entendido'}
                    </BaseButton>
                    {bloqueoEliminar.desvinculable && (
                      <PrimaryButton onClick={() => eliminar(true)} disabled={loading} $size={embedded ? 'sm' : undefined}>
                        {loading ? 'Eliminando...' : 'Sí, quitar y eliminar'}
                      </PrimaryButton>
                    )}
                  </ButtonGroupRight>
                </ButtonGroup>
              </>
            ) : (
              <>
                <ConfirmTitle>
                  <i className="fas fa-exclamation-triangle"></i>
                  ¿Confirmar eliminación?
                </ConfirmTitle>
                <ConfirmMessage>
                  ¿Está seguro que desea eliminar "<strong>{itemEliminar?.nombre || itemEliminar?.codigo || ''}</strong>"?
                  <br />Esta acción no se puede deshacer.
                </ConfirmMessage>
                <ButtonGroup $embedded={embedded}>
                  <ButtonGroupLeft />
                  <ButtonGroupRight $embedded={embedded}>
                    <BaseButton type="button" onClick={cerrarModal} $size={embedded ? 'sm' : undefined}>
                      Cancelar
                    </BaseButton>
                    <PrimaryButton onClick={() => eliminar(false)} disabled={loading} $size={embedded ? 'sm' : undefined}>
                      {loading ? 'Eliminando...' : 'Eliminar'}
                    </PrimaryButton>
                  </ButtonGroupRight>
                </ButtonGroup>
              </>
            )}
          </ConfirmDialog>
        </Modal>
      )}
      {showConfirmBulkDelete && (
        <Modal $zIndex={modalZIndex} onClick={buildHandleModalBackdropClick(cerrarConfirmBulkDelete)} onMouseDown={handleModalBackdropMouseDown}>
          <ConfirmDialog $wide={!!bulkDeleteResult} onClick={(e) => e.stopPropagation()}>
            {bulkDeleteResult ? (() => {
              const desvinculables = bulkDeleteResult.bloqueados.filter(b => b.desvinculable);
              return (
              <>
                <ConfirmTitle>
                  <i className="fas fa-exclamation-triangle"></i>
                  Algunos registros no se pudieron eliminar
                </ConfirmTitle>
                <ConfirmMessage>
                  Se eliminaron los que no tenían datos vinculados. Estos {bulkDeleteResult.bloqueados.length} quedaron sin borrar porque están en uso:
                </ConfirmMessage>
                <BloqueadosLista>
                  {bulkDeleteResult.bloqueados.map((b) => (
                    <li key={b.id}>
                      <strong>{b.nombre}</strong>: {b.motivo}
                    </li>
                  ))}
                </BloqueadosLista>
                {desvinculables.length > 0 && (
                  <ConfirmMessage>
                    {desvinculables.length === bulkDeleteResult.bloqueados.length
                      ? 'Se pueden quitar esos vínculos y eliminarlos igual. Los registros que los usan no se borran: solo dejan de tener este valor asignado.'
                      : `De estos, ${desvinculables.length} se pueden eliminar quitándoles los vínculos. El resto lo usan registros de operación y hay que modificarlos antes.`}
                  </ConfirmMessage>
                )}
                <ButtonGroup $embedded={embedded}>
                  <ButtonGroupLeft />
                  <ButtonGroupRight $embedded={embedded}>
                    <BaseButton type="button" onClick={cerrarConfirmBulkDelete} $size={embedded ? 'sm' : undefined}>
                      {desvinculables.length > 0 ? 'Cancelar' : 'Entendido'}
                    </BaseButton>
                    {desvinculables.length > 0 && (
                      <PrimaryButton
                        onClick={() => ejecutarEliminacionMasiva(true)}
                        disabled={bulkDeleting}
                        $size={embedded ? 'sm' : undefined}
                      >
                        {bulkDeleting
                          ? 'Eliminando...'
                          : `Quitar vínculos y eliminar ${desvinculables.length}`}
                      </PrimaryButton>
                    )}
                  </ButtonGroupRight>
                </ButtonGroup>
              </>
              );
            })() : (
              <>
                <ConfirmTitle>
                  <i className="fas fa-exclamation-triangle"></i>
                  ¿Confirmar eliminación?
                </ConfirmTitle>
                <ConfirmMessage>
                  ¿Está seguro que desea eliminar <strong>{selectedIds.size}</strong> elemento{selectedIds.size !== 1 ? 's' : ''} seleccionado{selectedIds.size !== 1 ? 's' : ''}?
                  <br />Esta acción no se puede deshacer.
                </ConfirmMessage>
                <ButtonGroup $embedded={embedded}>
                  <ButtonGroupLeft />
                  <ButtonGroupRight $embedded={embedded}>
                    <BaseButton type="button" onClick={cerrarConfirmBulkDelete} $size={embedded ? 'sm' : undefined}>
                      Cancelar
                    </BaseButton>
                    <PrimaryButton onClick={() => ejecutarEliminacionMasiva(false)} disabled={bulkDeleting} $size={embedded ? 'sm' : undefined}>
                      {bulkDeleting ? 'Eliminando...' : 'Eliminar'}
                    </PrimaryButton>
                  </ButtonGroupRight>
                </ButtonGroup>
              </>
            )}
          </ConfirmDialog>
        </Modal>
      )}
      {/* Modal para agregar componente rápido */}
      {showAgregarComp && (
        <ConfiguracionBase
          apiEndpoint="componentes"
          titulo="Componentes"
          descripcion="Componentes de equipos organizados por sistema"
          icon="fas fa-puzzle-piece"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleComponenteCreadoDesdeFormulario}
          onRequestClose={() => setShowAgregarComp(false)}
        />
      )}

      {/* Modal para agregar depósito rápido */}
      {showAgregarDeposito && (
        <ConfiguracionBase
          apiEndpoint="depositos"
          titulo="Depósitos"
          descripcion="Depósitos de fluidos de los equipos (cárter, tanque hidráulico, tanque de combustible, etc.)"
          icon="fas fa-fill-drip"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleDepositoCreadoDesdeFormulario}
          onRequestClose={() => setShowAgregarDeposito(false)}
        />
      )}

      {showAgregarSistema && (
        <ConfiguracionBase
          apiEndpoint="sistemas"
          titulo="Sistemas"
          descripcion="Sistemas para clasificación de componentes"
          icon="fas fa-cogs"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleSistemaCreadoDesdeFormulario}
          onRequestClose={() => setShowAgregarSistema(false)}
        />
      )}

      {showAgregarTarea && (
        <ConfiguracionBase
          apiEndpoint="tareas"
          titulo="Tareas"
          descripcion="Tareas de mantenimiento"
          icon="fas fa-tasks"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleTareaCreadaDesdeFormulario}
          onRequestClose={() => setShowAgregarTarea(false)}
        />
      )}

      {showAgregarFamiliaRepuesto && (
        <ConfiguracionBase
          apiEndpoint="familias-repuesto"
          titulo="Familias de Repuesto"
          descripcion="Gestioná las familias de repuesto"
          icon="fas fa-sitemap"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleFamiliaRepuestoCreadaDesdeFormulario}
          onRequestClose={() => setShowAgregarFamiliaRepuesto(false)}
        />
      )}

      {showAgregarSubfamiliaRepuesto && (
        <ConfiguracionBase
          apiEndpoint="subfamilias-repuesto"
          titulo="Subfamilias de Repuesto"
          descripcion="Gestioná las subfamilias de repuesto"
          icon="fas fa-sitemap"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleSubfamiliaRepuestoCreadaDesdeFormulario}
          onRequestClose={() => setShowAgregarSubfamiliaRepuesto(false)}
        />
      )}

      {showSubCentroCosto && (
        <ConfiguracionBase
          apiEndpoint="centroscosto"
          titulo="Centros de Costo"
          descripcion="Centros de costo disponibles"
          icon="fas fa-coins"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleCentroCostoCreadoDesdeFormulario}
          onRequestClose={() => setShowSubCentroCosto(false)}
        />
      )}

      {showAgregarMarcaRepuesto && (
        <ConfiguracionBase
          apiEndpoint="marcas-repuesto"
          titulo="Marcas de Repuesto"
          descripcion="Gestioná las marcas de repuesto"
          icon="fas fa-tags"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleMarcaRepuestoCreadaDesdeFormulario}
          onRequestClose={() => setShowAgregarMarcaRepuesto(false)}
        />
      )}

      {showAgregarUnidadMedida && (
        <ConfiguracionBase
          apiEndpoint="unidades-medida"
          titulo="Unidades de Medida"
          descripcion="Gestioná las unidades de medida de repuestos"
          icon="fas fa-balance-scale"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleUnidadMedidaCreadaDesdeFormulario}
          onRequestClose={() => setShowAgregarUnidadMedida(false)}
        />
      )}

      {showAgregarMarcaModelo && (
        <ConfiguracionBase
          apiEndpoint="marcas"
          titulo="Marcas"
          descripcion="Gestioná marcas de equipos"
          icon="fas fa-tags"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleMarcaCreadaDesdeFormularioModelo}
          onRequestClose={() => setShowAgregarMarcaModelo(false)}
        />
      )}

      {showAgregarObraSector && (
        <ConfiguracionBase
          apiEndpoint="obras"
          titulo="Obras"
          descripcion="Gestioná obras activas"
          icon="fas fa-road"
          embedded
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={childModalZIndex}
          onRecordSaved={handleObraCreadaDesdeFormularioSector}
          onRequestClose={() => setShowAgregarObraSector(false)}
        />
      )}
    </div>
  );

  // If embedded, return only the inner panel (no outer page container)
  if (embedded) return Inner;

  // If modalOnly, render Inner directly — it already contains the modal overlays,
  // and wrapping it in ConfigContainer would inject a full-page layout into the flow.
  if (modalOnly) return Inner;

  return (
    <ConfigContainer $embedded={embedded}>
      {Inner}
      {showImportModal && (
        <ImportarModal
          entidad={apiEndpoint}
          onClose={() => setShowImportModal(false)}
          onImportado={cargarDatos}
        />
      )}
      {showImportPreciosModal && (
        <ImportarModal
          entidad="precios-repuestos"
          onClose={() => setShowImportPreciosModal(false)}
          onImportado={cargarDatos}
        />
      )}
    </ConfigContainer>
  );
};

export default ConfiguracionBase;
